/* Sprint 5 — Risk Assessment Report (17 sections, matches design at
 * `New UI Changes/risk-report.html`). Pulls one consolidated payload
 * from /projects/:auditId/consolidated-report and renders the full
 * audit report combining DD + SPA + Risk + Tavily verification data.
 *
 * Layout: sticky sidebar TOC on the left, 17 sections on the right.
 * Sections backed by real data render in full; sections needing LLM
 * synthesis or new backend work render a "Pending — Sprint 6"
 * placeholder so the page is honest about coverage.
 *
 * Data wiring:
 *   §1, §4 — Project meta + Risk's overall verdict
 *   §3, §5, §6, §7, §17 — DD report JSON (dd.report) sub-keys
 *   §8, §9, §10 — SPA report JSON (spa.report) sub-keys
 *   §13, §14, §15 — Risk's ai_opinion + claims + market_probes +
 *                    peer_benchmark + external API log
 *   §2, §11, §12, §16 — placeholder (needs LLM synthesis pass)
 */

import React, { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormattedText, stripMarkdown, cleanTooltip } from '../components/FormattedText';
import { TruncatedCell } from '../components/TruncatedCell';
import { parseSectorSubsector } from '../utils/subsectorParser';
/* Sprint 5 client feedback 2026-07-08 (FE Ticket — Risk §10 IP
   Clauses sub-table consumes computeSpa4Status). Single source
   of truth for §4 → §5 → Risk §10 IP row → (AI Analysis + Status).
   Imported from spaSections where the util is exported and used
   by both SPA §4 and SPA §5. */
import { computeSpa4Status, getSpaIssueTableRows, getSpaPassClauses } from '../components/spa/spaSections';
import type { SpaIssueRow } from '../types/api';
import { computeAnnexureBTier } from '../utils/annexureBTier';
/* Sprint 5 client feedback 2026-07-11 (Iter6 #1) — canonical Source
   chip for every Risk Report table. */
import { SourceChip } from '../utils/sourceChip';
import { DraftReport } from '../components/draft/DraftReport';
import '../styles/draft-report.css';
import {
  getConsolidatedReport,
  downloadOiaAuditPdf,
  runRiskAssessment,
  type ConsolidatedReport,
} from '../services/api';
/* Sprint 5 client feedback 2026-06-23 — page-scoped design system from
 * Sprint5/FE/Risk report UI/risk-report_Expecetd....html. Exposes the
 * .ra-* classes (meta-grid, banner, exec-grid, heatmap, assumption
 * cards, legal cards, source-verification table, market posture,
 * etc.) consumed by the section components below. CSS variables it
 * references (--surface-alt, --watch-bg, --font-display, --s5, etc.)
 * are all defined globally in index.css so this file is additive only. */
import '../styles/risk-report.css';

const TOC_ITEMS = [
  { id: 's1', label: '1. Decision Summary' },
  { id: 's2', label: '2. Executive Summary' },
  { id: 's3', label: '3. Financial Assessment' },
  { id: 's4', label: '4. Investment Classification' },
  { id: 's5', label: '5. Risk Assessment' },
  { id: 's6', label: '6. Key Assumptions' },
  { id: 's7', label: '7. Calculation Verification' },
  { id: 's8', label: '8. Legal Assessment Summary' },
  { id: 's9', label: '9. Critical Legal Findings' },
  { id: 's10', label: '10. Mandatory Clause Compliance' },
  { id: 's11', label: '11. Consolidated Findings' },
  { id: 's12', label: '12. Good Practice Noted' },
  { id: 's13', label: '13. Source Verification' },
  { id: 's14', label: '14. Market Performance' },
  { id: 's15', label: '15. Analysis Summary' },
  { id: 's16', label: '16. Evidence & Traceability' },
  { id: 's17', label: '17. Captured Document Data' },
];

/* Tiny pieces — reused across multiple sections. */

/* Sprint 5 client feedback 2026-06-23 — the prior MetaGrid helper
 * (inline-styled card grid) has been replaced everywhere by the
 * design's `.ra-meta-grid` + `.ra-meta-cell` classes from
 * frontend/src/styles/risk-report.css. Sections that need this layout
 * (Decision Summary §1, Investment Classification §4) now emit the
 * markup directly so the cell-separator borders + display-font values
 * match the mockup. */

function SectionHead({
  eyebrow,
  title,
  id,
}: {
  eyebrow: string;
  title: string;
  id: string;
}): JSX.Element {
  // Sprint 5 client feedback 2026-06-23 (FE Four Issue 4): inline
  // styles moved into the .ra-section-head class in risk-report.css
  // so heading + body fonts come from one source. Heading now uses
  // --font-body (Work Sans / Hanken Grotesk) — same as section body
  // copy — fixing the "heading font and details font are different"
  // complaint. Display serif is reserved for the page-title row.
  return (
    <div className="ra-section-head" id={id}>
      <div className="eyebrow">{eyebrow}</div>
      <h2 className="title">{title}</h2>
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6):
   Assessment Summary KPI strip — 5 executive-level tiles
   rendered above §1 in the Risk Report. Replaces the document-
   style intro with a dashboard-style at-a-glance overview. Each
   tile carries an accent stripe in the severity colour and links
   to its source section. */
interface KpiTile {
  label: string;
  value: string;
  caption: string;
  accent: string;
  href?: string;
}

function AssessmentSummaryKpis({
  project,
  risk,
  spa,
  dd,
  recommendationLabel,
  recommendationVariant,
  riskRatingDisplay,
  claims,
}: {
  project: { project_name: string; audit_id: string; overall_status: string };
  risk: Record<string, unknown>;
  spa: Record<string, unknown>;
  dd: Record<string, unknown>;
  recommendationLabel: string;
  recommendationVariant: string;
  riskRatingDisplay: string;
  claims: ConsolidatedReport['claims'];
}): JSX.Element {
  void project;
  // Risk Rating accent
  const ratingAccent =
    riskRatingDisplay === 'High' ? 'var(--fail)' :
    riskRatingDisplay === 'Medium' ? 'var(--watch)' :
    riskRatingDisplay === 'Low' ? 'var(--pass)' :
    'var(--muted)';
  // Recommendation accent — map variant → CSS var
  const recAccent =
    recommendationVariant === 'pass' ? 'var(--pass)' :
    recommendationVariant === 'watch' ? 'var(--watch)' :
    recommendationVariant === 'fail' ? 'var(--fail)' :
    'var(--info)';
  /* Sprint 5 client feedback 2026-07-12 (issues/10 audit) — the KPI
   * tile MUST derive its count from the same pool §11 renders, per
   * the client invariant:
   *   count(Risk §11) == count(DD §11 rendered) + count(SPA §4 rendered)
   * Previously the tile pulled from the LLM synthesis breakdown
   * (auditor_opinion.breakdown.critical_n / high_n) which diverged
   * from the §11 pool by an unbounded amount (the audit doc reported
   * KPI="2 Critical + 1 High" against §11="10 rows"). Now the tile
   * counts canonical findings post Critical→High fold, so KPI, §11
   * chip strip, §15 Flag Composition histogram, and §16 card count
   * all agree by construction. */
  const _canonicalKpi = buildCanonicalFindings(risk, spa, dd);
  const highCount = _canonicalKpi.filter(
    (f) => f.severity === 'High' || f.severity === 'Critical',
  ).length;
  const mediumCount = _canonicalKpi.filter((f) => f.severity === 'Medium').length;
  const recCount = _canonicalKpi.filter((f) => f.severity === 'Recommendation').length;
  // Sections detected
  const sectionDetection = getNested(spa, 'mandatory_section_detection') as Record<string, unknown> | undefined;
  const universal = (sectionDetection?.universal as unknown[]) ?? [];
  const sectionsDetected = universal.filter((s) => (s as Record<string, unknown>).detected).length;
  const sectionsTotal = universal.length || 5;
  const sectionAccent =
    sectionsTotal && sectionsDetected === sectionsTotal ? 'var(--pass)' :
    sectionsDetected >= sectionsTotal * 0.6 ? 'var(--watch)' : 'var(--fail)';
  // Verification confidence — proportion of claims that aren't not_found
  const total = claims.length;
  const verifiedCount = claims.filter((c) => c.status !== 'not_found').length;
  const verifPct = total > 0 ? Math.round((verifiedCount / total) * 100) : 0;
  const verifAccent =
    verifPct >= 70 ? 'var(--pass)' :
    verifPct >= 40 ? 'var(--watch)' : 'var(--fail)';

  const tiles: KpiTile[] = [
    {
      label: 'Risk Rating',
      value: riskRatingDisplay,
      caption: 'Overall composite — See Section 15',
      accent: ratingAccent,
      href: '#s15',
    },
    {
      label: 'Recommendation',
      value: recommendationLabel,
      caption: 'Investment decision — See Section 1',
      accent: recAccent,
      href: '#s1',
    },
    {
      /* Sprint 5 client feedback 2026-07-12 (issues/10 audit) —
       * "Critical Findings" tile renamed to "High Findings" per the
       * Iter9 severity retirement. Count folds legacy Critical into
       * High. Caption summarises the deal's Medium + Recommendation
       * tail so analysts see the full composition at a glance. */
      label: 'High Findings',
      value: String(highCount),
      caption: (() => {
        if (highCount === 0 && mediumCount === 0 && recCount === 0) {
          return 'No escalations · See Section 11';
        }
        const parts: string[] = [];
        if (mediumCount > 0) parts.push(`${mediumCount} Medium`);
        if (recCount > 0) parts.push(`${recCount} Recommendation${recCount === 1 ? '' : 's'}`);
        return parts.length > 0
          ? `${parts.join(' · ')} · See Section 11`
          : 'See Section 11';
      })(),
      accent: highCount > 0 ? 'var(--fail)' : 'var(--pass)',
      href: '#s11',
    },
    {
      label: 'SPA Sections Detected',
      value: `${sectionsDetected} of ${sectionsTotal}`,
      caption: 'Mandatory clauses — See Section 10',
      accent: sectionAccent,
      href: '#s10',
    },
    {
      label: 'Verification Coverage',
      value: `${verifPct}%`,
      caption: `${verifiedCount} of ${total} claims corroborated`,
      accent: verifAccent,
      href: '#s13',
    },
  ];

  return (
    <section
      aria-label="Assessment summary at a glance"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--s3)',
      }}
    >
      {tiles.map((t) => (
        <a
          key={t.label}
          href={t.href}
          className="card rsk-kpi-tile"
          style={{
            display: 'block',
            borderTop: `4px solid ${t.accent}`,
            padding: 'var(--s4) var(--s4)',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <div
            style={{
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              color: 'var(--muted)',
              marginBottom: 'var(--s2)',
            }}
          >
            {t.label}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: t.accent,
              lineHeight: 1.15,
              marginBottom: 'var(--s2)',
            }}
          >
            {t.value}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-sec)' }}>
            {t.caption}
          </div>
        </a>
      ))}
    </section>
  );
}

function Pending({ note }: { note: string }): JSX.Element {
  return (
    <div
      style={{
        padding: 'var(--s4)',
        background: 'var(--surface-alt)',
        border: '1px dashed var(--lines)',
        borderRadius: 'var(--r-sm)',
        color: 'var(--muted)',
        fontStyle: 'italic',
        fontSize: '0.9rem',
      }}
    >
      ⏳ {note}
    </div>
  );
}

function Badge({
  variant,
  children,
}: {
  variant: 'pass' | 'watch' | 'fail' | 'info' | 'neutral';
  children: React.ReactNode;
}): JSX.Element {
  const colour =
    variant === 'pass' ? 'var(--pass)' :
    variant === 'watch' ? 'var(--watch)' :
    variant === 'fail' ? 'var(--fail)' :
    variant === 'info' ? 'var(--info)' :
    'var(--muted)';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        background: `color-mix(in srgb, ${colour} 15%, transparent)`,
        color: colour,
        borderRadius: '12px',
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {children}
    </span>
  );
}

/* Picks the right badge variant from a status / verdict string. */
function statusVariant(s: string | null | undefined): 'pass' | 'watch' | 'fail' | 'info' | 'neutral' {
  if (!s) return 'neutral';
  const v = s.toLowerCase();
  if (['pass', 'verified', 'confirmed', 'adequate', 'present', 'complete', 'completed', 'approve'].includes(v)) return 'pass';
  if (['watch', 'conditional', 'divergent', 'partial', 'monitor'].includes(v)) return 'watch';
  if (['fail', 'critical', 'high', 'missing', 'deficient', 'failed', 'reject'].includes(v)) return 'fail';
  if (['consistent', 'info', 'medium'].includes(v)) return 'info';
  return 'neutral';
}

/* Sprint 5 client feedback 2026-06-21: locate the flag list in a
   workflow report_json snapshot. The snapshot persists flags at
   `sections.flags_section.rows`, NOT at the top-level `flags` key
   that older code paths assumed. Several sections (§9, §11, §16,
   Section15.AnalysisSummary guard) were rendering empty because they
   only checked the top-level key. Mirrors the BE _flags_from_report
   helper in synthesizer.py. */
/**
 * Sprint 5 client feedback 2026-07-02 — derive the §8 eyebrow's
 * "Consolidated review · X · Y · Z" list from the SPA report's
 * actually-uploaded document subtypes rather than a hardcoded
 * "SPA · SHA · Escrow" that lied whenever the deal shipped a
 * different combination. Scans every attribute_results entry's
 * source_document field for known SPA subtype tokens, dedupes,
 * and returns them in canonical order so the header reads
 * stably across runs. Falls back to "SPA" when the report
 * carries no source_document data.
 */
const _SPA_DOC_CANONICAL_ORDER: ReadonlyArray<[RegExp, string]> = [
  [/\bspa\b|share purchase agreement/i, 'SPA'],
  [/\bsha\b|shareholders? agreement/i, 'SHA'],
  [/investors?.?rights|\bira\b/i, 'IRA'],
  [/voting agreement/i, 'Voting Agreement'],
  [/disclosure schedule/i, 'Disclosure Schedule'],
  [/escrow/i, 'Escrow'],
];

function _spaEyebrowDocs(spaSections: Record<string, unknown> | null | undefined): string {
  if (!spaSections || typeof spaSections !== 'object') return 'SPA';
  const attrs = (spaSections as Record<string, unknown>).attribute_results;
  if (!attrs || typeof attrs !== 'object') return 'SPA';
  const seen = new Set<string>();
  for (const key of Object.keys(attrs)) {
    const entry = (attrs as Record<string, unknown>)[key];
    if (!entry || typeof entry !== 'object') continue;
    const rec = entry as Record<string, unknown>;
    const src = String(rec.source_document ?? '').trim();
    if (!src) continue;
    for (const [rx, label] of _SPA_DOC_CANONICAL_ORDER) {
      if (rx.test(src)) {
        seen.add(label);
        break;
      }
    }
  }
  if (seen.size === 0) return 'SPA';
  return _SPA_DOC_CANONICAL_ORDER
    .map(([, label]) => label)
    .filter((label) => seen.has(label))
    .join(' · ');
}

/* Sprint 5 client feedback 2026-07-09 follow-up 7 — canonical
   findings builder. ONE reader for §9 / §11 / §15 / §16 (and the
   PDF via a Python mirror), so counts + IDs + dedupe always agree.

   Merge order (matches user spec):
     1. Non-Legal from synth.consolidated_findings (Financial /
        Technical / Operational / Risk).
     2. SPA §5 rows — walks attribute_groups.investor_protection
        (modern) with fallback to spa.attribute_results (old runs)
        through computeSpa4Status so severities match SPA §5.
     3. Raw DD flags from _flagsFromReport(dd) that aren't already
        covered by synth.consolidated_findings id set.
     4. Overall Investment Risk verdict (DD §5) — if not already
        emitted as an F-XX finding by the BE synth.

   Dedupe: prefer BE-emitted F-XX (F-01..F-N from consolidated_
   findings) when the same attribute_key/id appears in both.

   Renumber: after merging + dedupe, assign display_id "F-01",
   "F-02", ... sequentially. Preserve original_id so tooltips /
   traceability can still map back.

   Severity normalisation: Watch → Low, Reject / Severe → Critical.
   Applied uniformly so histogram + labels agree everywhere. */

export interface CanonicalFinding {
  display_id: string;      // "F-01", "F-02", ... — the user-visible ID
  original_id: string;     // whatever came in (F-XX, SPA-CLAUSE-*, DD-SIGNAL-*)
  area: string;            // Legal | Financial | Technical | Operational | Risk
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Recommendation';
  title: string;
  description: string;
  source_page: number | null;
  source_document: string | null;
  action_required: string;
  responsible: string | null;
  attribute_key: string | null;
  /* Sprint 5 client feedback 2026-07-11 (§16 audit trail empty) —
     §16 Full audit trail per finding was rendering "—" for every
     Extracted / Clause Number / Benchmark / Rule Triggered /
     Confidence field because CanonicalFinding did not carry these
     fields. §16 fell back to attribute-index lookup, which returns
     null for the many findings whose title doesn't match a keyword
     in the attr-key resolver. Thread these fields through the
     canonical model so §16 sees the raw flag / SPA / DD data
     directly. All optional — the FE still shows "—" when the BE
     didn't populate them, but no longer drops perfectly-good data
     on the floor. */
  quoted_text?: string | null;
  clause_number?: string | null;
  benchmark?: string | null;
  confidence?: number | null;
  criteria?: string | null;
}

/* Sprint 5 client feedback 2026-07-12 (Iter9 #4) — the previous
 * Critical tier is retired at the severity level. Any finding whose
 * source severity was Critical / Reject / Severe now folds into High.
 * A new Recommendation tier sits below Low for AI-downgraded advisory
 * observations (see the two-step pipeline in the source data). The
 * client's colour spec maps:
 *   High           → Red    (badge-fail)
 *   Medium         → Amber  (badge-watch)
 *   Low            → Green  (badge-pass)
 *   Recommendation → Blue   (badge-info) */
function _normSeverity(raw: unknown): 'Critical' | 'High' | 'Medium' | 'Low' | 'Recommendation' {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'high' || s === 'critical' || s === 'reject' || s === 'severe' || s === 'fail' || s === 'flag') return 'High';
  if (s === 'medium' || s === 'moderate' || s === 'watch') return 'Medium';
  if (s === 'recommendation' || s === 'opportunity' || s === 'info') return 'Recommendation';
  return 'Low';
}

/* Strip the "'Name' — Status: review clause status" boilerplate the SPA adapter emits, so
   the cards read as business statements instead of debug output.

   ONE definition. There used to be two identical copies (section 11 and section 9), and
   both carried the same bug: the separator class was [—–-], which includes an ASCII
   HYPHEN. The topic group is non-greedy, so "Anti-Dilution ESOP Carve-Out" split at its
   OWN hyphen and rendered as "Anti: Dilution ESOP Carve-Out". The separator must be an
   em/en dash or a SPACED hyphen -- never a bare one. (Same fix as the backend sanitizer;
   the Draft panel showed these titles correctly all along, which was the tell.) */
function stripClauseBoilerplate(raw: string, fallback = ''): string {
  const s = String(raw ?? '').trim();
  if (!s) return fallback;
  const m =
    /^['"‘’“”]?([^'"‘’“”—–]+?)['"‘’“”]?\s*(?:[—–]|\s-\s)\s*([^:]+?)\s*:\s*(?:review\s+clause\s+status|review\s+status)\.?\s*$/i.exec(
      s,
    );
  if (m) {
    const topic = m[1].trim().replace(/\s*\((%|pct|per\s*cent|percent)\)\s*$/i, '').trim();
    const condition = m[2].trim();
    return `${topic}: ${condition}`;
  }
  return s.replace(/[:\s]*review\s+clause\s+status\.?\s*$/i, '').trim() || fallback || s;
}

function _titleOf(text: string, fallback: string): string {
  const first = String(text ?? '').split(/[.\n]/)[0].trim();
  return (first || fallback).slice(0, 200);
}

/* Sprint 5 client feedback 2026-07-12 (issues/10 audit) — replicate
 * the DD §11 renderer's dedup logic here so Risk §11 counts match
 * DD §11's rendered rows verbatim. The DD renderer (ReportPage.tsx
 * line 3364) groups by attribute_key (with irr_pct + DD-IRR-* +
 * DD-CALC-* → g:irr, xirr_pct → g:xirr, everything else → g:${ak||fid})
 * and keeps the highest-severity survivor per group. Iter6b relaxed
 * Risk §11 to flag_id-only dedup, but that broke the invariant the
 * client audit doc requires:
 *   count(Risk §11) == count(DD §11 rendered) + count(SPA §4 rendered)
 * So we return to grouped dedup for DD contributions. SPA rows are
 * still passed through unchanged via getSpaIssueTableRows (which
 * already mirrors SPA §4 rendered rows). */
function _ddGroupKey(flag: Record<string, unknown>): string {
  const ak = String(flag.attribute_key ?? '');
  const fid = String(flag.flag_id ?? flag.id ?? '');
  if (ak === 'irr_pct' || fid.startsWith('DD-IRR-') || fid.startsWith('DD-CALC-IRR')) return 'g:irr';
  if (ak === 'xirr_pct') return 'g:xirr';
  return `g:${ak || fid}`;
}
const _DD_SEV_RANK: Record<string, number> = {
  Critical: 4, High: 4, Medium: 3, Low: 2, Recommendation: 1,
};

export function buildCanonicalFindings(
  risk: Record<string, unknown>,
  spa: Record<string, unknown> | null | undefined,
  dd: Record<string, unknown> | null | undefined,
): CanonicalFinding[] {
  // Signature preserves ``risk`` for call-site compatibility; the
  // canonical pool is now derived exclusively from DD + SPA per the
  // spec (2026-07-09 follow-up 11) so the risk enrichment is unused.
  void risk;
  /* Sprint 5 client feedback 2026-07-09 follow-up 11 + 2026-07-11
     Iter4e refinement — canonical pipeline is a STRICT aggregate:
        Consolidated Findings = DD §11 Flags & Action Items rows
                              + SPA §5 Issue Identification rows
     No LLM-narrated re-summarisations, no synthetic summary rows.
     The DD §5 Overall Investment Risk verdict USED to be appended
     as a synthetic "DD-OVERALL-RISK" F-XX entry — this created a
     double-count against §5's own KPI strip and violated the client's
     rule that §11 must contain one card per underlying row (rule 2
     — "no row may be merged, dropped, or silently re-classified",
     rule 3 — "the DD §5 Overall Investment Risk verdict is not a
     finding").

     Iter6b (2026-07-11 follow-up) — the client re-scoped rule 3:
     they now count the DD §7 Overall Investment Risk Rating
     summary row as one of the 3 rendered DD rows, so it MUST
     surface in §11 as an F-row. The Iter4e removal is reverted;
     §11 = DD §7 rendered rows (including the summary row) + SPA §4
     rendered rows.

     Iter6b also changes the dedup key from `attr_key || flag_id` to
     `flag_id` only. The prior attr_key-first key silently dropped
     distinct BE flags that happened to share an attribute_key
     (e.g. two flags on `entry_ev_usd_m`, or the SPA §4 walker's
     synthetic issue colliding with a BE-emitted issue on the same
     clause). With flag_id-only dedup, every distinct BE row makes
     it into §11. */
  const merged: CanonicalFinding[] = [];
  const seenKeys = new Set<string>();

  const _push = (f: CanonicalFinding, dedupKey: string): void => {
    if (dedupKey && seenKeys.has(dedupKey)) return;
    if (dedupKey) seenKeys.add(dedupKey);
    merged.push(f);
  };

  // 1. Raw DD §11 flags → Financial / Technical / Operational.
  //    Area inferred from the flag id / description.
  //
  //    Sprint 5 client feedback 2026-07-12 (issues/10 audit) —
  //    revert Iter6b flag_id-only dedup to grouped dedup so Risk §11
  //    contains the SAME rows DD §11 renders. Client invariant:
  //      count(Risk §11) == count(DD §11 rendered) + count(SPA §4 rendered)
  //    Group key mirrors ReportPage.tsx _dedupKey (line 3364); highest
  //    severity per group survives, matching the DD renderer's
  //    "description of the higher-severity survivor wins" behaviour.
  if (dd) {
    const _rawDdFlags = _flagsFromReport(dd).filter((f) => {
      const fid = String(f.flag_id ?? f.id ?? '');
      if (!fid) return false;
      if (f._diagnostic === true || fid.toUpperCase().startsWith('DD-DIAG-')) return false;
      return true;
    });
    const _ddByGroup = new Map<string, Record<string, unknown>>();
    for (const f of _rawDdFlags) {
      const key = _ddGroupKey(f);
      const prev = _ddByGroup.get(key);
      const sevNew = _DD_SEV_RANK[_normSeverity(f.severity)] ?? 0;
      const sevPrev = prev ? (_DD_SEV_RANK[_normSeverity(prev.severity)] ?? 0) : -1;
      if (!prev || sevNew > sevPrev) {
        _ddByGroup.set(key, f);
      }
    }
    for (const f of _ddByGroup.values()) {
      const original_id = String(f.flag_id ?? f.id ?? '');
      const attr_key = (f.attribute_key ?? null) as string | null;
      const dedup = original_id;
      const title = _titleOf(String(f.title ?? f.description ?? ''), original_id);
      const area = _inferArea(original_id, String(f.description ?? ''));
      // Legal is reserved for SPA §5 — if a DD flag heuristically
      // trips the Legal check, downgrade to Operational so it doesn't
      // impersonate a SPA finding.
      const safeArea = area === 'Legal' ? 'Operational' : area;
      _push({
        display_id: original_id,  // renumbered at step 4
        original_id,
        area: safeArea,
        severity: _normSeverity(f.severity),
        title,
        description: String(f.description ?? ''),
        source_page: (f.source_page ?? null) as number | null,
        source_document: (f.source_document ?? null) as string | null,
        action_required: String(f.action_required ?? ''),
        responsible: (f.responsible ?? null) as string | null,
        attribute_key: attr_key,
        /* Sprint 5 client feedback 2026-07-11 (§16 audit trail) —
           thread the provenance fields the BE emits on each flag so
           §16 renders them without falling back to the attr-index
           heuristic. quoted_text / clause_number / benchmark /
           confidence / criteria all pass through when populated;
           the FE cell renders '—' when null. */
        quoted_text: (f.quoted_text ?? f.evidence_text ?? null) as string | null,
        clause_number: (f.clause_number ?? f.clause_id ?? null) as string | null,
        benchmark: (() => {
          const b = f.benchmark;
          if (b && typeof b === 'object') {
            const _b = b as Record<string, unknown>;
            return (_b.row_key ?? _b.matched_column ?? _b.extracted_value ?? null) as string | null;
          }
          return (b ?? f.benchmark_reference ?? null) as string | null;
        })(),
        confidence: (f.confidence ?? null) as number | null,
        criteria: (f.criteria ?? f.rule ?? null) as string | null,
      }, dedup);
    }
  }

  // 2. SPA §5 issues → Legal. Modern path via attribute_groups,
  //    fallback to attribute_results walk on old runs.
  const spaAttrGroups = spa?.attribute_groups as Record<string, unknown> | undefined;
  let spa5: SpaIssueRow[] = getSpaIssueTableRows(spaAttrGroups);
  if (spa5.length === 0 && spa) {
    const attrResults = spa.attribute_results as
      | Record<string, Record<string, unknown>>
      | undefined;
    if (attrResults && typeof attrResults === 'object') {
      const rows: SpaIssueRow[] = [];
      for (const [key, entry] of Object.entries(attrResults)) {
        if (!key.startsWith('clause_') || !entry || typeof entry !== 'object') continue;
        const clauseRow = {
          attribute_key: key,
          display_name: (entry.display_name as string) || key,
          category_assigned: (entry.category_assigned as string | null) ?? null,
          value: entry.value,
          raw_value: entry.raw_value,
          source_document: (entry.source_document as string | null) ?? null,
          source_page: (entry.source_page as number | null) ?? null,
        } as unknown as Parameters<typeof computeSpa4Status>[0];
        const st = computeSpa4Status(clauseRow);
        if (st.status === 'Pass') continue;
        rows.push({
          flag_id: `SPA-CLAUSE-${key.toUpperCase()}`,
          severity: st.status as SpaIssueRow['severity'],
          attribute_key: key,
          description: `'${(entry.display_name as string) || key}' — ${st.aiAnalysisLabel}: review clause status.`,
          source_page: (entry.source_page as number | null) ?? null,
          responsible: 'Legal',
          action_required: 'Review clause terms and confirm alignment with investor protection standards.',
        });
      }
      spa5 = rows;
    }
  }
  for (const iss of spa5) {
    const original_id = String(iss.flag_id ?? '');
    const attr_key = iss.attribute_key ?? null;
    /* Iter6b — dedup by flag_id only. Fixes the "ROFR on Transfer
       missing from Risk §11" bug where the SPA §4 walker's
       synthetic issue collided with a BE-emitted issue on the same
       clause attribute_key. */
    const dedup = original_id;
    /* Sprint 5 client feedback 2026-07-11 (§16 audit trail) —
       previously source_document was hard-coded null and
       clause_number was dropped. Route SPA rows through their
       source_document field (defaults to 'SPA Agreement' since
       SPA extraction always reads the SPA) so §16 shows a
       canonical source label; derive clause_number from the
       attribute key so §16 renders "Indemnification Cap Pct"
       instead of "—" for every SPA row. SpaIssueRow doesn't
       formally declare quoted_text / confidence / criteria, but
       the BE payload carries them on many rows — read via a
       widened cast so we don't drop them on the floor. */
    const _issExt = iss as unknown as Record<string, unknown>;
    const _issSrcDoc = (_issExt.source_document as string | null | undefined) ?? 'SPA Agreement';
    const _issClause = attr_key
      ? attr_key.replace(/^clause_/, '').replace(/^section_/, '').replace(/_/g, ' ')
      : null;
    _push({
      display_id: original_id,
      original_id,
      area: 'Legal',
      severity: _normSeverity(iss.severity),
      title: _titleOf(String(iss.description ?? ''), attr_key ?? original_id),
      description: String(iss.description ?? ''),
      source_page: (iss.source_page ?? null) as number | null,
      source_document: _issSrcDoc,
      action_required: String(iss.action_required ?? ''),
      responsible: iss.responsible ?? null,
      attribute_key: attr_key,
      quoted_text: ((_issExt.quoted_text ?? _issExt.evidence_text ?? null) as string | null),
      clause_number: _issClause,
      benchmark: null,
      confidence: (_issExt.confidence as number | null | undefined) ?? null,
      criteria: ((_issExt.criteria ?? _issExt.rule ?? null) as string | null),
    }, dedup);
  }

  // 3. DD §7 Overall Investment Risk Rating summary row →
  //    injected as a synthetic F-XX row.
  //
  //    Iter6b (2026-07-11) — the client counts DD §7's rendered
  //    rows as the invariant right-hand side, and DD §7 renders the
  //    Overall Investment Risk Rating as one of its rows. To keep
  //    count(§11) == count(DD §7 rendered) + count(SPA §4 rendered),
  //    that summary row must reappear as a §11 F-row. Reverts the
  //    Iter4e removal.
  //
  //    Source: dd.risk_profile.summary.overall_rating. Emitted with
  //    area="Risk" and severity matching the verdict so §5's KPI
  //    strip and §11's summary agree on the deal's residual risk.
  const _rp = dd?.risk_profile as Record<string, unknown> | undefined;
  const _rpSumm = _rp?.summary as Record<string, unknown> | undefined;
  const _overallRaw = String(_rpSumm?.overall_rating ?? '').trim();
  const _overall = _overallRaw.charAt(0).toUpperCase() + _overallRaw.slice(1).toLowerCase();
  if (['Critical', 'High', 'Medium', 'Low'].includes(_overall)) {
    /* Sprint 5 client feedback 2026-07-11 (§16 audit trail) — the
       Overall Risk verdict is a computed composite, not a document
       extraction. Populate provenance-adjacent fields with the
       benchmark tier and the classifier "rule" so §16 shows the
       row's origin instead of five "—" cells. */
    const _bp = (_rpSumm?.benchmark_profile as string | undefined)
      ?? _rpSumm?.overall_rating as string | undefined;
    _push({
      display_id: 'DD-OVERALL-RISK',
      original_id: 'DD-OVERALL-RISK',
      area: 'Risk',
      severity: _overall as CanonicalFinding['severity'],
      title: `Overall Investment Risk Rating — ${_overall}`,
      description:
        `Consolidated risk assessment across all identified findings, benchmark ` +
        `evaluation, and mitigation effectiveness. Composite verdict derived from ` +
        `the per-row likelihood × impact classifier across DD Section 5 Risk Profile.`,
      source_page: null,
      source_document: 'DD Report — Section 5 Risk Profile',
      action_required: '',
      responsible: 'Deal Team',
      attribute_key: null,
      quoted_text: null,
      clause_number: null,
      benchmark: _bp ? String(_bp) : null,
      confidence: null,
      criteria: 'Composite verdict = worst-of(likelihood × impact) across DD §5 risk rows, mapped to the OIA benchmark tier.',
    }, 'DD-OVERALL-RISK');
  }

  // 4. Renumber sequentially F-01, F-02, ... preserving original_id.
  //    Sprint 5 client feedback 2026-07-09 follow-up 13 — also strip
  //    any "§N" section-mark from every text field so legacy runs
  //    and LLM echoes render as plain "Section N" wording. Leading
  //    zeros collapse ("§05" → "Section 5").
  const _SECTION_MARK_RE = /§\s*0*(\d+(?:[.\-]\d+)*)/g;
  const _desection = (s: string): string => s.includes('§')
    ? s.replace(_SECTION_MARK_RE, 'Section $1')
    : s;
  const numbered: CanonicalFinding[] = merged.map((f, i) => ({
    ...f,
    display_id: `F-${String(i + 1).padStart(2, '0')}`,
    title: _desection(f.title),
    description: _desection(f.description),
    action_required: _desection(f.action_required),
    source_document: f.source_document ? _desection(f.source_document) : f.source_document,
  }));

  /* Sprint 5 client feedback 2026-07-12 (issues/10 audit) — invariant
   * sanity check. Log a warning if Risk §11 rows don't add up to the
   * sum of DD §11 rendered rows + SPA §4 rendered rows. Development-
   * time signal only; render still happens with whatever we produced. */
  const _ddRendered = merged.filter((f) =>
    f.area === 'Financial' || f.area === 'Technical' || f.area === 'Operational' || f.area === 'Risk',
  ).length;
  const _spaRendered = merged.filter((f) => f.area === 'Legal').length;
  const _spaExpected = getSpaIssueTableRows(spa?.attribute_groups as Record<string, unknown> | undefined).length;
  if (_spaRendered !== _spaExpected) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Risk §11] SPA invariant break — canonical Legal count (${_spaRendered}) ≠ ` +
      `SPA §4 rendered count (${_spaExpected}). ` +
      `Aggregation is dropping or duplicating SPA rows.`,
    );
  }
  // eslint-disable-next-line no-console
  console.info(
    `[Risk §11] canonical counts — DD contributions: ${_ddRendered}, ` +
    `SPA contributions: ${_spaRendered}, total: ${numbered.length}`,
  );

  return numbered;
}


function _flagsFromReport(report: Record<string, unknown> | null | undefined): Array<Record<string, unknown>> {
  if (!report || typeof report !== 'object') return [];
  const sections = report.sections as Record<string, unknown> | undefined;
  if (sections && typeof sections === 'object') {
    const fs = sections.flags_section as Record<string, unknown> | undefined;
    const rows = fs?.rows;
    if (Array.isArray(rows) && rows.length > 0) {
      return rows.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object');
    }
  }
  for (const legacy of ['flags', 'dd_flags', 'spa_flags']) {
    const v = (report as Record<string, unknown>)[legacy];
    if (Array.isArray(v) && v.length > 0) {
      return v.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object');
    }
  }
  return [];
}


function getNested(o: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, o);
}

/* The page itself. */
export function RiskReportPage(): JSX.Element {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ConsolidatedReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeToc, setActiveToc] = useState<string>('s1');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /* Sprint 5 client feedback 2026-07-13 — the page is split into two collapsible
     panels, and BOTH start collapsed. The landing view is the header plus the two
     panel titles, so the analyst chooses what to open instead of scrolling past a
     report they may not want. */
  const [draftOpen, setDraftOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  /* Regenerate is two-step: it spends money on external verification calls and
     replaces the verified claims, so it must not fire on a stray click. */
  const [regenConfirm, setRegenConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  useEffect(() => {
    if (!auditId) return;
    getConsolidatedReport(auditId)
      .then(setReport)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load report'));
  }, [auditId]);

  /* Update the highlighted TOC item based on scroll position.
   *
   * Sprint 5 client feedback 2026-07-08 (UI Ticket — TOC scroll-spy
   * last-section fix) — bottom-of-page guard mirrors ReportPage:
   * when scrolled within 40 px of the document bottom, force active
   * = the last MOUNTED TOC entry. The Risk report's last section is
   * often short enough that it never crosses the intersection
   * observer's active band, leaving the highlight on the previous
   * section forever. */
  /* Re-runs when the AI panel is collapsed/expanded too: its sections
     unmount with it, so the observer must re-attach to the new nodes. */
  useEffect(() => {
    if (!report) return;
    const handler = (entries: IntersectionObserverEntry[]) => {
      const top = entries.filter((e) => e.isIntersecting).sort(
        (a, b) => b.intersectionRatio - a.intersectionRatio,
      )[0];
      if (top) setActiveToc(top.target.id);
    };
    const observer = new IntersectionObserver(handler, {
      rootMargin: '-30% 0px -60% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    const mountedIds: string[] = [];
    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        mountedIds.push(id);
      }
    });
    const onScroll = () => {
      if (!mountedIds.length) return;
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 40;
      if (scrolledToBottom) {
        setActiveToc(mountedIds[mountedIds.length - 1]);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [report, aiOpen]);

  if (error) {
    return (
      <div className="page-body">
        <div className="alert alert-fail">{error}</div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="page-body" style={{ textAlign: 'center', paddingTop: 80 }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  const { project, dd, spa, risk, claims, api_calls, api_total_cost_usd } = report;
  const aiOpinionClass =
    (risk.ai_opinion_class as string | null) ?? 'Disclaimer';

  /* Sprint 5 client feedback 2026-06-29 — Recommendation derivation
     now folds in the §15 Auditor Assurance Opinion (4-tier ladder:
     Effective / Improvement Required / Partially Effective /
     Ineffective). Observed AUDIT-2026-0079 had Auditor Assurance =
     Ineffective ("pervasive control failure") but Recommendation =
     Under Review (because ai_opinion_class = Disclaimer was winning
     in the prior matrix). When Internal Audit issues "Ineffective"
     the deal cannot be approved — Reject must override the ISA
     Disclaimer reading. Same logic for "Partially Effective" →
     Conditional rather than Under Review.

     Harshest-wins matrix (top→bottom):
       Ineffective         OR Adverse           OR Critical rating → Reject
       Partially Effective OR Qualified         OR High rating     → Conditional
       Improvement Required                                         → Conditional
       Disclaimer (verification inconclusive, no harsher signal)    → Under Review
       Effective + Unmodified + Low/Medium                          → Approve

     The rating source matches what §15 uses (same three-step
     fallback) so the two surfaces always reflect the same rating. */
  const _enrichment = risk.enrichment as Record<string, unknown> | null;
  const _synth = _enrichment?.synthesis as Record<string, unknown> | undefined;
  const _overallSynth = _synth?.risk_assessment_overall as Record<string, unknown> | undefined;
  const _auditorOpinion = _synth?.auditor_opinion as Record<string, unknown> | undefined;
  const _auditorClass = String(_auditorOpinion?.class ?? '').trim();
  const _ddSectionsForRating = (dd.report as Record<string, unknown> | null) ?? {};
  const _spaSectionsForRating = (spa.report as Record<string, unknown> | null) ?? {};
  const analysisSummaryRating = String(
    risk.overall_risk_rating
      ?? _overallSynth?.rating
      ?? risk.overall_risk_rating_baseline
      ?? _computeHeuristicBaseline(
        _flagsFromReport(_ddSectionsForRating),
        _flagsFromReport(_spaSectionsForRating),
      ),
  );
  const _ratingNorm = analysisSummaryRating.trim().toLowerCase();
  const _ratingIsCritical = _ratingNorm === 'critical';
  const _ratingIsHigh = _ratingNorm === 'high';

  // 2026-06-29 — diagnostic log so analysts can verify in DevTools
  // Console exactly which inputs drove the §1 Recommendation. L2
  // (security review): gate on Vite's DEV flag so prod bundles stay
  // silent. Toggleable via `?debug=rec` query param too — paste that
  // into the URL on any env to force-enable the log.
  if (
    typeof window !== 'undefined'
    && (import.meta.env.DEV
      || window.location?.search?.includes('debug=rec'))
  ) {
    // eslint-disable-next-line no-console
    console.info('[RiskReport.§1] Recommendation inputs:', {
      auditorClass: _auditorClass,
      aiOpinionClass,
      analysisSummaryRating,
      isCritical: _ratingIsCritical,
      isHigh: _ratingIsHigh,
    });
  }

  /* Sprint 5 client feedback 2026-07-12 (issues/11 follow-up) —
   * Recommendation label now shows the OIA Appendix B tier name
   * directly (Unacceptable Risk Exposure / Major Improvement
   * Opportunity / Some Improvement Opportunity / Satisfactory /
   * Well-Managed) instead of the Approve/Conditional/Reject legacy
   * verbs. Client confirmed on AUDIT-2026-0146 that the same tier
   * word should appear on the KPI tile, the §1 Recommendation card,
   * and the §15 Auditor Assurance Opinion so all three surfaces
   * carry one vocabulary. */
  const { recommendationLabel, recommendationVariant } = ((): {
    recommendationLabel: string;
    recommendationVariant: 'pass' | 'watch' | 'fail' | 'info';
  } => {
    // Sprint 5 client feedback 2026-07-03 (revised) — inherit the
    // recommendation from the single-source BE Conclusion payload
    // (synthesis.conclusion.recommendation). PDF §2 reads the same
    // payload verbatim, so HTML and PDF cannot disagree.
    //
    // Revised Annexure B vocabulary maps onto the 4-value badge as:
    //   Reject                        → Reject (fail)
    //   Under Review with conditions  → Conditional (watch)
    //     — Major Improvement tier still needs conditions attached
    //   Under Review                  → Under Review (info)
    //   Approved                      → Approve (pass)
    //   Approved (Preferred)          → Approve (pass)
    //     — Well-Managed reads as Approve on the enum; the coloured
    //       bar shows the finer distinction.
    /* issues/11 follow-up — map both the BE payload recommendation
     * verb AND the auditor class → the OIA Appendix B tier name so
     * the badge, KPI tile and §15 opinion all read the same string.
     * variant (colour) preserves the semantic tier: worst-tier =
     * fail (red), next = watch (amber), … best-tier = pass (green). */
    const _conclusion = (_synth as Record<string, unknown> | undefined)?.conclusion as
      { recommendation?: string } | undefined;
    const _payloadRec = String(_conclusion?.recommendation ?? '').trim();
    if (_payloadRec) {
      if (_payloadRec === 'Reject'
          || _payloadRec === 'Unacceptable Risk Exposure') {
        return { recommendationLabel: 'Unacceptable Risk Exposure', recommendationVariant: 'fail' };
      }
      if (_payloadRec === 'Under Review with conditions'
          || _payloadRec === 'Conditional'
          || _payloadRec === 'Major Improvement Opportunity') {
        return { recommendationLabel: 'Major Improvement Opportunity', recommendationVariant: 'watch' };
      }
      if (_payloadRec === 'Under Review'
          || _payloadRec === 'Some Improvement Opportunity') {
        return { recommendationLabel: 'Some Improvement Opportunity', recommendationVariant: 'info' };
      }
      if (_payloadRec === 'Approved'
          || _payloadRec === 'Approve'
          || _payloadRec === 'Pass'
          || _payloadRec === 'Satisfactory') {
        return { recommendationLabel: 'Satisfactory', recommendationVariant: 'pass' };
      }
      if (_payloadRec === 'Approved (Preferred)'
          || _payloadRec === 'Well-Managed') {
        return { recommendationLabel: 'Well-Managed', recommendationVariant: 'pass' };
      }
    }

    // Legacy fallback — pre-payload runs. Defensively normalise so
    // the matrix isn't defeated by a stray space or uppercase.
    const aClass = _auditorClass.trim().toLowerCase();
    const aOpinion = aiOpinionClass.trim();

    if (
      aClass === 'ineffective'
      || aClass === 'unacceptable risk exposure'
      || aOpinion === 'Adverse'
      || _ratingIsCritical
    ) {
      return { recommendationLabel: 'Unacceptable Risk Exposure', recommendationVariant: 'fail' };
    }
    if (
      aClass === 'partially effective'
      || aClass === 'major improvement opportunity'
      || aOpinion === 'Qualified'
      || _ratingIsHigh
    ) {
      return { recommendationLabel: 'Major Improvement Opportunity', recommendationVariant: 'watch' };
    }
    if (aClass === 'improvement required'
        || aClass === 'some improvement opportunity') {
      return { recommendationLabel: 'Some Improvement Opportunity', recommendationVariant: 'info' };
    }
    if (aOpinion === 'Disclaimer') {
      return { recommendationLabel: 'Some Improvement Opportunity', recommendationVariant: 'info' };
    }
    if (aOpinion === 'Unmodified'
        || aClass === 'effective'
        || aClass === 'satisfactory') {
      return { recommendationLabel: 'Satisfactory', recommendationVariant: 'pass' };
    }
    return { recommendationLabel: 'Some Improvement Opportunity', recommendationVariant: 'info' };
  })();

  /* Pull common DD slices once so each section can reach in cleanly. */
  const ddSections = (dd.report as Record<string, unknown> | null) ?? {};
  const spaSections = (spa.report as Record<string, unknown> | null) ?? {};

  /* Sprint 5 client feedback 2026-06-20: Section 1's Jurisdiction /
     Deal Size / Risk Rating fields rendered as "—" or "Not Found"
     because the Project intake row often doesn't carry those values
     (analysts skip them at create time, expecting the engine to
     extract). Fall back to:
       - Jurisdiction → DD's `geography` attribute (or `jurisdiction`)
       - Deal Size    → DD's `deal_size_oia_equity_usd_m` or
                        `deal_size_total_round_usd_m` attribute
       - Risk Rating  → derive from ai_opinion_class when the explicit
                        overall_risk_rating column is null
     This makes Section 1 reflect what the engine actually KNOWS about
     the deal, not just what the analyst typed at intake. */
  /* attribute_results comes back from the backend as a dict
     ({attr_key: attr_obj, …}), not an array, so calling .find() on it
     directly throws "ddAttrs.find is not a function" and blanks the
     page. Normalize to an array up front. Accept either shape
     defensively. */
  const ddAttrsRaw = ddSections.attribute_results;
  const ddAttrs: Array<Record<string, unknown>> = Array.isArray(ddAttrsRaw)
    ? (ddAttrsRaw as Array<Record<string, unknown>>)
    : ddAttrsRaw && typeof ddAttrsRaw === 'object'
      ? Object.entries(ddAttrsRaw as Record<string, unknown>)
          .filter(([k]) => !k.startsWith('_'))
          .map(([k, v]) =>
            v && typeof v === 'object'
              ? { attribute_key: k, ...(v as Record<string, unknown>) }
              : { attribute_key: k, value: v },
          )
      : [];
  function pickDdValue(...keys: string[]): string | null {
    for (const k of keys) {
      const a = ddAttrs.find((x) => x.attribute_key === k);
      const v = a?.value;
      if (v != null && v !== '' && v !== '—') return String(v);
    }
    return null;
  }

  /* Sprint 5 client feedback 2026-06-22 — DD-extracted geography wins
     over the project.jurisdiction value the user typed at project
     creation. The user often types a placeholder ("MENA", "Test")
     when filing the audit; the document-extracted value is the
     source of truth. Fall back to project.jurisdiction only when DD
     didn't extract anything usable. */
  const jurisdictionDisplay = (() => {
    const fromDd = pickDdValue(
      'geographical_allocation',
      'geography',
      'jurisdiction',
    );
    if (fromDd && fromDd.trim() && fromDd.toLowerCase() !== 'not found') {
      return fromDd;
    }
    if (project.jurisdiction && project.jurisdiction.trim()) {
      return project.jurisdiction;
    }
    return 'Not found in documents';
  })();

  const dealSizeDisplay = (() => {
    /* Sprint 5 client feedback 2026-06-22 — strip trailing-zero clutter
       so a raw "30.0000" renders as "$30M" instead of "USD 30.0000M".
       Pass through already-formatted strings ("USD 30M", "$30 million"
       etc.) unchanged. */
    const fmtUsdM = (n: number): string => {
      const intPart = Math.trunc(n);
      const decimals = n === intPart ? '' : (n.toFixed(2).replace(/\.?0+$/, '').replace(/^\d+/, ''));
      return `$${intPart}${decimals}M`;
    };
    if (project.deal_size_usd_m != null) {
      return fmtUsdM(project.deal_size_usd_m);
    }
    const fromDd = pickDdValue('deal_size_oia_equity_usd_m', 'deal_size_total_round_usd_m', 'deal_size');
    if (!fromDd) return '—';
    const looksFormatted = /USD|\$|million/i.test(fromDd);
    if (looksFormatted) return fromDd;
    const numeric = Number(String(fromDd).replace(/,/g, ''));
    if (!Number.isNaN(numeric)) return fmtUsdM(numeric);
    return fromDd;
  })();

  const riskRatingDisplay = (() => {
    /* Sprint 5 client feedback 2026-06-21: Section 01 was the only
       surface still showing "Not Determined" because it only looked at
       risk.overall_risk_rating + ISA opinion class. Section 15
       (AnalysisSummary) already falls back through the synthesised
       rating + heuristic baseline; use the same chain here so the
       investment-decision card stays in sync with the analysis
       summary. Priority:
         1. explicit risk.overall_risk_rating column
         2. synthesizer's risk_assessment_overall.rating
         3. ISA opinion class → rating mapping
         4. heuristic baseline from DD + SPA flag counts
         5. "Not Determined" only if nothing at all is available */
    if (risk.overall_risk_rating) return String(risk.overall_risk_rating);
    const enrichment = risk.enrichment as Record<string, unknown> | null | undefined;
    const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
    const overallSynth = synth?.risk_assessment_overall as Record<string, unknown> | undefined;
    const synthRating = overallSynth?.rating;
    if (typeof synthRating === 'string' && synthRating.trim()) return synthRating;
    if (aiOpinionClass === 'Unmodified') return 'Low';
    if (aiOpinionClass === 'Qualified') return 'Medium';
    if (aiOpinionClass === 'Adverse') return 'High';
    if (risk.overall_risk_rating_baseline) return String(risk.overall_risk_rating_baseline);
    const ddFlagsForBaseline = (ddSections.flags as Array<Record<string, unknown>> | undefined) ?? [];
    const spaFlagsForBaseline = (spaSections.flags as Array<Record<string, unknown>> | undefined) ?? [];
    if (ddFlagsForBaseline.length || spaFlagsForBaseline.length) {
      return _computeHeuristicBaseline(ddFlagsForBaseline, spaFlagsForBaseline);
    }
    return 'Not Determined';
  })();

  const claimsByStatus = claims.reduce<Record<string, number>>((acc, c) => {
    const s = _effectiveClaimStatus(c as unknown as Record<string, unknown>);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  /* Sprint 5 client feedback 2026-06-30 (FE Issues #1) — Risk Report
     header redesigned to match SPA Report's card layout. Compute the
     metadata-row values once before render so the JSX stays clean. */
  const _rhAssessmentDate = (() => {
    const raw = (risk.created_at as string | null | undefined)
      ?? (project as Record<string, unknown>).created_at as string | null | undefined;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  })();
  const _rhProcessingTime = (() => {
    const startRaw = risk.created_at as string | null | undefined;
    const endRaw = risk.completed_at as string | null | undefined;
    if (!startRaw || !endRaw) return null;
    try {
      const ms = new Date(endRaw).getTime() - new Date(startRaw).getTime();
      if (ms <= 0 || !Number.isFinite(ms)) return null;
      const totalSec = Math.round(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return m > 0 ? `${m} min ${s} sec` : `${s} sec`;
    } catch { return null; }
  })();
  const _rhCritical = (() => {
    // Try the synth breakdown first (covers DD §11 + SPA §05 combined,
    // matches what §15 Analysis Summary shows).
    const enrich = risk.enrichment as Record<string, unknown> | null | undefined;
    const synth = enrich?.synthesis as Record<string, unknown> | undefined;
    const ao = synth?.auditor_opinion as { breakdown?: { critical_n?: number } } | undefined;
    if (ao?.breakdown && typeof ao.breakdown.critical_n === 'number') return ao.breakdown.critical_n;
    // Fall back to counting consolidated_findings.
    const findings = (synth?.consolidated_findings as Array<Record<string, unknown>> | undefined) ?? [];
    return findings.filter((f) => String(f.severity ?? '').toLowerCase() === 'critical').length;
  })();
  /* Colour for the VALUE segment of the rating pill. The bare default is
     "Not Determined" — it stays neutral, since a coloured chip there would
     read as a verdict when none was reached. */
  const _rhRatingBadgeClass = (() => {
    const r = riskRatingDisplay.toLowerCase();
    if (r === 'critical' || r === 'high') return 'rsk-rh-rating-value rsk-rh-rating-fail';
    if (r === 'medium') return 'rsk-rh-rating-value rsk-rh-rating-watch';
    if (r === 'low') return 'rsk-rh-rating-value rsk-rh-rating-pass';
    return 'rsk-rh-rating-value';
  })();

  return (
    <div className="page-body risk-report-page">
      <header className="rsk-report-header">
        <div className="breadcrumb">
          <a
            href="/projects"
            onClick={(e) => { e.preventDefault(); navigate('/projects'); }}
          >Projects</a>
          <span className="bc-sep">/</span>
          <a
            href={`/projects/${project.audit_id}`}
            onClick={(e) => { e.preventDefault(); navigate(`/projects/${project.audit_id}`); }}
          >{project.audit_id}</a>
          <span className="bc-sep">/</span>
          {/* Sprint 5 client feedback 2026-07-11 (Iter4 G1/G2) —
              "Risk Report" label surfaces retitled to "Draft Report"
              per client direction ("we're still iterating, not
              publishing"), and the eyebrow's leading "Risk" chip
              renamed to "Report" so the eyebrow reads as a generic
              consolidated view instead of a risk-only surface. The
              per-project h1 keeps the deal name but drops the
              "Risk Assessment" prefix and uses "Draft Report" so
              the title matches the breadcrumb. */}
          <span>Draft Report</span>
        </div>
        <p className="rsk-rh-eyebrow">
          Report · Consolidated Audit · DD · SPA · External Verification
        </p>
        <div className="rsk-rh-title-row">
          <h1 className="rsk-rh-title">
            Draft Report — {project.project_name}
          </h1>
          <div className="rsk-rh-actions">
            {/* Sprint 5 client feedback 2026-07-11 (Task #714) — legacy
                DOCX audit report retired per client direction ("we need
                only new one"). Only the OIA-branded PDF remains. */}
            <button
              className="btn btn-accent"
              disabled={downloading}
              onClick={async () => {
                setDownloadError(null);
                setDownloading(true);
                try {
                  await downloadOiaAuditPdf(project.audit_id);
                } catch (e) {
                  setDownloadError(
                    e instanceof Error ? e.message : 'PDF download failed',
                  );
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloading ? 'Preparing PDF…' : 'Download Audit Report PDF'}
            </button>
            {/* Regenerate — re-runs Stage 3 only. DD and SPA are READ, never
                recomputed, so their results cannot be disturbed. Rebuilds the
                synthesis (findings, recommendations, opinion), re-verifies the
                external claims, and re-composes the Draft Report at the end — i.e.
                both panels on this page.

                Two-step, because it is not free: it makes paid external API calls
                and REPLACES the verified claims. A single mis-click should not
                spend money. */}
            {regenConfirm ? (
              <>
                <button
                  className="btn btn-accent"
                  disabled={regenerating}
                  onClick={async () => {
                    setRegenError(null);
                    setRegenerating(true);
                    try {
                      await runRiskAssessment(project.audit_id);
                      navigate(`/projects/${project.audit_id}/risk/processing`);
                    } catch (e) {
                      setRegenError(
                        e instanceof Error ? e.message : 'Failed to start regeneration',
                      );
                      setRegenerating(false);
                      setRegenConfirm(false);
                    }
                  }}
                >
                  {regenerating ? 'Starting…' : 'Yes, regenerate'}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={regenerating}
                  onClick={() => setRegenConfirm(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="btn btn-secondary"
                onClick={() => setRegenConfirm(true)}
                data-tip={
                  'Re-runs the AI analysis and rebuilds both reports on this page.\n'
                  + 'Does not re-run DD or SPA. Takes a few minutes and makes paid\n'
                  + 'external verification calls; your saved Draft Report edits are kept.'
                }
              >
                Regenerate
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/projects/${project.audit_id}`)}
            >
              ← Back
            </button>
          </div>
        </div>
        <div className="rsk-rh-meta">
          {/* Caption + value are ONE chip, so the meta row's gap can't
              split them — "Risk Assessment: High" reads as a single
              statement. Screen readers hear that statement once (the two
              segments are hidden), not two orphaned words. */}
          <span
            className="rsk-rh-rating"
            role="group"
            aria-label={`Risk Assessment: ${riskRatingDisplay}`}
            data-tip="Overall risk rating — derived from the DD and SPA findings plus external verification."
          >
            <span className="rsk-rh-rating-label" aria-hidden="true">Risk Assessment</span>
            <span className={_rhRatingBadgeClass} aria-hidden="true">{riskRatingDisplay}</span>
          </span>
          <span className="rsk-rh-meta-sep">·</span>
          <span>{_rhAssessmentDate}</span>
          <span className="rsk-rh-meta-sep">·</span>
          <span>Job: <strong>{project.audit_id}</strong></span>
          {_rhCritical > 0 && (
            <>
              <span className="rsk-rh-meta-sep">·</span>
              <span><strong>{_rhCritical}</strong> Critical Finding{_rhCritical === 1 ? '' : 's'}</span>
            </>
          )}
          {_rhProcessingTime && (
            <>
              <span className="rsk-rh-meta-sep">·</span>
              <span>Processed in <strong>{_rhProcessingTime}</strong></span>
            </>
          )}
        </div>
        {downloadError && (
          <p className="t-xs" style={{ color: 'var(--fail)', marginTop: 'var(--s2)' }}>
            {downloadError}
          </p>
        )}
        {regenError && (
          <p className="t-xs" style={{ color: 'var(--fail)', marginTop: 'var(--s2)' }}>
            {regenError}
          </p>
        )}
      </header>

      {/* Sprint 5 client feedback 2026-07-12 — the report is split into two
          collapsible panels. The Draft Report (the analyst's own write-up) is
          the deliverable; the Detailed AI Report is the machine-generated
          evidence behind it. The Contents TOC indexes §1–§17, so it belongs
          INSIDE the AI panel and collapses away with it. */}
      <div className="rsk-panels">
        <CollapsiblePanel
          title="Draft Report"
          subtitle="The audit report — same document the PDF prints"
          open={draftOpen}
          onToggle={() => setDraftOpen((v) => !v)}
        >
          {/* Mounted only while open: the panel fetches its own payload, and a
              collapsed panel shouldn't pay for a request nobody asked for. */}
          {draftOpen && <DraftReport auditId={project.audit_id} />}
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Detailed AI Report"
          subtitle="Sections 1–17 · Consolidated DD, SPA & external verification"
          open={aiOpen}
          onToggle={() => setAiOpen((v) => !v)}
        >
      <div className="report-layout">
        {/* Sticky TOC — Sprint 5 client feedback 2026-06-23: refactored
            from inline-styled aside to the shared .report-toc class
            so the Risk Report matches DD + SPA reports for the sticky
            behaviour. The .report-layout grid has align-items:start
            so the aside isn't stretched to full content height. */}
        <aside className="report-toc">
          <span className="report-toc-label">Contents</span>
          <nav>
            {TOC_ITEMS.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className={`report-toc-item${activeToc === id ? ' active' : ''}`}
                /* Sprint 5 client feedback 2026-06-25 (First set.docx
                   issue 13) — clicking a TOC item scrolled the
                   content correctly but the highlighted item in the
                   sidebar stayed on the previously-active section
                   because the IntersectionObserver scroll-spy can
                   miss fast anchor-jump events. Update activeToc
                   immediately on click so the highlight follows the
                   user's intent rather than waiting for the observer
                   to catch up. */
                /* Sprint 5 client feedback 2026-07-12 — every TOC target
                   lives inside the Detailed AI Report panel. If that panel
                   is collapsed the section isn't in the DOM, so the browser
                   has nothing to jump to. Expand it first, then scroll once
                   React has painted the newly-mounted section. */
                onClick={(e) => {
                  setActiveToc(id);
                  if (aiOpen) return;
                  e.preventDefault();
                  setAiOpen(true);
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      document.getElementById(id)?.scrollIntoView({ block: 'start' });
                    });
                  });
                }}
              >
                {label}
              </a>
            ))}
          </nav>
          {/* Sprint 5 client feedback 2026-06-23 (FE Four Issue 3):
              sticky Download PDF button anchored to the bottom of
              the Contents card. Same handler as the page-header
              button so error + busy state stay in sync across both
              entry points. */}
          <div className="report-toc-footer">
            <button
              type="button"
              className="btn btn-accent"
              disabled={downloading}
              onClick={async () => {
                setDownloadError(null);
                setDownloading(true);
                try {
                  await downloadOiaAuditPdf(project.audit_id);
                } catch (e) {
                  setDownloadError(
                    e instanceof Error ? e.message : 'PDF download failed',
                  );
                } finally {
                  setDownloading(false);
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
            {downloadError && (
              <span className="toc-footer-err">{downloadError}</span>
            )}
          </div>
        </aside>

        {/* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 6):
            wrap every section in a styled card container per the design
            doc — each major section now has rounded borders, consistent
            padding, and stronger spacing between sections. Applied via
            the .risk-report-sections wrapper class so we don't have to
            touch every individual <section>. Sections that previously
            had inline padding/border styling cascade-through cleanly
            because the wrapper rule uses `> section` direct-children
            selector with low specificity. */}
        <div className="risk-report-sections" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s7)' }}>

          {/* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6
              continuation): Assessment Summary KPI strip at the top so
              the dashboard leads with executive-level numbers before
              §1 narrative. 5 KPI tiles: Risk Rating, Recommendation,
              Critical Findings count, Sections Detected (X of Y),
              Verification Confidence. Each tile carries a severity-
              coloured accent and a tap-target jumping to the relevant
              section. */}
          <AssessmentSummaryKpis
            project={project}
            risk={risk}
            spa={spaSections}
            dd={ddSections}
            recommendationLabel={recommendationLabel}
            recommendationVariant={recommendationVariant}
            riskRatingDisplay={riskRatingDisplay}
            claims={claims}
          />

          {/* §1 — Investment Decision Summary
                 Sprint 5 client feedback 2026-06-23 — port from
                 Sprint5/FE/Risk report UI mockup. Uses the design's
                 .ra-meta-grid (3-col grid, single bordered block with
                 cell separators) + .ra-banner (left-bordered notice
                 banner with tag pill + display headline). */}
          <section className="report-section">
            <SectionHead
              id="s1"
              eyebrow="Section 01 · Immediate investment recommendation"
              title="Investment Decision Summary"
            />
            <div className="ra-meta-grid">
              <div className="ra-meta-cell">
                <div className="k">Project Name</div>
                <div className="v">{project.project_name}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Investment Type</div>
                <div className="v">{project.investment_type ?? '—'}</div>
              </div>
              {/* Sprint 5 client feedback 2026-06-25 (Issues/§1) —
                  Sector field replaced with Asset Class (DD-extracted)
                  per analyst request. Sector still surfaces in §4
                  Investment Classification where it belongs. Asset
                  Class falls back to "—" if DD hasn't extracted it. */}
              <div className="ra-meta-cell">
                <div className="k">Asset Class</div>
                <div className="v">{pickDdValue('asset_class') ?? '—'}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Jurisdiction</div>
                <div className="v">{jurisdictionDisplay}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Deal Size</div>
                <div className="v mono">{dealSizeDisplay}</div>
              </div>
              {/* Sprint 5 client feedback 2026-06-25 (Issues/§1) —
                  Analyst Name surfaced (resolved BE-side from
                  project.lead_analyst_id → users.full_name). */}
              <div className="ra-meta-cell">
                <div className="k">Analyst</div>
                <div className="v">{project.analyst_name ?? '—'}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Created</div>
                <div className="v mono">
                  {project.created_at ? new Date(project.created_at).toLocaleDateString() : '—'}
                </div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Status</div>
                <div className="v">
                  {/* Sprint 5 client feedback 2026-06-26 — pin Status to
                      "Completed" on the Risk Report. By the time the
                      analyst is reading this page, DD + SPA + Risk
                      have all finished (you can't reach the Risk
                      Report otherwise). The live project.overall_status
                      column lags during the same-session pipeline and
                      occasionally still reads "In Progress" because
                      the Sprint-4 worker hasn't refreshed it — which
                      misled analysts into thinking the report was
                      incomplete. The Recommendation cell carries the
                      verdict; the Status cell now consistently
                      confirms the data is final. */}
                  <span data-tip="Completed — DD, SPA and Risk Assessment have all finished.">
                    <Badge variant="pass">Completed</Badge>
                  </span>
                </div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Recommendation</div>
                <div className="v">
                  <Badge variant={recommendationVariant}>{recommendationLabel}</Badge>
                </div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Risk Rating</div>
                <div className="v">
                  <Badge
                    variant={statusVariant(
                      riskRatingDisplay === 'Low' ? 'pass'
                      : riskRatingDisplay === 'Medium' ? 'watch'
                      : riskRatingDisplay === 'High' ? 'fail'
                      : 'neutral'
                    )}
                  >
                    {riskRatingDisplay}
                  </Badge>
                </div>
              </div>
            </div>
            {/* Decision banner — left-border colour follows the
                 recommendation variant (pass/watch/fail/info) so the
                 emphasis matches the verdict severity. */}
            <div
              className="ra-banner"
              style={{
                borderLeftColor: `var(--${recommendationVariant === 'pass' ? 'pass' : recommendationVariant === 'watch' ? 'watch' : recommendationVariant === 'fail' ? 'fail' : 'info'})`,
              }}
            >
              {/* Sprint 5 client feedback 2026-07-11 (Iter5 #1) —
                  the "Not Approved" / "Approved" / "Conditional
                  Approval" chip on the red decision banner was
                  redundant. The h3 recommendation line beneath
                  already communicates the verdict; the chip
                  duplicated it. Removed entirely. */}
              {/* issues/11 follow-up — recommendationLabel now
                  carries the OIA Appendix B tier name. Map onto the
                  verdict sentence via variant so the h3 continues
                  to read as a business decision even though the
                  chip label above shows the tier vocabulary. */}
              <h3>
                {recommendationVariant === 'pass'
                  ? 'Recommended for investment'
                  : recommendationVariant === 'watch'
                  ? 'Recommended, subject to conditions'
                  : recommendationVariant === 'fail'
                  ? 'Not recommended for investment'
                  : 'Verdict pending — verification incomplete'}
              </h3>
              {/* Sprint 5 client feedback 2026-06-30 — §01 Investment
                  Decision Summary reads the BE-synthesised
                  decision-oriented paragraph that justifies the
                  final recommendation given the audit outcome +
                  severity profile. Falls back to the legacy ISA
                  opinion paragraph only when the new field hasn't
                  been populated.
                  Sprint 5 client feedback 2026-07-03 — LLM prose
                  frequently includes **bold** and *italic* markers.
                  FormattedText renders those inline instead of
                  leaving literal asterisks in the UI. */}
              <FormattedText
                text={
                  (_auditorOpinion?.investment_decision_summary as string | null) ??
                  (risk.ai_opinion_paragraph as string | null) ??
                  'No AI-generated opinion paragraph. Re-run Risk Assessment with TAVILY_API_KEY configured for substantive opinion text.'
                }
              />
            </div>
          </section>

          {/* §2 — Executive Summary
                 Sprint 5 client feedback 2026-06-25 (Issues/FE doc #5)
                 — 3-card layout (Financial Position / Risk Summary /
                 Legal Summary) + Conclusion panel, content sourced
                 from existing DD / SPA / Risk data (not new LLM
                 prose). */}
          <section className="report-section">
            <SectionHead
              id="s2"
              eyebrow="Section 02 · Snapshot across DD · SPA · Risk"
              title="Executive Summary"
            />
            <ExecutiveSummaryCards dd={ddSections} spa={spaSections} risk={risk} />
          </section>

          {/* §3 — Financial Assessment (from DD report) */}
          <section className="report-section">
            <SectionHead
              id="s3"
              eyebrow="Section 03 · Benchmark validation · source: Financial DD"
              title="Financial Assessment"
            />
            <DdFinancialTable dd={ddSections} />
          </section>

          {/* §4 — Investment Classification — 3-cell meta-grid matching
                 the design's "Sub Sector / Stage / Benchmark Applied"
                 row. */}
          <section className="report-section">
            <SectionHead
              id="s4"
              eyebrow="Section 04 · Sub-classification & benchmark applied"
              title="Investment Classification"
            />
            {/* Sprint 5 client feedback 2026-06-25 (Issues/§4) —
                column shape updated per analyst feedback:
                  • Asset Class moved to §1 (Investment Decision Summary)
                  • Added Sub Sector (DD `sub_sector` attribute)
                  • Added Stage (DD `stage` attribute — pre-revenue /
                    growth / mature etc.)
                  • Sector retained here (it's the canonical
                    granular sector label for THIS deal — Asset
                    Class is the coarser bucket)
                  • Benchmark Applied kept as the closing meta cell. */}
            {(() => {
              // Sprint 5 client feedback 2026-07-07 (BE Issue #5) —
              // Split DD's verbatim sub_sector phrase into (Sector,
              // Sub-sector) via the shared parser and add Asset Class
              // + Geography cells so Risk §4 mirrors the DD §3
              // classification the reviewer expects. The parser
              // handles parenthetical / em-dash / en-dash / colon /
              // slash / comma / single value formats; see
              // frontend/src/utils/subsectorParser.ts.
              const rawSubSector = pickDdValue('sub_sector');
              const { sector: parsedSector, subsector: parsedSubsector } =
                parseSectorSubsector(rawSubSector ? String(rawSubSector) : null);
              const displaySector = parsedSector
                ?? project.sector
                ?? '—';
              const displaySubsector = parsedSubsector ?? '—';
              const displayAssetClass = pickDdValue('asset_class') ?? '—';
              const displayGeography = pickDdValue('geography') ?? project.jurisdiction ?? '—';
              return (
            <div className="ra-meta-grid">
              <div className="ra-meta-cell">
                <div className="k">Investment Type</div>
                <div className="v">{project.investment_type ?? '—'}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Sector</div>
                <div className="v">{displaySector}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Sub-sector</div>
                <div className="v">{displaySubsector}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Asset Class</div>
                <div className="v">{displayAssetClass}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Geography</div>
                <div className="v">{displayGeography}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Stage</div>
                <div className="v">{pickDdValue('stage', 'revenue_status') ?? '—'}</div>
              </div>
              <div className="ra-meta-cell">
                <div className="k">Benchmark Applied</div>
                <div className="v">
                  {(() => {
                    // Sprint 5 client feedback 2026-07-06 (2nd pass) —
                    // for non-Oman deals whose LLM extraction remapped
                    // "Local Direct Investment" → "Direct Investment"
                    // (see workflow_engine
                    // ._apply_investment_type_geography_gate) render
                    // the plain "DIRECT INVESTMENT" (no TSG- prefix,
                    // proper spacing). Genuine Oman "Local Direct
                    // Investment" deals keep the existing TSG-
                    // prefixed code so the historical formatting is
                    // preserved for that population. Prefer an
                    // explicit BE-emitted classification.benchmark_label
                    // when present.
                    const explicit = getNested(ddSections, 'classification.benchmark_label') as string | undefined;
                    if (explicit) return explicit;
                    const invType = (project.investment_type ?? '').toString().trim().toLowerCase();
                    if (invType === 'direct investment') return 'DIRECT INVESTMENT';
                    return 'TSG-LOCALDIRECTINVESTMENT';
                  })()}
                </div>
              </div>
            </div>
              );
            })()}
          </section>

          {/* §5 — Risk Assessment
                 Sprint 5 client feedback 2026-06-23 (FE Third file
                 Issue 8) — full redesign per the doc:
                   * 5 KPI cards: Total / High / Medium / Low / Overall
                   * 2-col panel row: Risk Summary by Category | Risk
                     Matrix (multi-risk cells with "+N more")
                   * Collapsible Detailed Risks table with search +
                     pagination so 18+ rows are usable.
                 Same data source (dd.risk_profile.rows), same synth
                 rationale below; only the layout changes. */}
          <section className="report-section">
            <SectionHead
              id="s5"
              eyebrow="Section 05 · Risks identified across DD documents"
              title="Risk Assessment"
            />
            <Section5RisksRedesigned dd={ddSections} risk={risk} />
            <Section5RiskAssessmentSynthesis risk={risk} />
          </section>

          {/* §6 — Key Assumptions */}
          <section className="report-section">
            <SectionHead
              id="s6"
              eyebrow="Section 06 · Assumptions extracted from the documents as narrative"
              title="Key Assumption Assessment"
            />
            <Section6KeyAssumptionsCards risk={risk} fallbackDd={ddSections} />
          </section>

          {/* §7 — Calculation Verification */}
          <section className="report-section">
            <SectionHead
              id="s7"
              eyebrow="Section 07 · Validate financial-model outputs"
              title="Calculation Verification"
            />
            <Section7CalcVerificationCompact dd={ddSections} />
          </section>

          {/* §8 — Legal Assessment Summary
                 Matches `New UI Changes/risk-report.html` Section 08:
                   - 6-8 legal area cards (Investor Protection, Indemnification,
                     Anti-Dilution, Board Rights, Exit Rights, Confidentiality,
                     Dispute Resolution, Escrow, etc.) — each with a status badge
                   - Overall Legal Risk Rating: Low / Medium / High
                 Backed by the synthesizer's 5th LLM pass
                 (synthesis.legal_assessment_summary). Falls back to the legacy
                 SpaLegalSummary mandatory-section grid when synthesis isn't
                 available yet (older Risk Assessments).

                 Sprint 5 client feedback 2026-07-02 — eyebrow was hardcoded
                 as "SPA · SHA · Escrow" which was a lie whenever the deal
                 had a different combination of SPA docs uploaded (e.g. EH2
                 uploaded SPA + InvestorsRights + VotingAgreement but no
                 SHA / Escrow). Derive the doc-type list at render time
                 from the source_document fields across the SPA report's
                 attribute_results. Canonical order preserved so runs are
                 stable. Falls back to just "SPA" when the report carries
                 no source_document data. */}
          <section className="report-section">
            <SectionHead
              id="s8"
              eyebrow={`Section 08 · Consolidated review · ${_spaEyebrowDocs(spaSections)}`}
              title="Legal Assessment Summary"
            />
            <Section8LegalAssessment risk={risk} fallbackSpa={spaSections} />
          </section>

          {/* §9 — Critical Legal Findings */}
          <section className="report-section">
            <SectionHead
              id="s9"
              eyebrow="Section 09 · Findings requiring management attention"
              title="Critical Legal Findings"
            />
            <Section9CriticalLegalFindings risk={risk} spa={spaSections} ddReport={ddSections as Record<string, unknown>} />
          </section>

          {/* §10 — Mandatory Clause Compliance
                 Matches `New UI Changes/risk-report.html` Section 10:
                 3-column table (Clause | Present | Assessment). Reads the
                 same clause data the Section 8 LLM pass consumed, so the
                 two views are consistent. */}
          <section className="report-section">
            <SectionHead
              id="s10"
              eyebrow="Section 10 · SPA / SHA mandatory-clause check"
              title="Mandatory Clause Compliance"
            />
            <Section10MandatoryClauseCompliance
              risk={risk}
              fallbackSpa={spaSections}
              /* Sprint 5 client feedback 2026-06-25 (First set.docx
                 issue 5) — pass the DD-resolved jurisdiction so §10
                 stops showing the stale SPA-snapshot "MENA" header. */
              jurisdictionOverride={jurisdictionDisplay}
            />
          </section>

          {/* §11 — Consolidated Findings */}
          <section className="report-section">
            <SectionHead
              id="s11"
              eyebrow="Section 11 · Merged — Financial · Technical · Legal"
              title="Consolidated Findings"
            />
            <ConsolidatedFindingsTable
              risk={risk}
              spaAttributeGroups={spaSections?.attribute_groups as Record<string, unknown> | undefined}
              spaReport={spaSections as Record<string, unknown> | undefined}
              ddReport={ddSections as Record<string, unknown> | undefined}
            />
          </section>

          {/* §12 — Control Strengths */}
          <section className="report-section">
            <SectionHead
              id="s12"
              eyebrow="Section 12 · Positive observations — balanced reporting"
              title="Good Practice Noted"
            />
            <ControlStrengthsList risk={risk} spaAttributeGroups={spaSections?.attribute_groups as Record<string, unknown> | undefined} />
          </section>

          {/* §13 — Source Verification */}
          <section className="report-section">
            <SectionHead
              id="s13"
              eyebrow="Section 13 · Document claims cross-checked against public sources"
              title="Source Verification & Corroboration"
            />
            <ClaimsTable claims={claims} byStatus={claimsByStatus} risk={risk} />
          </section>

          {/* §14 — Market Performance */}
          <section className="report-section">
            <SectionHead
              id="s14"
              eyebrow="Section 14 · Independent sector read & listed-peer benchmark"
              title="Market Performance"
            />
            <MarketPerformance risk={risk} />
          </section>

          {/* §15 — Analysis Summary */}
          <section className="report-section">
            <SectionHead
              id="s15"
              eyebrow="Section 15 · Assurance opinion, recommendations & implications"
              title="Analysis Summary"
            />
            <AnalysisSummary
              risk={risk}
              apiCalls={api_calls.length}
              apiCost={api_total_cost_usd}
              apiCallsList={api_calls}
              ddReport={ddSections}
              spaReport={spaSections}
              projectCreatedAt={project.created_at}
            />
          </section>

          {/* §16 — Evidence & Traceability
                 Matches `New UI Changes/risk-report.html` §16 — per-finding
                 evidence cards with source doc / page / clause / extracted
                 quote / benchmark / rule / confidence. Pulls from
                 consolidated_findings (synthesizer pass) + the raw
                 attribute_results (which carry quoted_text, source_page,
                 benchmark_result, confidence). */}
          <section>
            <SectionHead
              id="s16"
              eyebrow="Section 16 · Full audit trail per finding"
              title="Evidence & Traceability"
            />
            <Section16EvidenceTraceability risk={risk} ddReport={ddSections} spaReport={spaSections} />
          </section>

          {/* §17 — Captured Document Data */}
          <section>
            <SectionHead
              id="s17"
              eyebrow="Section 17 · Internal / management-provided figures — for reference"
              title="Captured Document Data"
            />
            <CapturedData dd={ddSections} />
          </section>
        </div>
      </div>
        </CollapsiblePanel>
      </div>
    </div>
  );
}

/* Collapsible top-level panel for the Risk Report column. Controlled by the
   page so the TOC can force the AI panel open before an anchor jump — the
   body is unmounted (not just hidden) when closed, so a collapsed panel has
   no anchor targets in the DOM. */
function CollapsiblePanel({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const bodyId = `rsk-panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section className={`rsk-panel${open ? ' open' : ''}`}>
      <button
        type="button"
        className="rsk-panel-head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <svg
          className="rsk-panel-chev"
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="rsk-panel-titles">
          <span className="rsk-panel-title">{title}</span>
          <span className="rsk-panel-sub">{subtitle}</span>
        </span>
        {/* Icon, not a word. The chevron carries the same meaning in less space, and
            the whole header is the button — the state is already announced to screen
            readers by aria-expanded, so the icon is decorative. */}
        <span className="rsk-panel-state" aria-hidden="true">
          <svg
            width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="rsk-panel-body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}

/* ── Sub-components: defensive extractors for nested report data ── */

function DdFinancialTable({ dd }: { dd: Record<string, unknown> }): JSX.Element {
  /* Sprint 5 client feedback 2026-06-23 — Phase 2 of Risk Report UI port.
     Wraps the table in .table-wrap (matches the design's bordered
     scroll wrapper) and appends a .ra-note caveat under the table per
     the mockup. The Source column tag now uses .source-tag styling. */
  const rows = (getNested(dd, 'financial_parameters.rows') as unknown[]) ?? [];
  if (rows.length === 0) {
    return <Pending note="No DD financial assessment data — has the DD assessment been run?" />;
  }
  return (
    <>
      {/* Sprint 5 client feedback 2026-06-25 (Issues/§3) — Variance
          column RE-ADDED per analyst request (reversal of the
          2026-06-24 removal). Reads from the BE-computed `variance`
          field which is the % difference between Value and the
          matched benchmark range; renders "In range" / "+N.NN%" /
          "−N.NN%" / "—" depending on placement vs. range. */}
      <div className="table-wrap">
        <table className="data-table" aria-label="Financial parameters">
          {/* Sprint 5 client feedback 2026-07-11 (Iter5 #2, #3, #4)
              — Source column split to combined "Source" cell using
              the same "Investment Memo p.32" format the DD/SPA
              tables use (Iter4 G3 rule extended to Risk). Benchmark
              column now renders "Benchmark Not Applicable" instead
              of "—" when the metric has no configured benchmark.
              Variance column falls back to a closed-form
              computation for rows that carry a numeric value +
              benchmark but the BE did not stamp `variance` (Iter5
              #4 asked Projected IRR to render a variance like
              XIRR). */}
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Benchmark</th>
              <th>Variance</th>
              <th>Source</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const row = r as Record<string, unknown>;
              // BE _enrich_dd_report writes `source` (not source_doc) +
              // `source_page` on each row. Accept either to be defensive
              // against future shape drift.
              const srcDoc =
                row.source ?? row.source_doc ?? row.source_document ?? null;
              const srcPage = row.source_page ?? null;
              const variance = row.variance;
              // Iter5 #4 — closed-form variance fallback: parse
              // numeric value + numeric benchmark and compute
              // (value - benchmark) / benchmark * 100. Only fires
              // when the BE variance is empty AND both parts parse.
              // Extracts the first number in each string so
              // "29.0%" and "Below 15% target" both work.
              const _parseNum = (s: unknown): number | null => {
                if (s == null) return null;
                const m = String(s).match(/-?\d+(?:\.\d+)?/);
                return m ? parseFloat(m[0]) : null;
              };
              const _computedVar = (() => {
                if (variance != null && String(variance).trim() !== '' && String(variance).trim() !== '—') {
                  return String(variance);
                }
                const v = _parseNum(row.value);
                const b = _parseNum(row.benchmark);
                if (v == null || b == null || b === 0) return '—';
                const pct = ((v - b) / Math.abs(b)) * 100;
                return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
              })();
              const _benchmarkDisplay = row.benchmark != null && String(row.benchmark).trim() !== ''
                ? String(row.benchmark)
                : 'Benchmark Not Applicable';
              return (
                <tr key={i}>
                  <td className="td-name">{String(row.metric ?? '—')}</td>
                  <td className="t-mono t-xs">{String(row.value ?? '—')}</td>
                  <td className="t-xs">{_benchmarkDisplay}</td>
                  <td className="t-mono t-xs">{_computedVar}</td>
                  <td className="t-xs">
                    {/* Iter6 #1 — canonical SourceChip. */}
                    <SourceChip
                      doc={srcDoc != null ? String(srcDoc) : null}
                      page={srcPage != null ? String(srcPage) : null}
                    />
                  </td>
                  <td>
                    {/* Sprint 5 client feedback 2026-07-11 (Iter6c #1)
                        — rows without a configured benchmark render a
                        neutral "Benchmark N/A" chip. */}
                    {/* Sprint 5 client feedback 2026-07-11 — client
                        applied Iter1 §4 highest-only rule to DD §4 and
                        wants the same on Risk §3: when the row matches
                        multiple bands (e.g. Holding Period 5 years hits
                        Core/Core-Plus, Value Add, AND Opportunistic),
                        show ONLY the highest tier. Ranking:
                            Opportunistic > Value Add > Core-Plus > Core
                        Analysts read the highest tier as the operative
                        classification; the lower tiers add visual noise
                        and confuse the "which bucket does this fall in"
                        read. Same helper as DD §4 (colocated for
                        symmetry — one place to bump when a new tier
                        joins the vocabulary). */}
                    {(() => {
                      const _tierRank: Record<string, number> = {
                        opportunistic: 4,
                        'value add': 3,
                        'value-add': 3,
                        'core-plus': 2,
                        'core / core-plus': 2,
                        core: 1,
                        aggressive: 4,
                        conservative: 1,
                        regular: 2,
                        verified: 3,
                      };
                      const _rank = (v: string): number =>
                        _tierRank[v.trim().toLowerCase()] ?? 0;
                      const _pickHighest = (labels: string[]): string | null => {
                        const cleaned = labels
                          .map((s) => s.trim())
                          .filter((s) => s && s.toLowerCase() !== 'review required' && s !== '—');
                        if (cleaned.length === 0) return null;
                        return cleaned.reduce((a, b) =>
                          _rank(a) >= _rank(b) ? a : b,
                        );
                      };
                      let winner: string | null = null;
                      if (Array.isArray(row.classification) && row.classification.length > 0) {
                        winner = _pickHighest(
                          (row.classification as unknown[]).map((c) => String(c)),
                        );
                      } else {
                        const single = row.classification;
                        if (typeof single === 'string' && single.trim()) {
                          // Legacy comma-joined "Core / Core-Plus, Value Add, Opportunistic"
                          const parts = single.includes(',')
                            ? single.split(',')
                            : [single];
                          winner = _pickHighest(parts);
                        }
                      }
                      if (winner) {
                        return (
                          <Badge variant={statusVariant(winner)}>{winner}</Badge>
                        );
                      }
                      return <Badge variant="neutral">Benchmark N/A</Badge>;
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ra-note">
        Variance shown as % difference vs. the matched benchmark range.
        Values, pages and rules originate from the Financial DD extraction
        (see Section 16 for full traceability).
      </p>
    </>
  );
}

/* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6):
   Likelihood × Impact heatmap matching the design at
   risk-report.html#s5. Counts each DD risk-profile row into one of
   16 cells (4 likelihood × 4 impact); each cell shows the composite
   severity label (L/M/H/C) and the count of rows in that cell. The
   shading uses --pass-bg / --watch-bg / --fail-bg matching the
   reference template. */
const _LIKELIHOOD_KEYS = ['Likely', 'Possible', 'Unlikely', 'Rare'];
const _IMPACT_KEYS = ['Minor', 'Moderate', 'Major', 'Severe'];

/**
 * Sprint 5 client feedback 2026-07-01 — FIX 1 of 2 for the DD §5
 * Risk Matrix. Previous code computed `composite = (3 - li) + (3 - ii)`
 * which inverted the IMPACT axis: cells at (Likely × Minor) coloured
 * Critical, (Likely × Severe) coloured Medium, (Rare × Minor)
 * coloured Medium, etc. — exactly backwards from every real risk-
 * matrix convention (COSO, ISO 31000, PMBOK). Replaced with a
 * hardcoded 4×4 lookup that matches the standard bottom-left →
 * top-right severity progression the user specified verbatim.
 * Severity legend: Critical → red, High → orange, Medium → yellow,
 * Low → green.
 */
const _RISK_MATRIX_4x4: Record<string, Record<string, 'Critical' | 'High' | 'Medium' | 'Low'>> = {
  'Likely':   { 'Minor': 'Medium', 'Moderate': 'High',   'Major': 'Critical', 'Severe': 'Critical' },
  'Possible': { 'Minor': 'Low',    'Moderate': 'Medium', 'Major': 'High',     'Severe': 'Critical' },
  'Unlikely': { 'Minor': 'Low',    'Moderate': 'Low',    'Major': 'Medium',   'Severe': 'High'     },
  'Rare':     { 'Minor': 'Low',    'Moderate': 'Low',    'Major': 'Low',      'Severe': 'Medium'   },
};

const _SEVERITY_STYLE: Record<'Critical' | 'High' | 'Medium' | 'Low', { label: string; bg: string; fg: string }> = {
  'Critical': { label: 'C', bg: 'var(--fail-bg)',            fg: 'var(--fail)'   },
  'High':     { label: 'H', bg: 'rgba(232,96,44,0.12)',      fg: 'var(--accent)' },
  'Medium':   { label: 'M', bg: 'var(--watch-bg)',           fg: 'var(--watch)'  },
  'Low':      { label: 'L', bg: 'var(--pass-bg)',            fg: 'var(--pass)'   },
};

function _heatmapSeverityClass(likelihood: string, impact: string): { label: string; bg: string; fg: string } {
  const sev = _RISK_MATRIX_4x4[likelihood]?.[impact];
  if (!sev) return { label: '—', bg: 'var(--surface-alt)', fg: 'var(--muted)' };
  return _SEVERITY_STYLE[sev];
}

/* Sprint 5 client feedback 2026-06-23 (FE Third file, Issue 8) —
   redesigned §5 Risks layout. Renders:
     ROW 1 — 5 KPI tiles: Total / High / Medium / Low / Overall
     ROW 2 — 2-col panel grid:
              LEFT  Risk Summary by Category (aggregated counts)
              RIGHT Risk Matrix (multi-risk cells with "+N more")
     ROW 3 — collapsible Detailed Risks table with search + pagination
   Frontend-only. Data source is unchanged: dd.risk_profile.rows. */
type _RiskRow = Record<string, unknown>;

function _classifySeverity(likelihood: string, impact: string): 'Critical' | 'High' | 'Medium' | 'Low' | 'Unknown' {
  // Sprint 5 client feedback 2026-07-01 — same 4×4 lookup table as
  // _heatmapSeverityClass so the KPI strip counts (Critical / High /
  // Medium / Low) can never drift from the heat-map cell colours.
  // Any risk that lands in a "Critical" cell on the matrix is
  // counted in the "Critical" KPI tile by construction.
  const sev = _RISK_MATRIX_4x4[likelihood]?.[impact];
  return sev ?? 'Unknown';
}

/* Sprint 5 client feedback 2026-06-23 — §5 risk math fix.
   Many DD risk rows arrive with normalisable likelihood / impact;
   the rest have free-form or empty values that the L×I matrix
   can't classify. Previously those rows fell into an "Unknown"
   bucket — counted in the row Total but excluded from H/M/L
   columns, so Total ≠ H+M+L on every category row and on the
   KPI strip (user report: Total=20, H=7 M=6 L=0 → 13 ≠ 20).
   This fallback ensures EVERY row resolves to one of
   Critical / High / Medium / Low so the math is additive
   everywhere:
     1. Try likelihood × impact (the matrix classification);
     2. If Unknown, fall back to the row's flag / status text
        (Critical / High → High;  Watch / Medium / Review → Medium;
         Pass / Low → Low);
     3. Final fallback: Medium (the most neutral assumption — the
        risk exists, but neither L×I nor flag tells us the band). */
function _classifyRow(row: _RiskRow): 'Critical' | 'High' | 'Medium' | 'Low' {
  const sev = _classifySeverity(_normLikelihood(row.likelihood), _normImpact(row.impact));
  if (sev !== 'Unknown') return sev;
  const flag = String(row.flag ?? row.status ?? row.severity ?? '').trim().toLowerCase();
  if (['critical', 'crit', 'severe'].includes(flag)) return 'Critical';
  if (['high', 'escalate', 'fail', 'blocker', 'red'].includes(flag)) return 'High';
  if (['watch', 'medium', 'review', 'caution', 'amber', 'yellow'].includes(flag)) return 'Medium';
  if (['low', 'pass', 'accepted', 'green', 'ok'].includes(flag)) return 'Low';
  return 'Medium';
}

function _normLikelihood(raw: unknown): string {
  const v = String(raw ?? '').trim().toLowerCase();
  if (['likely', 'high', 'probable', 'almost certain'].includes(v)) return 'Likely';
  if (['possible', 'medium', 'moderate'].includes(v)) return 'Possible';
  if (['unlikely', 'low'].includes(v)) return 'Unlikely';
  if (['rare', 'remote', 'very low', 'insignificant'].includes(v)) return 'Rare';
  return '';
}

function _normImpact(raw: unknown): string {
  const v = String(raw ?? '').trim().toLowerCase();
  if (['severe', 'critical', 'catastrophic'].includes(v)) return 'Severe';
  if (['major', 'high'].includes(v)) return 'Major';
  if (['moderate', 'medium'].includes(v)) return 'Moderate';
  if (['minor', 'low', 'insignificant', 'minimal'].includes(v)) return 'Minor';
  return '';
}

function _riskName(row: _RiskRow): string {
  return String(
    row.risk_name ??
    row.name ??
    row.title ??
    row.risk ??
    row.description ??
    row.risk_category ??
    row.category ??
    '—',
  );
}

function Section5RisksRedesigned({
  dd,
  risk,
}: {
  dd: Record<string, unknown>;
  risk: Record<string, unknown>;
}): JSX.Element {
  const rows = (getNested(dd, 'risk_profile.rows') as _RiskRow[]) ?? [];

  // Overall rating — same 5-step fallback chain DdRiskTable used.
  const synthOverall = getNested(
    risk,
    'enrichment.synthesis.risk_assessment_overall.rating',
  ) as string | undefined;
  const overallRating =
    (getNested(dd, 'risk_profile.summary.overall_rating') as string | undefined) ??
    (getNested(dd, 'risk_profile.overall_rating') as string | undefined) ??
    (synthOverall && synthOverall.trim() ? synthOverall : undefined) ??
    (risk.overall_risk_rating ? String(risk.overall_risk_rating) : undefined) ??
    (risk.overall_risk_rating_baseline ? String(risk.overall_risk_rating_baseline) : undefined) ??
    '—';

  // Sprint 5 client feedback 2026-06-23 — every row now routes through
  // _classifyRow so Critical/High/Medium/Low are exhaustive and
  // Total === High + Medium + Low across both the KPI strip and the
  // per-category table. Critical still collapses into High for the
  // 3-tier KPI display per the original doc spec.
  const severities = rows.map((r) => _classifyRow(r));
  // Sprint 5 client feedback 2026-06-24 (DD/Risk.docx issue 5) — §5
  // KPI strip was collapsing Critical into High, so the "Critical
  // risk count" the user asked for never surfaced as its own tile.
  // Break it out: Critical is now its own KPI; the High tile shows
  // strict-High rows only.
  const critN = severities.filter((s) => s === 'Critical').length;
  const highN = severities.filter((s) => s === 'High').length;
  const medN = severities.filter((s) => s === 'Medium').length;
  const lowN = severities.filter((s) => s === 'Low').length;
  const totalN = critN + highN + medN + lowN;

  // Aggregate by category for the LEFT panel. Row total = sum of
  // its C + H + M + L counts.
  //
  // Sprint 5 client feedback 2026-06-24 (FIRST SET #5): broke
  // Critical out into its own column. Previously Critical rows
  // collapsed into "High", which produced the user-visible mismatch
  // — the §5 Requires-Attention card called a row "Critical" while
  // the same row showed as "High" in the per-category table. Both
  // surfaces now agree on the four-tier scale; the matrix already
  // distinguishes Critical from High via composite-score so this
  // change just exposes the same bucket in the table view.
  const byCategory = new Map<string, { crit: number; high: number; med: number; low: number; total: number }>();
  rows.forEach((r, i) => {
    const cat = String(r.risk_category ?? r.category ?? '—');
    const sev = severities[i];
    const entry = byCategory.get(cat) ?? { crit: 0, high: 0, med: 0, low: 0, total: 0 };
    if (sev === 'Critical') {
      entry.crit += 1;
    } else if (sev === 'High') {
      entry.high += 1;
    } else if (sev === 'Medium') {
      entry.med += 1;
    } else {
      // _classifyRow can only return Critical/High/Medium/Low, so
      // anything that's not Critical/High/Medium here is Low.
      entry.low += 1;
    }
    entry.total = entry.crit + entry.high + entry.med + entry.low;
    byCategory.set(cat, entry);
  });
  const categoryRows = Array.from(byCategory.entries())
    .map(([cat, c]) => ({ category: cat, ...c }))
    .sort((a, b) => b.total - a.total);

  // Sprint 5 client feedback 2026-06-23 (FE Four Issue 5): Risk
  // Summary by Category truncates with a "+N More" toggle.
  // When ≤ TOP_N categories, the toggle is hidden.
  //
  // Sprint 5 client feedback 2026-06-24 (FE Six issue 1) — TOP_N
  // tightened from 10 to 6 so the panel reads as a compact summary
  // (analysts asked for less vertical real estate on long
  // category lists; expandable footer still gives full visibility).
  const CATEGORY_TOP_N = 6;
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const visibleCategoryRows = categoriesExpanded
    ? categoryRows
    : categoryRows.slice(0, CATEGORY_TOP_N);
  const hiddenCategoryCount = Math.max(0, categoryRows.length - CATEGORY_TOP_N);

  // Matrix multi-risk: cell → [{ref: 'R3', name: '...'}].
  // Sprint 5 client feedback 2026-06-23 (heat-map v2) — cells must
  // be compact again. Store only a short reference ID (R<n+1>, where
  // n is the row's position in the source list) so each cell can
  // render "R1, R2, +N more" instead of inlining full risk text.
  // The full name still rides along for the title tooltip.
  const cellRisks = new Map<string, { ref: string; name: string }[]>();
  // Sprint 5 client feedback 2026-07-07 (BE Issue #7) — collect
  // risks that CAN'T be plotted (Likelihood or Impact unmapped, i.e.
  // "—" / null / freeform outside the canonical taxonomy). Prior
  // renderer silently dropped these from the matrix; the KPI strip
  // still counted them via _classifyRow's flag/severity fallback,
  // so the two panels disagreed on totals. Now we surface each one
  // in an explicit "Unassessed" panel below the matrix.
  const unassessedRisks: Array<{
    ref: string;
    name: string;
    severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Recommendation';
    missingLikelihood: boolean;
    missingImpact: boolean;
  }> = [];
  rows.forEach((r, idx) => {
    const l = _normLikelihood(r.likelihood);
    const i = _normImpact(r.impact);
    if (!l || !i) {
      unassessedRisks.push({
        ref: `R${idx + 1}`,
        name: _riskName(r),
        severity: _classifyRow(r),
        missingLikelihood: !l,
        missingImpact: !i,
      });
      return;
    }
    const key = `${l}|${i}`;
    const arr = cellRisks.get(key) ?? [];
    arr.push({ ref: `R${idx + 1}`, name: _riskName(r) });
    cellRisks.set(key, arr);
  });
  const plottedN = rows.length - unassessedRisks.length;

  // Sprint 5 client feedback 2026-07-07 (BE Issue #7) — render-time
  // assertion. Prior versions silently drifted between the KPI strip
  // (which counted ALL rows via _classifyRow's fallback chain) and
  // the category rollup (same source). This guard fires a dev-only
  // console warning if the two ever diverge — useful signal if
  // future refactors introduce an accidental filter somewhere.
  const kpiTotal = critN + highN + medN + lowN;
  const catRollupTotal = categoryRows.reduce((s, c) => s + c.total, 0);
  if (
    typeof console !== 'undefined' && console.warn &&
    (kpiTotal !== rows.length || catRollupTotal !== rows.length)
  ) {
    console.warn(
      '[Risk §5] KPI/category totals do not match rows.length',
      { kpiTotal, catRollupTotal, rowsLength: rows.length },
    );
  }
  // KPI vs (plotted + unassessed) reconciliation per ticket Test 1.
  if (
    typeof console !== 'undefined' && console.warn &&
    kpiTotal !== plottedN + unassessedRisks.length
  ) {
    console.warn(
      '[Risk §5] KPI total ≠ plotted + unassessed — bucketing drift',
      { kpiTotal, plottedN, unassessedN: unassessedRisks.length },
    );
  }

  // Detailed table state.
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      _riskName(r).toLowerCase().includes(q) ||
      String(r.risk_category ?? '').toLowerCase().includes(q) ||
      String(r.likelihood ?? '').toLowerCase().includes(q) ||
      String(r.impact ?? '').toLowerCase().includes(q) ||
      String(r.flag ?? '').toLowerCase().includes(q)
    );
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (rows.length === 0) {
    return <Pending note="No DD risk-profile data extracted." />;
  }

  const overallVariant = (() => {
    const r = overallRating.toLowerCase();
    if (r.includes('high') || r.includes('crit')) return 'fail';
    if (r.includes('med') || r.includes('mod')) return 'watch';
    if (r.includes('low')) return 'pass';
    return 'neutral';
  })();

  return (
    <>
      {/* ROW 1 — 6 KPI tiles (Critical broken out per 2026-06-24 issue 5) */}
      <div className="rsk-kpi-strip">
        <div className="rsk-kpi">
          <div className="rsk-kpi-label">Total Risks</div>
          <div className="rsk-kpi-value">{totalN}</div>
          <div className="rsk-kpi-sub">identified across DD</div>
        </div>
        <div className="rsk-kpi rsk-kpi-crit">
          <div className="rsk-kpi-label">Critical Risks</div>
          <div className="rsk-kpi-value">{critN}</div>
          <div className="rsk-kpi-sub">immediate action</div>
        </div>
        <div className="rsk-kpi rsk-kpi-high">
          <div className="rsk-kpi-label">High Risks</div>
          <div className="rsk-kpi-value">{highN}</div>
          <div className="rsk-kpi-sub">requires escalation</div>
        </div>
        <div className="rsk-kpi rsk-kpi-med">
          <div className="rsk-kpi-label">Medium Risks</div>
          <div className="rsk-kpi-value">{medN}</div>
          <div className="rsk-kpi-sub">review &amp; monitor</div>
        </div>
        <div className="rsk-kpi rsk-kpi-low">
          <div className="rsk-kpi-label">Low Risks</div>
          <div className="rsk-kpi-value">{lowN}</div>
          <div className="rsk-kpi-sub">accepted</div>
        </div>
        {/* Sprint 5 client feedback 2026-07-11 (Iter5 #5, #6) —
            Overall Investment Risk tile is now clickable: clicking
            scrolls to the "Why the rating is X" rationale block
            further down §5. Also carries a tooltip so hovering
            surfaces the rationale prose inline. The "composite
            verdict" subtitle removed — the rating alone is
            enough. */}
        <a
          href="#risk-overall-rationale"
          className={`rsk-kpi rsk-kpi-overall rsk-kpi-${overallVariant}`}
          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
          data-tip="Click to jump to the 'Why the rating is X' rationale below"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById('risk-overall-rationale');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          <div className="rsk-kpi-label">Overall Investment Risk</div>
          <div className="rsk-kpi-value">{overallRating}</div>
        </a>
      </div>

      {/* ROW 2 — 2-col panel grid */}
      <div className="rsk-twocol">
        <div className="rsk-panel">
          <div className="rsk-panel-h">
            Risk Summary by Category{hiddenCategoryCount > 0 ? ` (Top ${CATEGORY_TOP_N})` : ''}
          </div>
          <div className="table-wrap">
            <table className="data-table rsk-cat-table" aria-label="Risk count by category">
              <thead>
                <tr>
                  <th>Risk Category</th>
                  <th className="num">Critical</th>
                  <th className="num">High</th>
                  <th className="num">Medium</th>
                  <th className="num">Low</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategoryRows.map((c) => (
                  <tr key={c.category}>
                    <td className="td-name">{c.category}</td>
                    <td className="num"><span className="rsk-count rsk-count-crit">{c.crit}</span></td>
                    <td className="num"><span className="rsk-count rsk-count-high">{c.high}</span></td>
                    <td className="num"><span className="rsk-count rsk-count-med">{c.med}</span></td>
                    <td className="num"><span className="rsk-count rsk-count-low">{c.low}</span></td>
                    <td className="num rsk-count-total">{c.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenCategoryCount > 0 && (
            <div className="rsk-cat-footer">
              <button
                type="button"
                className="rsk-cat-toggle"
                aria-expanded={categoriesExpanded}
                onClick={() => setCategoriesExpanded((v) => !v)}
              >
                {categoriesExpanded
                  ? 'Show Less'
                  : `+${hiddenCategoryCount} More ${hiddenCategoryCount === 1 ? 'Category' : 'Categories'}`}
                <span className="chev" aria-hidden="true">▼</span>
              </button>
            </div>
          )}
        </div>
        <div className="rsk-panel">
          <div className="rsk-panel-h">Risk Matrix (Likelihood × Impact)</div>
          {/* Sprint 5 client feedback 2026-07-07 (BE Issue #7) —
              matrix reports plotted vs total ratio so the reviewer
              can immediately see how many rows are missing from the
              heatmap. The Unassessed panel below lists the
              unplotted risks with per-severity split so nothing is
              silently dropped. */}
          <RiskMatrixHeatmapMulti
            cellRisks={cellRisks}
            totalRows={rows.length}
            plottedN={plottedN}
            unassessedN={unassessedRisks.length}
          />
          {unassessedRisks.length > 0 && (
            <UnassessedRisksPanel risks={unassessedRisks} />
          )}
        </div>
      </div>

      {/* ROW 3 — collapsible detailed table */}
      <div className="rsk-detail">
        <button
          type="button"
          className="rsk-detail-toggle"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
        >
          <span className={`rsk-chev${expanded ? ' open' : ''}`} aria-hidden="true">▾</span>
          <span>Detailed Risks ({totalN})</span>
        </button>
        {expanded && (
          <div className="rsk-detail-body">
            <div className="rsk-detail-controls">
              <div className="filter-search">
                <svg className="filter-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="search"
                  className="filter-search-input"
                  placeholder="Search risks by name, category, severity…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                />
              </div>
              <span className="rsk-detail-count">{filtered.length} of {totalN}</span>
            </div>
            <div className="table-wrap">
              <table className="data-table rsk-detail-table" aria-label="Detailed risk rows">
                <thead>
                  <tr>
                    <th>Risk Name</th>
                    <th>Risk Category</th>
                    <th>Likelihood</th>
                    <th>Impact</th>
                    <th>Status</th>
                    <th>Matrix</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => {
                    const lik = _normLikelihood(r.likelihood);
                    const imp = _normImpact(r.impact);
                    // Route through _classifyRow so the Matrix pill in
                    // the detail table matches the KPI strip + category
                    // counts above (flag fallback + Medium default).
                    const sev = _classifyRow(r);
                    return (
                      <tr key={page * PAGE_SIZE + i}>
                        <td className="td-name">{_riskName(r)}</td>
                        <td>{String(r.risk_category ?? r.category ?? '—')}</td>
                        <td>{lik || String(r.likelihood ?? '—')}</td>
                        <td>{imp || String(r.impact ?? '—')}</td>
                        <td><Badge variant={statusVariant(String(r.flag ?? ''))}>{String(r.flag ?? 'Unknown')}</Badge></td>
                        <td>
                          <span className={`rsk-sev-pill rsk-sev-${sev.toLowerCase()}`}>{sev}</span>
                        </td>
                        {/* Sprint 5 client feedback 2026-07-11 — Source
                            column was rendering just the doc name (e.g.
                            "Investment Memo"). Route through canonical
                            SourceChip so it reads "InvMemo p.31" like
                            every other Risk / DD / SPA source cell. */}
                        <td className="t-muted">
                          <SourceChip
                            doc={String(r.source_document ?? r.source ?? '') || null}
                            page={(r.source_page ?? r.page) as number | string | null | undefined}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="rsk-pager">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Prev
                </button>
                <span className="rsk-pager-info">Page {page + 1} of {pageCount}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/* Compact heat-map — same 4×4 grid as the original. Cells stay
   FIXED size (aspect-ratio 1.9/1, the original spec) and contain
   ONLY:
     - severity letter (L/M/H/C)
     - count badge (×N)
     - up to 2 reference IDs (R3, R7) with "+N more" overflow
   Full risk text is reserved for the cell tooltip and the detailed
   table below. This matches the user's "compact executive-style
   risk heat-map" brief (FE Third Issue 8 follow-up). */
function RiskMatrixHeatmapMulti({
  cellRisks,
  totalRows,
  plottedN,
  unassessedN,
}: {
  cellRisks: Map<string, { ref: string; name: string }[]>;
  totalRows: number;
  /** Sprint 5 client feedback 2026-07-07 (BE Issue #7) — count of
   *  risks actually plotted on the heatmap (both axes mapped). */
  plottedN?: number;
  /** Count of risks NOT plotted (Likelihood or Impact unmapped). */
  unassessedN?: number;
}): JSX.Element {
  const cellClass = (label: string): string => {
    if (label === 'C') return 'hm-cell hm-crit';
    if (label === 'H') return 'hm-cell hm-high';
    if (label === 'M') return 'hm-cell hm-med';
    return 'hm-cell hm-low';
  };
  // Sprint 5 client feedback 2026-07-07 (BE Issue #7) — truthful
  // matrix header. Old text said "21 risks from DD" on a matrix
  // that only plotted 15. New text is explicit about the split.
  const noteText = plottedN != null && unassessedN != null && unassessedN > 0
    ? `Likelihood × Impact — ${plottedN} of ${totalRows} plotted (${unassessedN} unassessed)`
    : `Likelihood × Impact (${totalRows} risk${totalRows === 1 ? '' : 's'} from DD)`;
  return (
    <div>
      <div className="ra-matrix-note">
        {noteText}
      </div>
      <div className="ra-heatmap rsk-heatmap-compact">
        <div />
        {_IMPACT_KEYS.map((imp) => (
          <div key={imp} className="ra-axis">{imp}</div>
        ))}
        {_LIKELIHOOD_KEYS.map((lik) => (
          <React.Fragment key={lik}>
            <div className="ra-axis">{lik}</div>
            {_IMPACT_KEYS.map((imp) => {
              const sev = _heatmapSeverityClass(lik, imp);
              const items = cellRisks.get(`${lik}|${imp}`) ?? [];
              const previewItems = items.slice(0, 2);
              const overflow = items.length - previewItems.length;
              // Tooltip lists ALL risks in this cell, "R3: <name>"
              // one per line, so hover surfaces the detail the cell
              // intentionally omits.
              const tooltip = items.length
                ? items.map((it) => `${it.ref}: ${it.name}`).join('\n')
                : undefined;
              return (
                <div key={imp} className={cellClass(sev.label)} data-tip={tooltip}>
                  {/* Sprint 5 client feedback 2026-06-24 (AUDIT-2026-0054
                      S-17): empty cells used to render the severity
                      letter (M/L/H/C) anyway, which read like phantom
                      risks. Hide the letter when items.length === 0
                      and show a muted em-dash so the cell still has
                      a visual anchor without looking like a populated
                      tier. */}
                  {items.length > 0 && <span className="hm-badge">×{items.length}</span>}
                  <div className="hm-cell-body">
                    {items.length > 0 ? (
                      <span className="hm-cell-label">{sev.label}</span>
                    ) : (
                      <span className="hm-cell-label hm-cell-empty">—</span>
                    )}
                    {previewItems.length > 0 && (
                      <span className="hm-cell-refs">
                        {previewItems.map((it) => it.ref).join(', ')}
                        {overflow > 0 && <span className="hm-cell-more"> +{overflow} more</span>}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #7) — Unassessed Risks
 * panel. Renders below the Risk Matrix when one or more risks have
 * unmapped Likelihood or Impact. Prior renderer silently dropped these
 * from the matrix while still counting them in the KPI strip, so the
 * two panels disagreed (KPI total 21, matrix plotted 15).
 *
 * Layout:
 *   - Header: "Unassessed Likelihood/Impact ({count})"
 *   - Per-severity split chips (Critical / High / Medium / Low)
 *   - List of risk refs with tooltips showing full risk name +
 *     which axis is missing
 */
function UnassessedRisksPanel({
  risks,
}: {
  risks: Array<{
    ref: string;
    name: string;
    severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Recommendation';
    missingLikelihood: boolean;
    missingImpact: boolean;
  }>;
}): JSX.Element {
  const critN = risks.filter((r) => r.severity === 'Critical').length;
  const highN = risks.filter((r) => r.severity === 'High').length;
  const medN  = risks.filter((r) => r.severity === 'Medium').length;
  const lowN  = risks.filter((r) => r.severity === 'Low').length;
  // What's missing across the set — used for the header wording.
  const anyMissingLikelihood = risks.some((r) => r.missingLikelihood);
  const anyMissingImpact = risks.some((r) => r.missingImpact);
  const missingAxisLabel = anyMissingLikelihood && anyMissingImpact
    ? 'Likelihood / Impact'
    : anyMissingLikelihood ? 'Likelihood' : 'Impact';

  return (
    <div
      className="ra-unassessed-panel"
      style={{
        marginTop: 'var(--s3)',
        padding: 'var(--s3) var(--s4)',
        background: 'var(--surface-alt)',
        border: '1px dashed var(--lines)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--s2)',
          marginBottom: 'var(--s2)',
        }}
      >
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text)' }}>
          Unassessed {missingAxisLabel} ({risks.length})
        </div>
        <div style={{ display: 'inline-flex', gap: 'var(--s2)', flexWrap: 'wrap' }}>
          {critN > 0 && <span className="rsk-sev-pill rsk-sev-critical">C {critN}</span>}
          {highN > 0 && <span className="rsk-sev-pill rsk-sev-high">H {highN}</span>}
          {medN > 0  && <span className="rsk-sev-pill rsk-sev-medium">M {medN}</span>}
          {lowN > 0  && <span className="rsk-sev-pill rsk-sev-low">L {lowN}</span>}
        </div>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 'var(--s2)' }}>
        These risks are counted in the KPI strip but cannot be plotted on the
        matrix because their {missingAxisLabel} was not extracted. The
        detailed risks table below lists them individually.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {risks.map((r) => {
          const missingLabel = r.missingLikelihood && r.missingImpact
            ? 'Likelihood + Impact missing'
            : r.missingLikelihood ? 'Likelihood missing' : 'Impact missing';
          return (
            <span
              key={r.ref}
              className={`rsk-sev-pill rsk-sev-${r.severity.toLowerCase()}`}
              data-tip={`${r.ref} · ${r.name}\n${missingLabel}`}
              style={{ fontFamily: 'var(--font-mono)', cursor: 'help' }}
            >
              {r.ref}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-23 (FE Third file Issue 8) — the
   previous DdRiskTable + RiskMatrixHeatmap pair has been replaced by
   Section5RisksRedesigned above. Both helpers are removed; their data
   chain (5-step overall rating, severity normalisation, cell counts)
   was inlined into the redesigned component so behaviour is preserved. */

function DdAssumptions({ dd }: { dd: Record<string, unknown> }): JSX.Element {
  const items = (getNested(dd, 'key_assumptions.items') as unknown[]) ?? [];
  if (items.length === 0) {
    return <Pending note="No captured assumptions." />;
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--s3)' }}>
      {items.slice(0, 10).map((a, i) => {
        const ax = a as Record<string, unknown>;
        return (
          <div
            key={i}
            style={{
              padding: 'var(--s3) var(--s4)',
              background: 'var(--surface)',
              border: '1px solid var(--lines)',
              borderRadius: 'var(--r-sm)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {String(ax.assumption ?? ax.text ?? '—')}
              </span>
              <Badge variant={statusVariant(String(ax.status ?? ''))}>
                {String(ax.status ?? 'Unknown')}
              </Badge>
            </div>
            {Boolean(ax.source_doc) && (
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                Source: {String(ax.source_doc)}{ax.source_page ? ` · p.${String(ax.source_page)}` : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Unused after Section 7 redesign — kept for fallback reference
function DdCalcTable({ dd }: { dd: Record<string, unknown> }): JSX.Element {
  const rows = (getNested(dd, 'calculations.rows') as unknown[]) ?? [];
  if (rows.length === 0) {
    return <Pending note="No calculation verifications recorded." />;
  }
  return (
    <table className="data-table" aria-label="Calculation verification">
      <thead>
        <tr>
          <th>Calculation</th>
          <th>Stated</th>
          <th>Computed</th>
          <th>Variance %</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 20).map((r, i) => {
          const row = r as Record<string, unknown>;
          return (
            <tr key={i}>
              <td>{String(row.calculation ?? row.metric ?? '—')}</td>
              <td className="t-mono">{String(row.stated ?? '—')}</td>
              <td className="t-mono">{String(row.computed ?? '—')}</td>
              <td className="t-mono">{String(row.variance_pct ?? '—')}</td>
              <td>
                <Badge variant={statusVariant(String(row.status ?? ''))}>
                  {String(row.status ?? '—')}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SpaLegalSummary({ spa }: { spa: Record<string, unknown> }): JSX.Element {
  const sections =
    (getNested(spa, 'sections_detected') as unknown[]) ??
    (getNested(spa, 'section_detections') as unknown[]) ??
    [];
  if (sections.length === 0) {
    return <Pending note="No SPA legal sections detected — has the SPA assessment been run?" />;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 'var(--s3)',
      }}
    >
      {sections.slice(0, 12).map((s, i) => {
        const sec = s as Record<string, unknown>;
        return (
          <div
            key={i}
            style={{
              padding: 'var(--s3) var(--s4)',
              border: '1px solid var(--lines)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--surface)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                {String(sec.label ?? sec.section ?? sec.name ?? '—')}
              </span>
              <Badge variant={statusVariant(String(sec.status ?? sec.present ?? ''))}>
                {String(sec.status ?? (sec.present ? 'Present' : 'Missing'))}
              </Badge>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SpaFlagsTable({ spa }: { spa: Record<string, unknown> }): JSX.Element {
  const flags = (getNested(spa, 'flags') as unknown[]) ?? [];
  if (flags.length === 0) {
    return <Pending note="No SPA legal flags recorded." />;
  }
  return (
    <table className="data-table" aria-label="SPA flags">
      <thead>
        <tr>
          <th>ID</th>
          <th>Description</th>
          <th>Severity</th>
        </tr>
      </thead>
      <tbody>
        {flags.slice(0, 20).map((f, i) => {
          const fl = f as Record<string, unknown>;
          return (
            <tr key={i}>
              <td className="t-mono">{String(fl.id ?? fl.flag_id ?? i + 1)}</td>
              <td>{String(fl.description ?? fl.title ?? fl.text ?? '—')}</td>
              <td>
                <Badge variant={statusVariant(String(fl.severity ?? ''))}>
                  {String(fl.severity ?? '—')}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SpaClauseTable({
  spa,
  jurisdictionOverride,
}: {
  spa: Record<string, unknown>;
  /* Sprint 5 client feedback 2026-06-25 (First set.docx issue 5) —
     when the SPA workflow ran before DD finished, the SPA-cached
     `jurisdiction_meta.jurisdiction` records the stale intake value
     ("MENA"). After DD overlays the project.jurisdiction with the
     extracted geography ("North America"), the §10 header still shows
     MENA. Parent passes the DD-resolved jurisdiction down so the
     header always reflects current truth, not the SPA snapshot. */
  jurisdictionOverride?: string;
}): JSX.Element {
  /* Sprint 5 — split mandatory-section detection into two sub-tables.
     The SPA report now stores the detection under
     `mandatory_section_detection` with sub-keys:
       - universal[]    → static 10 SPA sections
       - jurisdiction[] → LLM-derived jurisdiction-specific clauses
       - jurisdiction_meta → {jurisdiction, investment_type, degraded, ...}
     Older payloads (pre-Sprint-5) still have flat `mandatory_clauses` /
     `clauses` keys; we fall back to those when the new split isn't there. */
  const universal =
    (getNested(spa, 'mandatory_section_detection.universal') as unknown[]) ?? [];
  const jurisdiction =
    (getNested(spa, 'mandatory_section_detection.jurisdiction') as unknown[]) ?? [];
  const rawJurisdictionMeta =
    (getNested(spa, 'mandatory_section_detection.jurisdiction_meta') as
      | Record<string, unknown>
      | undefined) ?? undefined;
  // Apply the DD-resolved jurisdiction override when supplied. Keep
  // the rest of jurisdiction_meta (investment_type, degraded flag,
  // degraded_reason) unchanged.
  const jurisdictionMeta = jurisdictionOverride
    ? { ...(rawJurisdictionMeta || {}), jurisdiction: jurisdictionOverride }
    : rawJurisdictionMeta;

  const legacyClauses =
    (getNested(spa, 'mandatory_clauses') as unknown[]) ??
    (getNested(spa, 'clauses') as unknown[]) ??
    [];

  if (
    universal.length === 0 &&
    jurisdiction.length === 0 &&
    legacyClauses.length === 0
  ) {
    return <Pending note="No mandatory-clause data — SPA workflow row may be missing the clause check step." />;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--s5)' }}>
      {/* Universal SPA structure */}
      {universal.length > 0 && (
        <div>
          <h3
            style={{
              margin: '0 0 var(--s2) 0',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--muted)',
            }}
          >
            Universal SPA Structure ({universal.filter(
              (s) => (s as Record<string, unknown>).detected,
            ).length}{' '}
            of {universal.length} detected)
          </h3>
          <table className="data-table" aria-label="Universal SPA structure">
            <thead>
              <tr>
                <th>Section</th>
                <th>Present</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {universal.slice(0, 20).map((c, i) => {
                const cl = c as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{String(cl.display_name ?? cl.name ?? '—')}</td>
                    <td>
                      <Badge variant={cl.detected ? 'pass' : 'fail'}>
                        {cl.detected ? 'Present' : 'Missing'}
                      </Badge>
                    </td>
                    <td className="t-muted t-sm">
                      {cl.source_document
                        ? `${cl.source_document}${cl.first_page ? ` · p.${cl.first_page}` : ''}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Jurisdiction-specific clauses */}
      {jurisdiction.length > 0 && (
        <div>
          <h3
            style={{
              margin: '0 0 var(--s2) 0',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--muted)',
            }}
          >
            Jurisdiction-Specific Clauses (
            {String(jurisdictionMeta?.jurisdiction ?? '—')} /{' '}
            {String(jurisdictionMeta?.investment_type ?? '—')}) —{' '}
            {jurisdiction.filter(
              (s) => (s as Record<string, unknown>).detected,
            ).length}{' '}
            of {jurisdiction.length} detected
          </h3>
          {Boolean(jurisdictionMeta?.degraded) && (
            /* Sprint 5 client feedback 2026-06-25 (DD.docx §10) —
               friendlier wording for the degraded-mode banner.
               Previously surfaced the raw Pydantic error class
               name ("LLM validation failed (ValidationError)")
               which read alarming to analysts. The underlying
               reason stays available on hover for debugging. */
            <p
              className="t-sm"
              data-tip={cleanTooltip(String(
                jurisdictionMeta?.degraded_reason ?? 'reason unspecified',
              ))}
              style={{
                color: 'var(--watch)',
                fontStyle: 'italic',
                marginBottom: 'var(--s2)',
              }}
            >
              ⚠ Using generic mandatory-clause list for this
              jurisdiction · pair — analyst review recommended.
            </p>
          )}
          <table className="data-table" aria-label="Jurisdiction-specific clauses">
            <thead>
              <tr>
                <th>Clause</th>
                <th>Present</th>
                <th>Severity</th>
                <th>Why mandatory</th>
              </tr>
            </thead>
            <tbody>
              {jurisdiction.slice(0, 20).map((c, i) => {
                const cl = c as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{String(cl.display_name ?? '—')}</td>
                    <td>
                      <Badge variant={cl.detected ? 'pass' : 'fail'}>
                        {cl.detected ? 'Present' : 'Missing'}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={statusVariant(String(cl.severity ?? ''))}>
                        {String(cl.severity ?? '—')}
                      </Badge>
                    </td>
                    <td className="t-sm t-muted">
                      {String(cl.rationale ?? '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legacy / pre-Sprint-5 fallback table — rare in fresh runs. */}
      {legacyClauses.length > 0 && universal.length === 0 && jurisdiction.length === 0 && (
        <table className="data-table" aria-label="Mandatory clauses">
          <thead>
            <tr>
              <th>Clause</th>
              <th>Present</th>
              <th>Assessment</th>
            </tr>
          </thead>
          <tbody>
            {legacyClauses.slice(0, 20).map((c, i) => {
              const cl = c as Record<string, unknown>;
              return (
                <tr key={i}>
                  <td>{String(cl.clause ?? cl.name ?? cl.label ?? '—')}</td>
                  <td>
                    <Badge variant={statusVariant(String(cl.present ?? cl.status ?? ''))}>
                      {String(cl.present ?? cl.status ?? '—')}
                    </Badge>
                  </td>
                  <td>{String(cl.assessment ?? cl.note ?? cl.rationale ?? '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 13 rebuilt to match
   `New UI Changes/risk-report.html` §13:
     - Status summary chips with iconography (✓ ≈ ⚠ ⊘)
     - Grouped tables per bucket (A Company Facts, B Deal/Funding Facts,
       C Market Claims) — matches the design's letter-prefixed group
       headers
     - Per-row status badge with the same iconography
     - "Material items — could not be verified" callout block for any
       claim with status=not_found (the design's group E) */

/* Sprint 5 client feedback 2026-07-11 (Iter5 #15, #17) — status
   badges no longer carry the ✓ ≈ ⚠ ⊘ symbol prefix; the text label
   alone is enough. `not_in_public` stays on badge-neutral (grey) —
   Iter5 #17 asks explicitly for grey not red, and the map already
   uses badge-neutral, so this is a rename-only correction for the
   icon field. */
const _STATUS_META: Record<
  string,
  { label: string; badgeClass: string; icon: string }
> = {
  confirmed: { label: 'Confirmed', badgeClass: 'badge badge-pass', icon: '' },
  consistent: { label: 'Consistent', badgeClass: 'badge badge-info', icon: '' },
  divergent: { label: 'Divergent', badgeClass: 'badge badge-watch', icon: '' },
  /* Sprint 5 client feedback 2026-06-21: `not_found` is a legitimate
     comparator verdict ("public sources don't contain a match"), not
     a parse failure. Default it to "Not in public" instead of the
     previous misleading "Parse error" label. The _claimStatusBadge
     helper below now inspects the rationale to escalate to a real
     "Parse error" badge ONLY when the verdict came from the
     comparator's JSON-invalid / timeout fallback. */
  not_found: { label: 'Not in public', badgeClass: 'badge badge-neutral', icon: '' },
  /* Sprint 5 client feedback 2026-07-02 — two new status types
     specifically for §13-C canonical placeholder rows so the
     analyst can distinguish an OPS issue (data in deck, extractor
     missed it) from a DATA issue (deck genuinely silent on this
     attribute). Both amber ⚠ / neutral ⊘ chips so the FE's
     divergent-count summary stays honest — extraction gaps and
     silent-source rows are not divergences.
        extraction_gap  — deck HAS market data (market_forecasts
                          items exist) but this specific canonical
                          scalar was not aggregated by DD.
        not_documented  — deck is silent on market data across ALL
                          sources; this row exists purely for the
                          §13-C canonical layout completeness. */
  extraction_gap: { label: 'Extraction gap', badgeClass: 'badge badge-watch', icon: '' },
  not_documented: { label: 'Not documented', badgeClass: 'badge badge-neutral', icon: '' },
  parse_error: { label: 'Parse error', badgeClass: 'badge badge-neutral', icon: '' },
};

const _BUCKET_META: Record<
  string,
  { letter: string; title: string; subtitle?: string; emptyNote?: JSX.Element }
> = {
  // Sprint 5 client feedback 2026-06-24 — buckets keep a stable
  // canonical order (A → A2 → B → C) regardless of which ones have
  // claims this run. Each bucket carries an optional subtitle the
  // user requested ("— forecasts, read as ranges" for C) and a
  // per-bucket emptyNote so the analyst sees WHY a section is sparse
  // rather than just missing.
  company: {
    letter: 'A',
    title: 'Company Facts',
    emptyNote: (
      <>
        No company-fact claims to verify. The Stage 3 claim collector emits this group only
        when DD extracts attributes like <code className="t-mono">year_founded</code>,
        <code className="t-mono"> headquarters</code>, <code className="t-mono">headcount</code>,
        <code className="t-mono"> revenue_status</code>, and <code className="t-mono">total_raised</code>.
        See Group A2 below for the public-source enrichment that filled the same gap.
      </>
    ),
  },
  deal: {
    letter: 'B',
    title: 'Deal / Funding Facts',
    emptyNote: (
      <>
        No deal-fact claims surfaced. Stage 3 emits this group when DD extracts attributes like
        <code className="t-mono"> deal_size_oia_equity_usd_m</code>,
        <code className="t-mono"> post_money_valuation</code>,
        <code className="t-mono"> total_raised</code>,
        <code className="t-mono"> lead_investors</code>, and
        <code className="t-mono"> oia_money_multiple</code>.
      </>
    ),
  },
  market: {
    letter: 'C',
    title: 'Market Claims',
    subtitle: '— forecasts, read as ranges',
    emptyNote: (
      <>
        No market-fact claims surfaced. Stage 3 emits this group when DD extracts attributes like
        <code className="t-mono"> market_size_usd_m</code>,
        <code className="t-mono"> market_growth_rate_pct</code>,
        <code className="t-mono"> estimated_market_share_pct</code>, and
        <code className="t-mono"> market_indicators</code>.
        Forecast ranges in the deck (e.g. "$1B (2022) → $30B (2030)") are typically captured here
        once the extractor lands on the BNEF / IEA forecast slide.
      </>
    ),
  },
};

// Sprint 5 client feedback 2026-06-24 — _isParseErrorRationale
// retired. The analyst prefers a single "Not in Public" disposition
// for every row where the comparator couldn't corroborate, so the
// promotion logic was removed. The parse_error entry in _STATUS_META
// is kept commented out in case a future revision wants the chip back.

/**
 * Sprint 5 client feedback 2026-06-24 — defensive divergent
 * promotion, shared between the KPI count row and the per-row
 * chip. When the comparator falls back to `not_found` but Tavily
 * DID surface a public_value that differs from the stated claim,
 * the row reads more truthfully as Divergent than as "Not in
 * Public" — the analyst can see the public column has content.
 *
 * Sprint 5 client feedback 2026-07-01 — hoisted to module scope so
 * the KPI counter at line ~664 and the notInPublicCount computation
 * at line ~2438 both use this same logic. Prior split had the KPI
 * counting raw c.status while the row chip did the promotion,
 * making "Divergent 2" render next to 3 ⚠ chips in the body.
 */
function _effectiveClaimStatus(c: Record<string, unknown>): string {
  const raw = String(c.status ?? '').toLowerCase();
  if (raw !== 'not_found') return raw;
  const pub = String((c.public_value ?? c.publicValue ?? '') as string).trim();
  const stated = String(
    (c.claimed_value ?? c.stated_value ?? c.value ?? '') as string,
  ).trim();
  if (pub && pub !== '—' && stated && pub.toLowerCase() !== stated.toLowerCase()) {
    return 'divergent';
  }
  return 'not_found';
}

function _claimStatusBadge(
  status: string,
  _rationale?: string | null,
  public_value?: string | null,
  claimed_value?: string | null,
): JSX.Element {
  const effective = _effectiveClaimStatus({
    status,
    public_value,
    claimed_value,
  } as Record<string, unknown>);
  /* Iter5 #15 — drop the leading icon; render only the text label.
     The fallback meta stays icon-empty too. */
  const meta = _STATUS_META[effective] ?? { label: status, badgeClass: 'badge badge-neutral', icon: '' };
  return <span className={meta.badgeClass}>{meta.label}</span>;
}

/* Sprint 5 client feedback 2026-06-21: per the design at
   Investment-Assessment-Platform 5/risk-report.html#s13, Section 13
   has FIVE groups not three:
     A  Company Facts            — from company-bucket claims
     A2 Company Overview         — from risk.enrichment.rows (public-
                                    source enrichment, NOT claims)
     B  Deal / Funding Facts
     C  Market Claims
     E  Material Items Could Not Be Verified
   We now accept `risk` so the A2 panel can read enrichment.rows. */
function _enrichmentTopicMeta(topicId: string): { label: string } {
  const m: Record<string, string> = {
    management_team: 'Management team',
    syndicate: 'Full investor syndicate',
    customers: 'Customers / projects / offtake',
    corporate_info: 'Other corporate info',
  };
  return { label: m[topicId] ?? topicId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) };
}

function _hostnameFromTavily(url: string | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function ClaimsTable({
  claims,
  byStatus,
  risk,
}: {
  claims: ConsolidatedReport['claims'];
  byStatus: Record<string, number>;
  risk: Record<string, unknown>;
}): JSX.Element {
  if (claims.length === 0) {
    return <Pending note="No verifiable claims — Risk Stage 3 not yet run, or no claims extracted from the DD." />;
  }

  /* Group claims by bucket so each group becomes its own table. Order
     by the canonical A → B → C convention. */
  const byBucket: Record<string, ConsolidatedReport['claims']> = {};
  claims.forEach((c) => {
    const b = c.bucket || 'other';
    (byBucket[b] = byBucket[b] || []).push(c);
  });
  // Sprint 5 client feedback 2026-06-24 — always render the canonical
  // A → A2 → B → C order even when a bucket has no claims, so the
  // analyst can see at a glance which sections were attempted vs
  // which actually returned data. The empty-bucket placeholder
  // mirrors the existing Group-A "no claims" treatment. Any
  // non-canonical bucket keys (defensive — backend currently emits
  // only company/deal/market) tail-render after C.
  const bucketOrder = ['company', 'deal', 'market'];
  const extraBuckets = Object.keys(byBucket).filter(
    (b) => !bucketOrder.includes(b) && byBucket[b]?.length,
  );
  const orderedBuckets = [...bucketOrder, ...extraBuckets];

  /* Material unverified items get a callout block at the bottom
     mirroring the design's "Group E". Sprint 5 2026-07-01 — use
     _effectiveClaimStatus so a claim promoted to divergent (public
     value present but different from stated) is NOT double-counted
     as unverified. Prior code counted raw status, which produced
     "Divergent 2 / Not in Public 10" while the analyst counted
     3 ⚠ chips and 9 ⊘ chips in the body. */
  const unverified = claims.filter(
    (c) => _effectiveClaimStatus(c as unknown as Record<string, unknown>) === 'not_found',
  );
  const notInPublicCount = unverified.length;
  /* Sprint 5 client feedback 2026-07-02 — dedicated counters for the
     two new §13-C statuses so the KPI strip can distinguish an OPS
     issue (deck has market data, extractor missed a canonical
     scalar) from a DATA issue (deck genuinely silent). Both
     categories are rendered as chips only when count > 0. */
  const extractionGapCount = claims.filter(
    (c) => String(c.status ?? '').toLowerCase() === 'extraction_gap',
  ).length;
  const notDocumentedCount = claims.filter(
    (c) => String(c.status ?? '').toLowerCase() === 'not_documented',
  ).length;

  /* Group A2 — Company Overview (public enrichment). Pulled from
     risk.enrichment.rows which the Stage 3 orchestrator populates via
     the per-topic Tavily lookups (management_team, syndicate,
     corporate_info, etc.). Each row carries snippets + source URLs;
     we trim the snippet to a short one-line summary so the table
     stays readable.

     Sprint 5 client feedback 2026-06-24 — A2 now renders immediately
     after Group A (Company Facts) rather than at the bottom of §13,
     so the public-enrichment companion table sits next to the
     company-fact verification table it complements. B (Deal /
     Funding) and C (Market Claims) follow. */
  const enrichment = risk.enrichment as Record<string, unknown> | null | undefined;
  const enrichRows = (
    (enrichment?.rows as Array<Record<string, unknown>> | undefined) ?? []
  ).filter((r) => {
    const snippets = String(r.snippets ?? '').trim();
    const extracted = String(r.extracted_value ?? '').trim();
    const failed = r.failed_reason;
    return (snippets.length > 0 || extracted.length > 0) && !failed;
  });

  // Sprint 5 client feedback 2026-06-24 — A2 enrichment panel JSX
  // hoisted into a constant so it can render either (a) immediately
  // after the empty-company placeholder, or (b) immediately after
  // the 'company' bucket row in the bucket loop. Both paths land
  // A2 right between Group A and Group B without duplicating markup.
  const a2Panel: JSX.Element | null = enrichRows.length > 0 ? (
    <div className="ra-verif-group" key="a2-enrichment">
      <div className="ra-vg-title">
        <span className="gl">A2</span>
        Company Overview — Public Enrichment
      </div>
      <div className="ra-caveat" role="note">
        <span className="ci">ⓘ</span>
        <span>
          These fields were <b>absent from the proposal documents</b> and filled
          from public data via the Stage 3 enrichment pass. Verify against primary
          documents before relying on them.
        </span>
      </div>
      <div className="table-wrap">
        <table className="data-table" aria-label="Company overview public enrichment">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Field</th>
              <th>Public Finding</th>
              <th style={{ width: '20%' }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {enrichRows.map((row, i) => {
              const meta = _enrichmentTopicMeta(String(row.topic_id ?? `topic-${i}`));
              const snippets = String(row.snippets ?? '').trim();
              const extracted = (row.extracted_value as string | null | undefined)?.trim();
              let summary: string;
              if (extracted) {
                summary = extracted;
              } else {
                const firstChunk = snippets
                  .split(/\n\n\[/)[0]
                  .replace(/^\[\d+\]\s*[^\n]*\n?/, '')
                  .trim();
                summary = firstChunk.length > 280
                  ? firstChunk.slice(0, 277) + '…'
                  : firstChunk;
              }
              const sourceUrls = (row.source_urls as string[] | undefined) ?? [];
              const primaryUrl = sourceUrls[0];
              const sourceHost = _hostnameFromTavily(primaryUrl);
              return (
                <tr key={i}>
                  <td className="td-name">{meta.label}</td>
                  {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) —
                      A2 enrichment public findings had **bold** LLM
                      output rendering as literal asterisks. Route
                      through FormattedText. */}
                  <td className="t-xs" style={{ maxWidth: 460, whiteSpace: 'normal' }}>
                    {summary ? <FormattedText text={summary} inline /> : <span className="t-muted">—</span>}
                  </td>
                  <td className="t-xs">
                    {primaryUrl ? (
                      <a
                        href={primaryUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--accent, var(--info, #2563eb))' }}
                      >
                        {sourceHost}
                      </a>
                    ) : <span className="t-muted">—</span>}
                    {sourceUrls.length > 1 && (
                      // Sprint 5 client feedback 2026-06-24
                      // (AUDIT-2026-0054 S-15): "+N more" used to
                      // render as plain text — the additional
                      // source URLs were collected but never
                      // surfaced to the analyst. Now each extra
                      // source renders as a small clickable host
                      // chip next to the primary, comma-separated,
                      // so the analyst can verify each one.
                      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, marginLeft: 6 }}>
                        {sourceUrls.slice(1).map((u, ui) => {
                          const h = _hostnameFromTavily(u);
                          return (
                            <span key={ui} className="t-xs t-muted">
                              ·{' '}
                              <a
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: 'var(--accent, var(--info, #2563eb))' }}
                              >
                                {h}
                              </a>
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="t-xs t-muted" style={{ marginTop: 'var(--s1)', fontStyle: 'italic' }}>
        Shareholder structure (ownership %) is private — see Section 17 (Captured Document Data)
        for management-provided cap-table figures.
      </p>
    </div>
  ) : null;

  return (
    <div style={{ display: 'grid', gap: 'var(--s5)' }}>
      {/* Status summary chips — Sprint 5 Phase 6 of Risk Report UI port.
          Switched to the mockup's .ra-verif-sum + .ra-sum-chip layout
          (pill row with badge + mono count). */}
      <div className="ra-verif-sum">
        {(['confirmed', 'consistent', 'divergent'] as const).map((s) => {
          const n = byStatus[s] || 0;
          const meta = _STATUS_META[s];
          return (
            <span key={s} className="ra-sum-chip">
              <span className={meta.badgeClass}>{meta.label}</span>
              <b>{n}</b>
            </span>
          );
        })}
        {/* Sprint 5 client feedback 2026-06-24 — single "Not in Public"
            chip replaces the prior "Not in Public" + "Parse error"
            pair. The comparator's transient parse failures and
            genuine Tavily misses both surface here. */}
        <span className="ra-sum-chip">
          <span className={_STATUS_META.not_found.badgeClass}>
            {_STATUS_META.not_found.icon} {_STATUS_META.not_found.label}
          </span>
          <b>{notInPublicCount}</b>
        </span>
        {/* Sprint 5 client feedback 2026-07-02 — Extraction gap +
            Not documented chips only render when a §13-C canonical
            placeholder actually fired that status, so runs where
            the market bucket is empty don't add clutter. */}
        {extractionGapCount > 0 && (
          <span className="ra-sum-chip">
            <span className={_STATUS_META.extraction_gap.badgeClass}>
              {_STATUS_META.extraction_gap.icon} {_STATUS_META.extraction_gap.label}
            </span>
            <b>{extractionGapCount}</b>
          </span>
        )}
        {notDocumentedCount > 0 && (
          <span className="ra-sum-chip">
            <span className={_STATUS_META.not_documented.badgeClass}>
              {_STATUS_META.not_documented.icon} {_STATUS_META.not_documented.label}
            </span>
            <b>{notDocumentedCount}</b>
          </span>
        )}
      </div>

      {/* Sprint 5 client feedback 2026-06-24 — buckets render in
          canonical A → A2 → B → C order regardless of which ones
          have claims this run. Empty buckets render a small
          placeholder explaining why so the report reads complete.
          A2 (Company Overview — Public Enrichment) injects as a
          Fragment sibling immediately after the 'company' bucket. */}
      {orderedBuckets.map((bucket) => {
        const meta = _BUCKET_META[bucket] ?? {
          letter: bucket[0]?.toUpperCase() ?? '·',
          title: bucket.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        };
        const rows = byBucket[bucket] || [];
        const titleRow = (
          <div className="ra-vg-title">
            <span className="gl">{meta.letter}</span>
            {meta.title}
            {meta.subtitle && (
              <span className="ra-vg-note">{meta.subtitle}</span>
            )}
          </div>
        );
        const bucketEl = rows.length === 0 ? (
          <div key={bucket} className="ra-verif-group">
            {titleRow}
            <div
              style={{
                padding: 'var(--s3) var(--s4)',
                background: 'var(--surface)',
                border: '1px dashed var(--lines)',
                borderRadius: 'var(--r-sm)',
                fontSize: '0.82rem',
                color: 'var(--text-sec)',
              }}
            >
              {meta.emptyNote ?? `No ${meta.title.toLowerCase()} claims to verify.`}
            </div>
          </div>
        ) : (
          <div key={bucket} className="ra-verif-group">
            {titleRow}
            <div className="table-wrap">
              <table className="data-table" aria-label={`${meta.title} verification`}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Stated in Document</th>
                    {/* Sprint 5 client feedback 2026-06-22 — added
                        Source Doc column per BE-issues doc #8 on
                        AUDIT-2026-0034. Every VerifiableClaim row
                        carries source_document + source_page from
                        the DD extraction; surface them so the
                        analyst can jump to the supporting page. */}
                    <th>Source Doc</th>
                    <th>Public Finding</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    /* Sprint 5 client feedback 2026-06-23 — surface
                       divergent rows with the design's .ra-diverge
                       row-level highlight + use the mockup's compact
                       .ra-pub-src span (uppercase mono "Public"
                       prefix) and .none placeholder class so the
                       mockup-defined CSS rules pick up styling. */
                    const rowClass = c.status === 'divergent' ? 'ra-diverge' : undefined;
                    return (
                      <tr key={c.id} className={rowClass}>
                        <td className="td-name">{c.field}</td>
                        {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3 + #2)
                            — LLM-emitted **bold** in claimed_value renders
                            through FormattedText; long descriptions are
                            clamped to 2 lines with a View link that opens
                            the full text in a modal. Nested inside TruncatedCell
                            so the two behaviours compose cleanly. */}
                        <td className="ra-stated">
                          <TruncatedCell
                            text={stripMarkdown(String(c.claimed_value ?? ''))}
                            title={`Stated · ${c.field ?? 'value'}`}
                          >
                            <FormattedText text={String(c.claimed_value ?? '')} inline />
                          </TruncatedCell>
                        </td>
                        <td className="t-xs">
                          {/* Iter6 #1 — canonical SourceChip. */}
                          <SourceChip doc={c.source_document ?? null} page={c.source_page ?? null} />
                        </td>
                        <td className="ra-public">
                          {c.public_value
                            ? (
                              <>
                                {c.source_url ? (
                                  <a
                                    href={c.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="ra-pub-src"
                                  >
                                    Public ↗
                                  </a>
                                ) : (
                                  <span className="ra-pub-src">Public</span>
                                )}
                                {/* UI Issues #2 — long public findings clamped
                                    with View. */}
                                <TruncatedCell
                                  text={stripMarkdown(String(c.public_value))}
                                  title={`Public finding · ${c.field ?? 'value'}`}
                                >
                                  <FormattedText text={String(c.public_value)} inline />
                                </TruncatedCell>
                              </>
                            ) : <span className="none">—</span>}
                        </td>
                        <td>{_claimStatusBadge(c.status, c.rationale, c.public_value, c.claimed_value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
        // Sprint 5 client feedback 2026-06-24 — when this bucket is
        // 'company', append the A2 enrichment panel right after it
        // so the visual order reads A → A2 → B → C.
        if (bucket === 'company' && a2Panel) {
          return (
            <React.Fragment key={bucket}>
              {bucketEl}
              {a2Panel}
            </React.Fragment>
          );
        }
        return bucketEl;
      })}

      {/* Sprint 5 client feedback 2026-06-23 — Section D Flagged
          Contradictions. Two sources of contradictions:
          (1) doc-vs-public divergences: any claim whose status was
              comparator-judged "divergent" (the public source
              disagrees with the doc's claimed value).
          (2) intra-document contradictions: pulled from
              risk.captured_data.intra_document_contradictions
              (populated by contradiction_detector — e.g. management
              projects 31 GW vs Apricum 15 GW in the same deck).
          Renders nothing when both buckets are empty. */}
      {(() => {
        // Sprint 5 2026-07-01 — use _effectiveClaimStatus so section
        // D lists every row whose chip renders as ⚠ Divergent in the
        // A/B/C tables above, including the ones promoted from
        // not_found because a public_value differs from the stated
        // value.
        const divergent = claims.filter(
          (c) => _effectiveClaimStatus(c as unknown as Record<string, unknown>) === 'divergent',
        );
        const captured = (risk.captured_data as Record<string, unknown> | null | undefined) ?? {};
        const intraRaw = (captured?.intra_document_contradictions as Array<Record<string, unknown>> | undefined) ?? [];
        const intra = intraRaw.filter((x) => x && typeof x === 'object');
        if (divergent.length === 0 && intra.length === 0) return null;
        return (
          <div className="ra-verif-group">
            <div className="ra-vg-title">
              <span className="gl" style={{ background: 'var(--watch)' }}>D</span>
              Flagged Contradictions
              <span className="ra-vg-note">— reviewer attention</span>
            </div>
            {/* Doc-vs-public divergences — Sprint 5 Phase 7 of Risk Report
                UI port. Each card uses the design's .ra-contra block
                with a left-bordered watch-tinted background, .cmp
                comparison row (DOC chip + PUBLIC chip), and the
                rationale below. */}
            {divergent.map((c) => (
              <div key={`div-${c.id}`} className="ra-contra">
                <div className="ct">
                  <span className="badge badge-watch">⚠ Contradiction</span>
                  <span>{c.field}</span>
                </div>
                {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) —
                    LLM-emitted **bold** in claimed_value / public_value /
                    rationale was rendering as literal asterisks in the
                    contradiction cards. Route through FormattedText. */}
                <div className="cmp">
                  <span className="b doc">
                    DOC · <FormattedText text={String(c.claimed_value ?? '—')} inline />
                  </span>
                  <span className="b pub">
                    PUBLIC · <FormattedText text={String(c.public_value ?? '—')} inline />
                  </span>
                </div>
                {c.rationale && <p><FormattedText text={String(c.rationale)} inline /></p>}
              </div>
            ))}
            {/* Intra-document contradictions — Sprint 5 client feedback
                2026-06-23. Mockup distinguishes intra-doc contradictions
                from doc-vs-public ones:
                  - Header badge uses badge-fail (red) instead of
                    badge-watch (amber).
                  - The second comparison chip uses .b.pub-alt (accent
                    tint) instead of .b.pub (watch tint) — the two
                    sources are MGMT vs in-deck-analyst (e.g. Apricum),
                    both inside the document. */}
            {intra.map((row, i) => {
              const topic = String(row.topic ?? '—');
              const narrative = String(row.narrative ?? '');
              const sourcePrimary = String(
                (row.source_primary ?? row.source_a ?? 'MGMT'),
              );
              const sourceSecondary = String(
                (row.source_secondary ?? row.source_b ?? 'IN-DECK'),
              );
              const valuePrimary = String(
                (row.value_primary ?? row.position_a ?? row.primary ?? ''),
              );
              const valueSecondary = String(
                (row.value_secondary ?? row.position_b ?? row.secondary ?? ''),
              );
              const hasComparison = valuePrimary || valueSecondary;
              return (
                <div key={`intra-${i}`} className="ra-contra">
                  <div className="ct">
                    <span className="badge badge-fail">⚠ Internal contradiction</span>
                    <span>{topic}</span>
                  </div>
                  {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) —
                      LLM emits **bold** in intra-doc contradiction
                      values + narrative. Route through FormattedText. */}
                  {hasComparison && (
                    <div className="cmp">
                      <span className="b doc">
                        {sourcePrimary}
                        {valuePrimary && (
                          <> · <FormattedText text={valuePrimary} inline /></>
                        )}
                      </span>
                      <span className="b pub-alt">
                        {sourceSecondary}
                        {valueSecondary && (
                          <> · <FormattedText text={valueSecondary} inline /></>
                        )}
                      </span>
                    </div>
                  )}
                  {narrative && <p><FormattedText text={narrative} inline /></p>}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Material items — could not be verified (group E in the design).
          Phase 7 of Risk Report UI port — switched from inline-styled
          flex rows to the mockup's .ra-unverified blocks with
          .uv-ic exclamation chip and .uv text body. */}
      {unverified.length > 0 && (
        <div className="ra-verif-group">
          {/* Sprint 5 client feedback 2026-07-12 — this group is an ABSENCE of
              public sources, not a set of failures, so its marker is neutral
              rather than fail-red and the row icon is "?" (unknown) rather
              than "!" (alarm). Red is reserved for genuine divergences. */}
          <div className="ra-vg-title">
            <span className="gl" style={{ background: 'var(--muted)' }}>E</span>
            Material Items — Could Not Be Verified
          </div>
          {unverified.slice(0, 10).map((c) => (
            <div key={c.id} className="ra-unverified">
              <span className="uv-ic" aria-hidden="true">?</span>
              <div className="uv">
                <b>
                  {c.field}
                  {_claimStatusBadge(c.status, c.rationale)}
                </b>
                {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) —
                    LLM emits **bold** inside the material-item rationale;
                    render through FormattedText so bold spans work and
                    literal ** disappears. */}
                <span>
                  Material but not confirmable from public sources;{' '}
                  {c.rationale
                    ? <FormattedText text={String(c.rationale)} inline />
                    : 'request primary documentation.'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 14 rebuilt to match
   `New UI Changes/risk-report.html` §14. Three changes:
     1. Overall Sector Posture card at the top — derived from the
        signal-count distribution across the probes.
     2. Source column added to the probe table (was hidden; the
        backend already persists source_urls on every probe).
     3. Signal badges use the design's iconography:
          ▲ Positive (green) / ▼ Negative (red) /
          ◑ Mixed (amber)   / ⊘ Not Found (grey)
   Sprint 5 client feedback 2026-06-23 — relabelled "Parse error" →
   "Not Found" for the analyst-facing signal column. The internal
   verdict token (parse_error / error) is unchanged; only the display.
*/
function _signalMeta(
  signal: string,
  context?: { rationale?: string; source_url?: string },
): { label: string; badgeClass: string; icon: string } {
  const s = signal.toLowerCase();
  if (s === 'positive') return { label: 'Positive', badgeClass: 'badge badge-pass', icon: '▲' };
  if (s === 'negative') return { label: 'Negative', badgeClass: 'badge badge-fail', icon: '▼' };
  if (s === 'mixed') return { label: 'Mixed', badgeClass: 'badge badge-watch', icon: '◑' };
  if (s === 'error' || s === 'parse_error') {
    // Sprint 5 client feedback 2026-06-24 (AUDIT-2026-0054 S-27):
    // distinguish "Not Found" failure modes based on rationale +
    // source URL. The error bucket covered four genuinely
    // different cases — paywall, login required, empty response,
    // genuine miss — but rendered them identically as one chip.
    const rationale = (context?.rationale ?? '').toLowerCase();
    const url = (context?.source_url ?? '').toLowerCase();
    if (rationale.includes('sign in') || rationale.includes('login required') || url.includes('linkedin.com')) {
      return { label: 'Login required', badgeClass: 'badge badge-neutral', icon: '⊘' };
    }
    if (rationale.includes('paywall') || rationale.includes('subscribe') || rationale.includes('subscription required')) {
      return { label: 'Paywalled', badgeClass: 'badge badge-neutral', icon: '⊘' };
    }
    if (rationale.includes('empty response') || rationale.includes('no content') || rationale.includes('empty body')) {
      return { label: 'Empty response', badgeClass: 'badge badge-neutral', icon: '⊘' };
    }
    return { label: 'Not Found', badgeClass: 'badge badge-neutral', icon: '⊘' };
  }
  return { label: signal || '—', badgeClass: 'badge badge-neutral', icon: '•' };
}

function _hostnameFromUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url.length > 32 ? url.slice(0, 29) + '…' : url;
  }
}

/* Sprint 5 client feedback 2026-07-12 — the posture card was hardcoded to
   the fail tone in CSS (red fill, red border, red label) because the design
   mock happened to show "Deteriorating". Only the value text was coloured
   from the verdict, so a positive read rendered as a green word inside a red
   box — the card said "bad" while the text said "good". The tone now travels
   with the verdict, so ALL FOUR outcomes of the heuristic below are styled
   truthfully. */
type PostureTone = 'pass' | 'fail' | 'info' | 'neutral';

function _overallSectorPosture(
  probes: Array<Record<string, unknown>>,
): { label: string; colour: string; tone: PostureTone } {
  const counts = { positive: 0, negative: 0, mixed: 0, error: 0 };
  for (const p of probes) {
    const s = String(p.signal || '').toLowerCase();
    if (s === 'positive') counts.positive++;
    else if (s === 'negative') counts.negative++;
    else if (s === 'mixed') counts.mixed++;
    else counts.error++;
  }
  /* Posture heuristic — the backend classifies each probe as
     positive / negative / mixed / error; these are the only four verdicts
     that can result:
     - negative > positive → "Deteriorating" (fail — red)
     - positive > negative → "Improving"     (pass — green)
     - balanced            → "Stable"        (info — blue)
     - everything errored  → "Inconclusive"  (neutral — grey) */
  const evaluated = counts.positive + counts.negative + counts.mixed;
  if (evaluated === 0) return { label: 'Inconclusive', colour: 'var(--muted)', tone: 'neutral' };
  if (counts.negative > counts.positive) return { label: 'Deteriorating', colour: 'var(--fail)', tone: 'fail' };
  if (counts.positive > counts.negative) return { label: 'Improving', colour: 'var(--pass)', tone: 'pass' };
  return { label: 'Stable', colour: 'var(--info)', tone: 'info' };
}

function MarketPerformance({ risk }: { risk: Record<string, unknown> }): JSX.Element {
  const probes = (risk.market_probes as Array<Record<string, unknown>> | null) ?? [];
  /* Sprint 5 client feedback 2026-06-23 — peer_benchmark is persisted by
     the orchestrator as {target, peers}; reading it directly as an
     Array<> silently produced length=undefined and hid the whole
     "Peer Financial Benchmark — Listed Comparables" table. */
  const pb = risk.peer_benchmark as
    | { peers?: Array<Record<string, unknown>> }
    | Array<Record<string, unknown>>
    | null;
  const peers: Array<Record<string, unknown>> = Array.isArray(pb)
    ? pb
    : (pb?.peers ?? []);
  if (probes.length === 0 && peers.length === 0) {
    // Sprint 5 client feedback 2026-06-24 (DD/Risk.docx issue 11) —
    // §14 was rendering as a bare "No market data" stub which read
    // as a system error. The actual cause is usually one of: TAVILY
    // API key missing, sector not in the EDGAR coverage set, or the
    // probes ran but found no listed comparables. Spell out the
    // diagnostic + remediation path so the analyst can act on it.
    return (
      <Pending
        note={
          'No external market data was collected for this engagement. ' +
          'Likely causes: (1) Tavily probes returned 0 hits for the sector keywords, ' +
          '(2) EDGAR has no listed comparables for the deal\'s sub-sector, or ' +
          '(3) TAVILY_API_KEY is unset/401 (check the orchestrator env). ' +
          'Re-run Risk Assessment after confirming the API key is valid; ' +
          'if the sector is truly niche (e.g. green hydrogen pre-revenue), document the ' +
          'absence in the IC paper rather than leaving the section blank.'
        }
      />
    );
  }

  const posture = _overallSectorPosture(probes);
  const today = new Date().toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--s4)' }}>
      {/* Overall Sector Posture card — Sprint 5 Phase 7 of Risk Report
          UI port. Switched from inline-styled flex box to the mockup's
          .ra-mp-posture block (fail-tinted container with .pl label,
          .pv display-font value, .pd italic timestamp on the right). */}
      {probes.length > 0 && (() => {
        /* Sprint 5 client feedback 2026-07-11 (Iter5 #18) — tooltip
           on the Overall Sector Posture badge that lists which
           probes drove the verdict + their signal, so the analyst
           can see why the AI picked Improving / Stable /
           Deteriorating without opening §16. */
        const counts = { positive: 0, negative: 0, mixed: 0 };
        const wins: string[] = [];
        for (const p of probes) {
          const sig = String(p.signal ?? '').toLowerCase();
          if (sig === 'positive') counts.positive += 1;
          if (sig === 'negative') counts.negative += 1;
          if (sig === 'mixed') counts.mixed += 1;
          wins.push(`  • ${String(p.label ?? '(unnamed)')} — ${p.signal ?? '?'}`);
        }
        const _tip = (
          `Overall Sector Posture — ${posture.label}\n\n`
          + `Verdict rule: positive > negative → Improving, negative > positive → Deteriorating, `
          + `balanced → Stable.\n\n`
          + `This deal: ${counts.positive} positive · ${counts.negative} negative · ${counts.mixed} mixed.\n\n`
          + `Contributing probes:\n${wins.slice(0, 10).join('\n')}`
        );
        // The tone class carries fill + border + label colour; no inline colour
        // overrides, so the card can never disagree with its own verdict.
        return (
          <div
            className={`ra-mp-posture tone-${posture.tone}`}
            style={{ cursor: 'help', whiteSpace: 'pre-line' }}
            data-tip={cleanTooltip(_tip)}
          >
            <span className="pl">Overall Sector Posture</span>
            <span className="pv">{posture.label}</span>
            <span className="pd">AI independent view as of {today}</span>
          </div>
        );
      })()}

      {/* Per-indicator probe table — 4 columns including Source */}
      {probes.length > 0 && (
        <div className="table-wrap">
          <table className="data-table" aria-label="Market probes">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Signal</th>
                <th>Evidence</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {probes.slice(0, 10).map((p, i) => {
                const urls = (p.source_urls as string[] | undefined) ?? [];
                const firstUrl = urls.length > 0 ? urls[0] : null;
                const sig = _signalMeta(
                  String(p.signal ?? p.verdict ?? ''),
                  {
                    rationale: String(p.rationale ?? p.evidence ?? ''),
                    source_url: firstUrl ?? undefined,
                  },
                );
                return (
                  <tr key={i}>
                    <td className="td-name">{String(p.label ?? p.signal ?? '—')}</td>
                    <td>
                      <span className={sig.badgeClass}>{sig.icon} {sig.label}</span>
                    </td>
                    {/* Sprint 5 client feedback 2026-07-11 (Iter5 #19)
                        — Evidence column should render as plain
                        prose, not bold. Previously routed through
                        FormattedText which renders **markdown**
                        emphasis as <b>. Switched to stripMarkdown so
                        the excerpt shows without emphasis; the modal
                        body uses the same source too. */}
                    <td className="t-xs" style={{ fontWeight: 400 }}>
                      <TruncatedCell
                        text={stripMarkdown(String(p.rationale ?? '—'))}
                        title={`Evidence · ${String(p.label ?? p.signal ?? 'signal')}`}
                      >
                        {stripMarkdown(String(p.rationale ?? '—'))}
                      </TruncatedCell>
                    </td>
                    <td className="t-xs">
                      {firstUrl ? (
                        <a
                          href={firstUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--accent, var(--info))', fontSize: '0.75rem' }}
                        >
                          {_hostnameFromUrl(firstUrl)} ↗
                        </a>
                      ) : (
                        <span className="t-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="ra-note">
            Sector signals are an independent analyst read and do not modify
            the document's own market claims (Section 13-C); they provide
            context for the assurance opinion. A deteriorating sector posture
            raises the weight on the company's financing runway and offtake
            commitments.
          </p>
        </div>
      )}

      {/* Peer Financial Benchmark — historical actuals from SEC EDGAR.
            Matches the design's 7-column layout. Gross Margin / Net
            Income / R&D require EDGAR XBRL company-concept fetches the
            current backend doesn't issue yet — those columns render
            "—" with a tooltip so the structure is correct and we can
            populate them in a follow-up commit. */}
      {peers.length > 0 && (
        <div>
          <div
            style={{
              fontSize: '0.88rem',
              fontWeight: 600,
              marginBottom: 'var(--s2)',
              marginTop: 'var(--s2)',
            }}
          >
            Peer Financial Benchmark — Listed Comparables (actuals)
          </div>
          {/* EDGAR caveat callout — Sprint 5 Phase 7 of Risk Report UI
              port. Switched to the design's .ra-caveat treatment used
              elsewhere for info-tinted info notes. */}
          <div className="ra-caveat">
            <span className="ci">ⓘ</span>
            <span>
              Historical financials of listed sector peers, pulled live from{' '}
              <strong>SEC EDGAR</strong> (US filers; no model involved). The
              target is private and pre-revenue, so peer <strong>actuals</strong>{' '}
              are benchmarked against the target's <strong>projections</strong> —
              not like-for-like. Latest annual (10-K); revision-prone — confirm
              against current filings.
            </span>
          </div>
          <div className="table-wrap">
            <table className="data-table" aria-label="Peer benchmark">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>FY</th>
                  <th>Revenue</th>
                  <th>Gross Margin</th>
                  <th>Net Income</th>
                  <th>R&amp;D</th>
                  <th>Cash</th>
                  {/* Sprint 5 client feedback 2026-06-23 — new Status
                      column so analysts can tell at a glance whether
                      a row's financials came from EDGAR ("Collected")
                      or are unavailable because the peer is non-US /
                      EDGAR-unresolvable ("Not in Public"). */}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {peers.slice(0, 10).map((p, i) => {
                  const formatUsdM = (v: unknown): string => {
                    if (v == null || v === '') return '—';
                    const n = typeof v === 'number' ? v : parseFloat(String(v));
                    if (!Number.isFinite(n)) return '—';
                    /* Render as $X.XXb when ≥1000m, $XXXm otherwise. */
                    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(2)}b`;
                    return `$${Math.round(n)}m`;
                  };
                  const fy = (() => {
                    const asOf = p.as_of;
                    if (!asOf) return '—';
                    /* Extract the year. as_of is usually an ISO date or year. */
                    const yr = String(asOf).match(/\d{4}/)?.[0];
                    return yr ?? String(asOf);
                  })();
                  const skippedReason = p.skipped_reason as string | null | undefined;
                  /* Empty-cell tooltip explains the missing data. */
                  const missingTooltip = skippedReason
                    || 'Requires EDGAR XBRL concept fetch — wired in a follow-up commit.';
                  const dash = (
                    <span className="t-muted" data-tip={missingTooltip}>—</span>
                  );
                  /* Status derivation: "Collected" when EDGAR returned
                     any numeric value (revenue or cash is the best
                     liveness signal). Otherwise "Not in Public" — the
                     row's peer is non-US, EDGAR-unresolvable, or the
                     fetch failed. The cell tooltip surfaces the
                     specific skipped_reason when present. */
                  const hasRevenue = p.latest_revenue_usd_m != null && p.latest_revenue_usd_m !== '';
                  const hasCash = p.latest_cash_usd_m != null && p.latest_cash_usd_m !== '';
                  const isCollected = hasRevenue || hasCash;
                  const statusBadge = isCollected
                    ? <span className="badge badge-pass" data-tip="EDGAR returned annual filings.">Collected</span>
                    : <span className="badge badge-neutral" data-tip={skippedReason || 'Peer is non-US, EDGAR-unresolvable, or no annual filing available.'}>Not in Public</span>;
                  return (
                    <tr key={i}>
                      <td className="td-name">{String(p.name ?? p.ticker ?? '—')}</td>
                      <td className="t-mono t-xs">{fy}</td>
                      <td className="t-mono t-xs">{formatUsdM(p.latest_revenue_usd_m)}</td>
                      <td className="t-mono t-xs">{p.gross_margin_pct != null ? `${p.gross_margin_pct}%` : dash}</td>
                      <td className="t-mono t-xs">{p.latest_net_income_usd_m != null ? formatUsdM(p.latest_net_income_usd_m) : dash}</td>
                      <td className="t-mono t-xs">{p.latest_rd_usd_m != null ? formatUsdM(p.latest_rd_usd_m) : dash}</td>
                      <td className="t-mono t-xs">{formatUsdM(p.latest_cash_usd_m)}</td>
                      <td>{statusBadge}</td>
                    </tr>
                  );
                })}
                {/* Sprint 5 client feedback 2026-06-23 — trailing target
                    company row so the analyst sees the deal in context
                    of its peers (POC parity). Target is private and
                    pre-revenue by definition for any deal we audit at
                    this stage, so the financial cells are descriptive
                    rather than numeric. */}
                {(() => {
                  const targetMeta = (risk.peer_benchmark as { target?: { name?: unknown } } | null)?.target;
                  const targetName = String(targetMeta?.name ?? '').trim();
                  if (!targetName) return null;
                  return (
                    <tr style={{ background: 'color-mix(in srgb, var(--accent) 6%, transparent)' }}>
                      <td className="td-name" style={{ fontWeight: 600 }}>
                        {targetName} <span className="t-muted">(target)</span>
                      </td>
                      <td className="t-mono t-xs">—</td>
                      <td className="t-mono t-xs">pre-revenue</td>
                      <td className="t-mono t-xs">—</td>
                      <td className="t-mono t-xs">losses (See Section 17)</td>
                      <td className="t-mono t-xs">—</td>
                      <td className="t-mono t-xs">See Section 17</td>
                      <td>
                        <span className="badge badge-info" data-tip="Private company — financials not on EDGAR by definition.">Target (private)</span>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 15 rebuilt to address
   three gaps the analyst saw:
     1. Overall Risk Rating showed "—" — column is null on most runs;
        now falls back to the synthesis pass's rating (the §5 LLM
        pass already produces Low/Medium/High).
     2. Heuristic Baseline showed "—" — column is null too; compute
        deterministically from flag counts so the analyst can compare
        against the AI-adjusted rating.
     3. External Calls showed "17 · $0.1500" with no breakdown — now
        renders one line per API (Tavily X, EDGAR Y, Ollama N) so
        the value is auditable.
   Plus a new Recommendations & Implications table sourced from the
   7th synthesizer pass (synthesis.recommendations). */

function _computeHeuristicBaseline(
  ddFlags: Array<Record<string, unknown>>,
  spaFlags: Array<Record<string, unknown>>,
): string {
  /* Same logic the design's narrative implies: count Critical/High
     items, return Low/Medium/High. */
  let critical = 0, high = 0;
  for (const f of [...ddFlags, ...spaFlags]) {
    const sev = String(f.severity || '').toLowerCase();
    if (sev === 'critical') critical++;
    else if (sev === 'high' || sev === 'flag') high++;
  }
  if (critical >= 2 || high >= 4) return 'High';
  if (critical >= 1 || high >= 2) return 'Medium';
  return 'Low';
}

function _ratingColour(rating: string | null | undefined): string {
  const r = (rating ?? '').trim().toLowerCase();
  if (r === 'high' || r === 'critical' || r === 'severe') return 'var(--fail)';
  if (r === 'medium' || r === 'moderate' || r === 'watch') return 'var(--watch)';
  if (r === 'low' || r === 'minimal' || r === 'pass') return 'var(--pass)';
  return 'var(--muted)';
}

/* Annexure B ladder → severity tone. ONE source of truth, so the selected
   ladder tile and the verdict paragraph below it can never disagree about
   what colour the verdict is. Legacy 4-band `class` values are folded onto
   the 5-band vocabulary first, so older projects still colour correctly
   without a data migration. */
const _AO_LEGACY_MAP: Record<string, string> = {
  'Ineffective': 'Unacceptable Risk Exposure',
  'Partially Effective': 'Major Improvement Opportunity',
  'Improvement Required': 'Some Improvement Opportunity',
  'Effective': 'Satisfactory',
};

function _aoFoldClass(cls: string): string {
  return _AO_LEGACY_MAP[cls] ?? cls;
}

function _aoToneFor(level: string): string {
  if (level.startsWith('Unacceptable')) return 'var(--fail)';
  if (level.startsWith('Major')) return 'var(--accent)';
  if (level.startsWith('Some')) return 'var(--watch)';
  if (level.startsWith('Satisfactory') || level.startsWith('Well-Managed')) return 'var(--pass)';
  return 'var(--muted)'; // unknown / not yet issued — no verdict, no colour
}

function _priorityBadgeClass(p: string): string {
  /* Sprint 5 client feedback 2026-07-12 (issues/11 §15 Recs) — badge
   * palette re-mapped to the Iter9 4-tier severity spec:
   *   High           → badge-fail  (red)
   *   Medium         → badge-watch (amber)
   *   Low            → badge-pass  (green)
   *   Recommendation → badge-info  (blue)
   * Critical folds into High. Pre-fix Medium=badge-info (blue) and
   * High=badge-watch (amber) — both wrong per client's approved
   * severity colour scheme. */
  const k = (p ?? '').trim().toLowerCase();
  if (k === 'critical' || k === 'high' || k === 'reject' || k === 'severe' || k === 'fail') return 'badge badge-fail';
  if (k === 'medium' || k === 'moderate' || k === 'watch') return 'badge badge-watch';
  if (k === 'low' || k === 'minor') return 'badge badge-pass';
  if (k === 'recommendation' || k === 'opportunity' || k === 'info') return 'badge badge-info';
  return 'badge badge-neutral';
}

function AnalysisSummary({
  risk,
  // Sprint 5 client feedback 2026-06-24 — apiCalls / apiCost /
  // apiCallsList retained on the props signature for any future
  // run-details subroute; the §15 body no longer renders them so
  // mark them unused. Keeping the props avoids touching the parent
  // call site at line 994.
  apiCalls: _apiCalls,
  apiCost: _apiCost,
  apiCallsList: _apiCallsList,
  ddReport,
  spaReport,
  projectCreatedAt,
}: {
  risk: Record<string, unknown>;
  apiCalls: number;
  apiCost: number;
  apiCallsList: ConsolidatedReport['api_calls'];
  ddReport: Record<string, unknown>;
  spaReport: Record<string, unknown>;
  /* Sprint 5 client feedback 2026-06-25 (Issues/§15 Auditor Opinion) —
     project.created_at is threaded through so the Auditor Opinion
     "Version" cell can render a dated identifier (v1.0 · DD Mon YY). */
  projectCreatedAt: string | null;
}): JSX.Element {
  // Sprint 5 client feedback 2026-07-11 (Iter5 #20) — `opinion` was
  // read only by the ISA Opinion tile that Iter5 removed; kept the
  // read behind a void reference so the risk shape is still walked.
  void (risk.ai_opinion_class as string | null);
  // Sprint 5 client feedback 2026-06-30 — Analysis Summary prose
  // must be derived exclusively from the Flag Composition (no
  // specific issues, claims, workstreams). The BE synth now emits
  // a deterministic paragraph on auditor_opinion.rationale used by
  // the Auditor Assurance Opinion card below. The prior outer
  // "Auditor narrative" paragraph that consumed this value was
  // removed in commit cca0a2c (R7 — duplicate prose), so this
  // intermediate alias is no longer needed.
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const overallSynth = synth?.risk_assessment_overall as Record<string, unknown> | undefined;
  const _rawRecs = (synth?.recommendations as Array<Record<string, unknown>> | undefined) ?? [];

  const _canonS15 = buildCanonicalFindings(risk, spaReport, ddReport);

  /* Sprint 5 client feedback 2026-07-12 — the browser NO LONGER invents recommendations.
   *
   * This block used to scan for findings the synthesizer had not covered and fabricate a
   * recommendation for each, in the browser, from a hardcoded template:
   *
   *   "Review the "<title>" finding, confirm the underlying gap with the deal team,
   *    and document a remediation or risk-acceptance plan before IC submission."
   *   "If not addressed, the underlying gap may propagate into post-close operational
   *    risk."
   *
   * On AUDIT-2026-0147 that produced R-10, R-11 and R-12: three cards that read the same
   * as each other, carried no Recommended Action (so they fell through to the generic
   * "Deal team / control owner to confirm…"), carried no Best Practice, and DUPLICATED
   * R-09, which already covered the same three findings. It also meant §15 and the Draft
   * Report disagreed — the backend never saw these invented cards, so the same finding
   * had two different recommendations depending on which panel you looked at.
   *
   * A fabricated recommendation is worse than a missing one: it looks like an answer.
   * Coverage is the synthesizer's job — _backfill_missing_recommendations() now gives
   * every uncovered finding a REAL recommendation, grounded in that finding, and the
   * page simply renders what the backend produced. */
  const recs = _rawRecs;
  /* Sprint 5 client feedback 2026-06-24 — Auditor Assurance Opinion
     payload now carries a `breakdown` field with the count/percentage
     decomposition used to drive the mechanical class. Type widened
     accordingly so the §15 card can render the "Flag Composition"
     mini-table beneath the rationale. */
  const rawAuditorOpinion = synth?.auditor_opinion as
    | {
        class: string;
        rationale: string;
        scope: string;
        opinion_basis: string;
        ai_confidence: number;
        breakdown?: {
          total_flags: number;
          critical_n: number;
          high_n: number;
          medium_n: number;
          low_n: number;
          pct_critical: number;
          pct_high: number;
          pct_medium: number;
          pct_low: number;
          pct_severe_sum: number;
          matrix_floor_applied: boolean;
        };
      }
    | undefined;

  /* Sprint 5 client feedback 2026-07-09 follow-up 8 — Flag Composition
     histogram + Auditor Opinion class are recomputed FE-side from the
     SAME canonical F-XX pool consumed by §11 / §9 / §16. Guarantees
     that §15 Flag Composition total == §11 Consolidated Findings total
     for every project, and lets the Auditor Assurance Opinion class /
     rationale be re-derived from a single source of truth. */
  const _canonBucket = (target: string): number => {
    const t = target.toLowerCase();
    return _canonS15.reduce((acc, f) => acc + (String(f.severity).toLowerCase() === t ? 1 : 0), 0);
  };
  /* Sprint 5 client feedback 2026-07-12 (issues/10 audit + Iter9 #4)
   * — Critical retired at the severity level and folds into High
   * inside _normSeverity (buildCanonicalFindings). We still poll a
   * "critical" bucket to catch any residual legacy Critical rows
   * that slipped through un-folded, but the migration guarantees
   * this returns 0 on new runs. Recommendation is a new blue-badged
   * tier below Low; it does NOT influence the Appendix B tier calc
   * (advisory-only per client spec) but is surfaced separately in
   * the §15 Flag Composition histogram + KPI tile. */
  const _feCounts = {
    critical: _canonBucket('critical'),
    high:     _canonBucket('high'),
    medium:   _canonBucket('medium'),
    low:      _canonBucket('low'),
  };
  const _feRecommendation = _canonBucket('recommendation');
  void _feRecommendation; // reserved for §15 histogram + Flag Composition display
  const _feTier = computeAnnexureBTier(_feCounts);
  const _feTotal = _feTier.flag_composition.total;
  const _pct = (n: number): number => (_feTotal > 0 ? Math.round((n / _feTotal) * 1000) / 10 : 0);
  const _feBreakdown = _feTotal > 0
    ? {
        total_flags: _feTotal,
        critical_n:  _feCounts.critical,
        high_n:      _feCounts.high,
        medium_n:    _feCounts.medium,
        low_n:       _feCounts.low,
        pct_critical: _pct(_feCounts.critical),
        pct_high:     _pct(_feCounts.high),
        pct_medium:   _pct(_feCounts.medium),
        pct_low:      _pct(_feCounts.low),
        pct_severe_sum: _pct(_feCounts.critical + _feCounts.high + _feCounts.medium),
        matrix_floor_applied: false,
      }
    : rawAuditorOpinion?.breakdown;

  // Merge BE payload with FE override. Rationale + scope + opinion_basis
  // + ai_confidence come from the BE narrative; class + breakdown come
  // from the FE histogram (when we have one).
  //
  // Sprint 5 client feedback 2026-07-09 follow-up 5 — the BE-emitted
  // rationale cites the OLD synth histogram (e.g. "10 total issues, 6
  // Critical, 1 High, 3 Medium, 0 Low") which now disagrees with the
  // FE-recomputed breakdown (12 total, 4 C / 1 H / 6 M / 1 L). Since
  // the rationale is a pure severity-distribution paragraph and MUST
  // NOT reference individual findings per the spec, we regenerate it
  // deterministically from the FE histogram when we've overridden.
  /* Sprint 5 client feedback 2026-07-12 (issues/10 audit + Iter9 #4)
   * — Critical retired at the severity level; the rationale now
   * reports the 4-tier vocabulary (High → Medium → Low →
   * Recommendation). Any residual Critical count folds into High
   * before we compose the sentence. Signature keeps `c` for
   * call-site compatibility but it's always merged into `h`. */
  const _buildDeterministicRationale = (
    c: number, h: number, m: number, lo: number,
  ): string => {
    const highTotal = c + h; // fold retired Critical tier into High
    const total = highTotal + m + lo;
    if (total === 0) return 'No findings identified across DD/SPA/Risk.';
    const parts = [`${total} total issues, comprising ${highTotal} High, ${m} Medium, and ${lo} Low findings.`];
    if (highTotal > 0) {
      parts.push('The presence of High-severity findings materially elevates the overall risk posture and requires remediation before IC submission.');
    } else if (m > 0) {
      parts.push('The severity mix is dominated by Medium findings — control gaps present but not blocking.');
    } else {
      parts.push('The severity mix reflects a comparatively well-controlled posture with only Low-tier items.');
    }
    parts.push('This summary reflects only the aggregated severity distribution shown in the Flag Composition and does not reference individual issues or findings.');
    return `The analysis identified ${parts.join(' ')}`;
  };
  const auditorOpinion = rawAuditorOpinion
    ? {
        ...rawAuditorOpinion,
        class: _feTotal > 0 ? _feTier.auditor_opinion : rawAuditorOpinion.class,
        breakdown: _feBreakdown,
        rationale: _feTotal > 0
          ? _buildDeterministicRationale(_feCounts.critical, _feCounts.high, _feCounts.medium, _feCounts.low)
          : rawAuditorOpinion.rationale,
      }
    : undefined;

  /* Severity tone of the issued verdict — drives the ladder tile tint AND the
     verdict paragraph's accent bar. */
  const _aoTone = _aoToneFor(_aoFoldClass(String(auditorOpinion?.class ?? '')));

  /* Flag pool (used elsewhere — heuristic baseline, §16 evidence). */
  const ddFlags = _flagsFromReport(ddReport);
  const spaFlags = _flagsFromReport(spaReport);
  const heuristicBaseline = String(risk.overall_risk_rating_baseline ?? _computeHeuristicBaseline(ddFlags, spaFlags));

  /* Sprint 5 client feedback 2026-06-24 — the FE-side read-time
     downgrade guardrail (introduced 2026-06-21 to catch the
     all_flags=[] synth bug) is now removed. The BE synth picks the
     class deterministically via `_classify_auditor_opinion` from the
     same flag pool the FE reads, so any disagreement between BE
     class and FE-visible flags is a BE bug to be fixed at source
     rather than masked by a read-time downgrade. Keeping the
     guardrail would mask future BE drift. */
  const overallRating = String(
    risk.overall_risk_rating
      ?? overallSynth?.rating
      ?? heuristicBaseline,
  );

  /* Sprint 5 client feedback 2026-06-24 — API breakdown table
     removed from §15 (analyst-facing report shouldn't lead with
     engineering cost telemetry). Computation kept commented out so a
     future "Run Details" subroute can resurrect it without re-deriving
     the count/cost shape:

     const apiBreakdown = (() => { ... }) reads apiCallsList and
     returns Object.entries(...) of {count, cost, success, failed}
     per api name. See git history (commit 55650a3 and earlier) for
     the prior render.
  */

  return (
    <div style={{ display: 'grid', gap: 'var(--s4)' }}>
      {/* Sprint 5 client feedback 2026-07-11 (Iter5 #20) —
          Heuristic Baseline + ISA Opinion tiles removed per client
          direction. Only the Overall Risk Rating tile survives from
          the headline row. `opinion` / `heuristicBaseline` locals
          are still computed above because downstream code (the
          Auditor Assurance Opinion ladder, the deterministic
          rationale, and the § footer) reads them; the tiles just
          don't render here anymore. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 'var(--s3)',
        }}
      >
        <div
          className="card"
          style={{
            padding: 'var(--s3) var(--s4)',
            borderLeft: `4px solid ${_ratingColour(overallRating)}`,
          }}
        >
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', fontWeight: 700 }}>
            Overall Risk Rating
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 500, color: _ratingColour(overallRating), marginTop: 4 }}>
            {overallRating}
          </div>
        </div>
      </div>

      {/* Sprint 5 client feedback 2026-06-30 (R7) — the prior
          "Auditor narrative" paragraph rendered here showed the same
          deterministic Flag-Composition prose that now renders inside
          the Auditor Assurance Opinion card below ("Issued" block).
          Two identical paragraphs in the same section was confusing
          duplication — drop the outer one. The ISA Opinion badge
          above is retained as the lone surface for the narrow ISA
          verdict; the broader analysis summary text lives once,
          inside the AAO card. */}

      {/* Auditor Assurance Opinion (8th synthesis pass) — matches the
          design's 4-level UI block at New UI Changes/risk-report.html §15. */}
      {auditorOpinion ? (
        <div
          className="card"
          style={{
            padding: 'var(--s4)',
          }}
        >
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 var(--s2) 0' }}>
            Auditor Assurance Opinion
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 'var(--s2)',
              marginBottom: 'var(--s3)',
            }}
          >
            {/* Sprint 5 client feedback 2026-07-12 (Iter9 #1) — the
                4-band ISA-style vocabulary (Effective / Improvement
                Required / Partially Effective / Ineffective) is
                retired. The Auditor Opinion now uses the Appendix B
                Audit Report Rating Matrix 5-band vocabulary — same
                strings the OIA PDF cover / gauge / summary already
                use — so a single verdict drives every report surface.
                Legacy `class` values on `auditor_opinion` are folded
                into the new bands via _AO_LEGACY_MAP so existing
                projects keep rendering without a data migration. */}
            {(
              [
                ['Unacceptable Risk Exposure',    '3+ High findings or 9+ Medium — remediate before IC submission'],
                ['Major Improvement Opportunity', '2 High findings or 6-8 Medium — significant control weaknesses'],
                ['Some Improvement Opportunity',  '1 High finding or 1-5 Medium — control gaps need remediation'],
                ['Satisfactory',                  '1 Medium finding or fewer — governance & controls operating well'],
                ['Well-Managed',                  'No High or Medium findings — best-practice controls in place'],
              ] as Array<[string, string]>
            ).map(([level, blurb]) => {
              const selected = _aoFoldClass(String(auditorOpinion.class ?? '')) === level;
              /* Sprint 5 client feedback 2026-06-24 (AUDIT-2026-0054 S-29):
                 the active tier must be the visual anchor of the ladder, not
                 a subtle border.

                 Sprint 5 client feedback 2026-07-12 — two fixes:
                 (1) The fill was AMBER for every tier, so a "Well-Managed"
                     verdict looked identical to an "Unacceptable" one. The
                     tile is now tinted BY SEVERITY, matching the report's
                     red/amber/green language everywhere else.
                 (2) The fill was solid with WHITE text, which fails contrast
                     (white on amber ≈ 3.6:1). Solid brand colours can't carry
                     white text in both themes — the dark-mode variants of
                     --fail/--watch are LIGHT. So this uses the app's standard
                     tinted-surface recipe instead: a soft tint of the tone,
                     with the tone itself as the text colour and a 2px border.
                     That is guaranteed-legible in both themes, and the tile is
                     still unmistakably the anchor (only one is coloured, at
                     full opacity, with a bold coloured heading). */
              const tone = _aoToneFor(level);
              return (
                <div
                  key={level}
                  style={{
                    padding: 'var(--s3)',
                    background: selected
                      ? `color-mix(in srgb, ${tone} 14%, var(--surface))`
                      : 'transparent',
                    border: selected
                      ? `2px solid ${tone}`
                      : '1px solid var(--lines)',
                    borderRadius: 'var(--r-sm)',
                    opacity: selected ? 1 : 0.7,
                  }}
                >
                  {/* Sprint 5 client feedback 2026-07-11 (Iter5 #21)
                      — "Issued" chip removed from the active tier
                      tile. The filled-tile treatment already
                      communicates that this tier is the AAO's
                      selected verdict. */}
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4, color: selected ? tone : 'var(--text)' }}>{level}</div>
                  <div style={{ fontSize: '0.72rem', color: selected ? 'var(--text-sec)' : 'var(--muted)', lineHeight: 1.4 }}>{blurb}</div>
                </div>
              );
            })}
          </div>
          {/* The verdict paragraph's accent bar follows the SAME severity tone
              as the selected tile above — it was hardcoded amber, so it
              disagreed with its own verdict on every tier but one. */}
          <div
            style={{
              padding: 'var(--s3) var(--s4)',
              background: 'var(--surface-alt)',
              borderLeft: `3px solid ${_aoTone}`,
              borderRadius: 'var(--r-sm)',
              fontSize: '0.88rem',
              lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4, color: _aoTone }}>{auditorOpinion.class}</div>
            {/* Sprint 5 client feedback 2026-07-03 — rationale may
                include LLM-emitted markdown; FormattedText handles
                **bold** / bullets without leaving literal markers. */}
            <FormattedText text={auditorOpinion.rationale} />
          </div>

          {/* Sprint 5 client feedback 2026-06-24 — Flag Composition panel:
              shows the count + percentage breakdown the deterministic
              classifier used to pick the class. Transparency about the
              "by the numbers" derivation lets analysts audit the
              opinion against the §5 Risk Matrix without re-counting. */}
          {auditorOpinion.breakdown && auditorOpinion.breakdown.total_flags > 0 && (
            <div
              style={{
                marginTop: 'var(--s3)',
                padding: 'var(--s3) var(--s4)',
                background: 'var(--surface)',
                border: '1px solid var(--lines)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--muted)',
                  fontWeight: 700,
                  marginBottom: 'var(--s2)',
                }}
              >
                Flag Composition · {auditorOpinion.breakdown.total_flags} total flags
              </div>
              <div className="table-wrap">
                <table
                  className="data-table"
                  aria-label="Flag composition driving the auditor opinion"
                  style={{ fontSize: '0.82rem' }}
                >
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th className="num">Count</th>
                      {/* Iter5 #22 — column header shortened. */}
                      <th className="num">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span className="rsk-count rsk-count-crit">Critical</span></td>
                      <td className="num t-mono">{auditorOpinion.breakdown.critical_n}</td>
                      <td className="num t-mono">{auditorOpinion.breakdown.pct_critical}%</td>
                    </tr>
                    <tr>
                      <td><span className="rsk-count rsk-count-high">High</span></td>
                      <td className="num t-mono">{auditorOpinion.breakdown.high_n}</td>
                      <td className="num t-mono">{auditorOpinion.breakdown.pct_high}%</td>
                    </tr>
                    <tr>
                      <td><span className="rsk-count rsk-count-med">Medium</span></td>
                      <td className="num t-mono">{auditorOpinion.breakdown.medium_n}</td>
                      <td className="num t-mono">{auditorOpinion.breakdown.pct_medium}%</td>
                    </tr>
                    <tr>
                      <td><span className="rsk-count rsk-count-low">Low</span></td>
                      <td className="num t-mono">{auditorOpinion.breakdown.low_n}</td>
                      <td className="num t-mono">{auditorOpinion.breakdown.pct_low}%</td>
                    </tr>
                    <tr style={{ background: 'var(--surface-alt)' }}>
                      {/* Sprint 5 client feedback 2026-06-30 — bottom row
                          includes Low so the formula matches what the
                          user expects to read: the full flag pool
                          (DD §11 + SPA §05 + DD Overall Risk
                          assessment, with Watch normalised to Low).
                          The classifier still uses the internal
                          severe-sum (C+H+M) for tier thresholds — only
                          the DISPLAY formula changes here. */}
                      <td><b>Severe sum (C + H + M + L)</b></td>
                      <td className="num t-mono">
                        {auditorOpinion.breakdown.critical_n
                          + auditorOpinion.breakdown.high_n
                          + auditorOpinion.breakdown.medium_n
                          + auditorOpinion.breakdown.low_n}
                      </td>
                      <td className="num t-mono">
                        <b>{(
                          auditorOpinion.breakdown.pct_critical
                          + auditorOpinion.breakdown.pct_high
                          + auditorOpinion.breakdown.pct_medium
                          + auditorOpinion.breakdown.pct_low
                        ).toFixed(1)}%</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 'var(--s2)', lineHeight: 1.5 }}>
                Aggregated across DD §11 (Flags &amp; Action Items), SPA §05 (Issue
                Identification &amp; Classification), and the DD §5 Overall Investment
                Risk verdict (contributes +1 flag at its assigned severity). Class
                picked by deterministic threshold matrix from these percentages.
                {auditorOpinion.breakdown.matrix_floor_applied && (
                  <>
                    {' '}
                    <b>Audit-convention floor applied:</b> any Critical lifts the minimum tier to Partially Effective.
                  </>
                )}
              </p>
              {/* Sprint 5 client feedback 2026-07-12 (issues/11 §15
                  Appendix B) — display the Audit Report Rating Matrix
                  below the Flag Composition so reviewers can see the
                  criteria the AI used to derive the Auditor Opinion.
                  The currently selected tier is highlighted so the
                  matrix acts as a legend, not just a static table.
                  Numbers mirror utils/annexureBTier.ts single source
                  of truth. */}
              {(() => {
                /* issues/11 follow-up — auditor_opinion.class now
                 * carries the OIA tier name directly (annexureBTier
                 * update). Reverse map is identity for post-Iter10
                 * payloads; legacy Ineffective / Partially Effective
                 * / Improvement Required / Effective aliases still
                 * resolve for backwards compat with older runs. */
                const _tierMap: Record<string, string> = {
                  'Unacceptable Risk Exposure':    'Unacceptable Risk Exposure',
                  'Major Improvement Opportunity': 'Major Improvement Opportunity',
                  'Some Improvement Opportunity':  'Some Improvement Opportunity',
                  'Satisfactory':                  'Satisfactory',
                  'Well-Managed':                  'Well-Managed',
                  'Ineffective':                   'Unacceptable Risk Exposure',
                  'Partially Effective':           'Major Improvement Opportunity',
                  'Improvement Required':          'Some Improvement Opportunity',
                  'Effective':                     'Satisfactory',
                  'Effective (Well-Managed)':      'Well-Managed',
                };
                const _activeTier = _tierMap[auditorOpinion.class] ?? '';
                const _matrix: Array<{ tier: string; high: string; medium: string }> = [
                  { tier: 'Unacceptable Risk Exposure',    high: '3 or more',  medium: '9 or more' },
                  { tier: 'Major Improvement Opportunity', high: '2',          medium: '6 to 8' },
                  { tier: 'Some Improvement Opportunity',  high: '1',          medium: 'Up to 5' },
                  { tier: 'Satisfactory',                  high: '1',          medium: 'Up to 3' },
                  { tier: 'Well-Managed',                  high: '—',          medium: '1' },
                ];
                return (
                  <div style={{ marginTop: 'var(--s3)' }}>
                    <div
                      style={{
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'var(--muted)',
                        fontWeight: 700,
                        marginBottom: 'var(--s2)',
                      }}
                    >
                      Appendix B · Audit Report Rating Matrix
                    </div>
                    <div className="table-wrap">
                      <table
                        className="data-table"
                        aria-label="Appendix B Audit Report Rating Matrix"
                        style={{ fontSize: '0.82rem' }}
                      >
                        <thead>
                          <tr>
                            <th>Overall Rating</th>
                            <th className="num">High</th>
                            <th className="num">Medium</th>
                          </tr>
                        </thead>
                        <tbody>
                          {_matrix.map((row) => {
                            const isActive = row.tier === _activeTier;
                            return (
                              <tr
                                key={row.tier}
                                style={isActive ? {
                                  background: 'color-mix(in srgb, var(--info) 12%, var(--surface))',
                                  fontWeight: 600,
                                } : undefined}
                              >
                                <td>
                                  {row.tier}
                                  {isActive && (
                                    <span
                                      className="badge badge-info"
                                      style={{ marginLeft: 8, fontSize: '0.62rem' }}
                                    >
                                      Applied
                                    </span>
                                  )}
                                </td>
                                <td className="num t-mono">{row.high}</td>
                                <td className="num t-mono">{row.medium}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: 'var(--s2)', lineHeight: 1.5 }}>
                      Notes: (1) High is equivalent to 3 Medium risk observations. (2) Low
                      risk observations are not considered in the overall rating.
                      (3) Matrix is indicative only.
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Sprint 5 client feedback 2026-07-11 (Iter5 #23) —
              dashed-bordered footer block (Scope / Opinion Basis /
              AI Confidence / Issued By / Version) removed as
              unnecessary metadata clutter. The `auditorOpinion`,
              `projectCreatedAt` locals stay in scope for future
              consumers but no longer render as a footer. */}
          {void projectCreatedAt}
          <p
            style={{
              marginTop: 'var(--s3)',
              fontSize: '0.78rem',
              color: 'var(--muted)',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            <b>Note on scope:</b> Internal Audit expresses assurance on the
            effectiveness of governance, risk management and internal controls
            — it does not approve or reject the investment. The investment
            decision rests with the Investment Committee.
          </p>
        </div>
      ) : (
        <Pending note="Auditor Assurance Opinion pending — re-run Risk Assessment to generate the 8th synthesis pass." />
      )}

      {/* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6):
          Recommendations rendered as priority-coloured cards instead
          of a table. Each card carries a left-border priority stripe,
          Ref + Priority chip on the top row, the recommendation in
          larger type, and an "If not addressed" tinted callout below
          for the implication. Cards ordered Critical → High → Medium
          → Low priority. */}
      {recs.length > 0 ? (
        <div>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 var(--s2) 0' }}>
            Recommendations &amp; Implications
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 var(--s4) 0', fontStyle: 'italic' }}>
            Each recommendation has its implication for the deal's strategic and
            financial objectives. Management action items are added once management
            has reviewed and agreed the findings.
          </p>
          {(() => {
            // Sprint 5 client feedback 2026-06-24 (2nd pass) —
            // recommendations numbering was still out-of-order on
            // the user's screen because the array .sort() on ties
            // wasn't deterministic across browsers/runtimes. Now:
            //   1. Decorate each rec with its original index +
            //      priority rank.
            //   2. Sort by (priority rank ASC, original index ASC)
            //      so within the same priority, the synth's emit
            //      order is preserved.
            //   3. Re-number sequentially R-01..R-N from display
            //      position.
            const prioOrder: Record<string, number> = {
              critical: 0, high: 1, medium: 2, low: 3,
              // Tolerate alternate spellings the synth might emit
              info: 3, low_priority: 3, '': 99,
            };
            const sortedRecs = recs
              .map((r, originalIdx) => ({
                rec: r,
                originalIdx,
                rank: prioOrder[String(r.priority ?? 'Medium').toLowerCase().trim()] ?? 99,
              }))
              .sort((a, b) => {
                if (a.rank !== b.rank) return a.rank - b.rank;
                return a.originalIdx - b.originalIdx;
              })
              .map((entry) => entry.rec);
            /* Sprint 5 client feedback 2026-07-09 follow-up 10 —
               recommendation chips were rendering the LLM synth's raw
               finding_id (e.g. "SPA-CLAUSE-CLAUSE_ANTIDILUTION_TYPE")
               instead of the user-friendly F-XX ID. Build a lookup
               keyed by every known alias (original_id, attribute_key,
               attribute_key without the ``clause_`` prefix) → the
               canonical F-XX display_id, then translate any inbound
               finding_id / for_finding_id / ref before we render it. */
            const _canonIdMap: Record<string, string> = {};
            for (const f of _canonS15) {
              _canonIdMap[f.display_id.toLowerCase()] = f.display_id;
              if (f.original_id) _canonIdMap[f.original_id.toLowerCase()] = f.display_id;
              if (f.attribute_key) {
                const ak = String(f.attribute_key).toLowerCase();
                _canonIdMap[ak] = f.display_id;
                if (ak.startsWith('clause_')) {
                  _canonIdMap[`spa-clause-${ak.toUpperCase().toLowerCase()}`] = f.display_id;
                  _canonIdMap[`spa-clause-${ak.replace(/^clause_/, '').toLowerCase()}`] = f.display_id;
                }
              }
            }
            const _toDisplayId = (raw: unknown): string => {
              const s = String(raw ?? '').trim();
              if (!s) return '';
              const hit = _canonIdMap[s.toLowerCase()];
              return hit ?? '';  // drop the chip if we can't map — never leak a raw internal id
            };
            /* Sprint 5 client feedback 2026-07-12 (issues/11 §15 Recs) —
             * severity of each Recommendation card must be INHERITED
             * from its linked §11 canonical finding, not sourced from
             * the LLM's own priority field. Build a map keyed by every
             * alias the recommendation might carry so we can resolve
             * finding_id / for_finding_id / attribute_key → the
             * canonical finding, then read its (Iter9-normalised)
             * severity. Falls back to the LLM's priority when no link
             * resolves. */
            const _canonFindingByKey: Record<string, CanonicalFinding> = {};
            for (const f of _canonS15) {
              _canonFindingByKey[f.display_id.toLowerCase()] = f;
              if (f.original_id) _canonFindingByKey[f.original_id.toLowerCase()] = f;
              if (f.attribute_key) _canonFindingByKey[String(f.attribute_key).toLowerCase()] = f;
            }
            const _resolveLinkedFinding = (r: Record<string, unknown>): CanonicalFinding | null => {
              for (const raw of [r.finding_id, r.for_finding_id, r.attribute_key]) {
                const s = String(raw ?? '').trim().toLowerCase();
                if (s && _canonFindingByKey[s]) return _canonFindingByKey[s];
              }
              return null;
            };
            /* Iter9 #4 + issues/11 §15 Recs — 4-tier colour spec:
             *   High           → Red    (--fail)
             *   Medium         → Amber  (--watch)
             *   Low            → Green  (--pass)
             *   Recommendation → Blue   (--info)
             * Critical folds into High. Previous mapping had Medium=blue
             * and Recommendation absent; both broken by the client's
             * severity retirement spec. */
            const prioColor = (p: string): string => {
              const s = p.toLowerCase();
              if (s === 'critical' || s === 'high') return 'var(--fail)';
              if (s === 'medium') return 'var(--watch)';
              if (s === 'low') return 'var(--pass)';
              if (s === 'recommendation') return 'var(--info)';
              return 'var(--muted)';
            };
            /* Iter9 fold: any residual "Critical" ever displayed to
             * the user renders as "High" per the retirement spec. */
            const _foldPriorityDisplay = (p: string): string => {
              const s = String(p ?? '').trim();
              return s.toLowerCase() === 'critical' ? 'High' : (s || 'Medium');
            };
            return (
              <div style={{ display: 'grid', gap: 'var(--s3)' }}>
                {sortedRecs.map((r, i) => {
                  // Sprint 5 client feedback 2026-06-24
                  // (AUDIT-2026-0054 S-20): recommendations were
                  // labelled with the synth-emitted r.ref (R-01,
                  // R-02, ...) which reflected the LLM's original
                  // emit order, not the post-sort display order. So
                  // after sorting by priority the cards appeared
                  // R-01 → R-02 → R-05 → R-03 → R-04. Re-number
                  // strictly sequential from 1 based on display
                  // position; preserve the original ref as a small
                  // grey aside so analysts cross-referencing the
                  // synth output don't lose the mapping.
                  void r.ref;
                  const ref = `R-${String(i + 1).padStart(2, '0')}`;
                  /* issues/11 §15 Recs — inherit severity from linked
                   * §11 finding. Falls back to LLM-emitted priority if
                   * the finding link can't be resolved. Critical folds
                   * to High for display via _foldPriorityDisplay. */
                  const _linkedFinding = _resolveLinkedFinding(r as Record<string, unknown>);
                  const _rawPriority = _linkedFinding
                    ? _linkedFinding.severity
                    : String(r.priority ?? 'Medium');
                  const priority = _foldPriorityDisplay(_rawPriority);
                  /* Drop the trailing "— Closure: <test>" from the headline.
                     It is REDUNDANT on this card: the AI Recommended Actions block ends
                     with a verification step that the prompt requires to agree with this
                     very closure test, so the same sentence was being printed twice —
                     "Closure: the executed SPA contains a signed provision" above, and
                     "the auditor must inspect the signed SPA … to confirm closure" below.
                     The closure test still exists in the data (the PDF and the audit trail
                     rely on it); it just is not repeated to the reader. */
                  const recommendation = String(r.recommendation ?? '—')
                    .replace(/\s*[—–-]\s*Closure\s*:.*$/is, '')
                    .trim() || '—';
                  const implication = String(r.implication ?? '');
                  // Sprint 5 client feedback 2026-06-24 — surface
                  // the finding_id link the synth now emits so the
                  // reader can verify each recommendation ties back
                  // to a real finding in §11. Rendered as a chip
                  // anchor that scrolls to the finding's card.
                  //
                  // 2026-07-09 follow-up 10 — the LLM synth emits the
                  // raw pool-native id (F-XX for its own pool, but
                  // SPA-CLAUSE-* for Legal rows the SPA §5 adapter
                  // injects). Translate through the canonical map so
                  // the chip always shows the user-facing F-XX label.
                  const findingId = _toDisplayId(
                    r.finding_id ?? r.for_finding_id ?? r.attribute_key,
                  );
                  const accent = prioColor(priority);
                  return (
                    <article
                      key={i}
                      style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--lines)',
                        borderLeft: `4px solid ${accent}`,
                        borderRadius: 'var(--r-md)',
                        padding: 'var(--s4) var(--s5)',
                        display: 'grid',
                        gap: 'var(--s3)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)', flexWrap: 'wrap' }}>
                        <span className="t-mono t-xs" style={{ fontWeight: 600, color: 'var(--text-sec)' }}>{ref}</span>
                        <span className={_priorityBadgeClass(priority)}>{priority}</span>
                        {findingId && (
                          /* The chip is only a link when the finding actually
                             resolves to a card in §11 — otherwise the anchor
                             would be dead and the click would do nothing. */
                          _linkedFinding ? (
                            <a
                              href={`#${findingAnchorId(findingId)}`}
                              className="badge badge-neutral t-xs"
                              style={{ textDecoration: 'none', fontFamily: 'var(--font-mono)' }}
                              data-tip={`Closes ${findingId} — go to it in Section 11 Consolidated Findings`}
                            >
                              Closes {findingId}
                            </a>
                          ) : (
                            <span
                              className="badge badge-neutral t-xs"
                              style={{ fontFamily: 'var(--font-mono)' }}
                              data-tip={`Closes ${findingId} — See Section 11 Consolidated Findings`}
                            >
                              Closes {findingId}
                            </span>
                          )
                        )}
                      </div>
                      {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) —
                          Recommendation prose + implication + management
                          action all carry LLM-emitted **bold** markup;
                          route through FormattedText. */}
                      <div style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                        <FormattedText text={recommendation} />
                      </div>
                      {implication && implication !== '—' && (
                        <div
                          style={{
                            padding: 'var(--s3) var(--s4)',
                            background: `color-mix(in srgb, ${accent} 6%, var(--surface-alt))`,
                            borderLeft: `2px solid ${accent}`,
                            borderRadius: 'var(--r-sm)',
                            fontSize: '0.82rem',
                            lineHeight: 1.5,
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.625rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              fontWeight: 700,
                              color: 'var(--text-sec)',
                              marginBottom: 'var(--s1)',
                            }}
                          >
                            If not addressed
                          </div>
                          <FormattedText text={implication} inline />
                        </div>
                      )}
                      {/* Sprint 5 client feedback 2026-07-12 — the market standard comes
                          FIRST, then what this deal team must do about it. The reader
                          judges the proposed action against the benchmark, rather than
                          reading the action and only afterwards learning what peers do.

                          Rendered ONLY when the pass produced it. No fallback sentence —
                          a generic line on every card is precisely the failure that made
                          "Recommended Action" worthless for months. */}
                      {Boolean((r as Record<string, unknown>).best_practice) && (
                        <div
                          style={{
                            padding: 'var(--s3) var(--s4)',
                            background: 'var(--surface-alt)',
                            borderLeft: '2px solid var(--pass)',
                            borderRadius: 'var(--r-sm)',
                            fontSize: '0.82rem',
                            lineHeight: 1.5,
                          }}
                        >
                          <div
                            className="rsk-rec-label"
                            data-tip={
                              'How globally recognised institutional investors — sovereign wealth\n'
                              + 'funds, large pension funds, major LPs — typically address this class\n'
                              + 'of risk during due diligence and transaction execution.'
                            }
                          >
                            {/* "sovereign fund" carries the accent: it is the specific claim
                                the block makes, and the rest of the label is scaffolding. */}
                            Global <span className="rsk-rec-label-hl">sovereign fund</span> best practices
                          </div>
                          {/* A real <ul>. The synthesizer emits three "- " lines, but
                              FormattedText renders them as paragraphs, so the markers were
                              lost and three separate practices read as one run-on block. */}
                          <ul className="rsk-bp-list">
                            {String((r as Record<string, unknown>).best_practice)
                              .split('\n')
                              .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
                              .filter(Boolean)
                              .map((line, bi) => (
                                <li key={bi}>
                                  <FormattedText text={line} inline />
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                      {/* AI Recommended Action — the synthesizer's numbered remediation
                          plan for THIS finding, read against the market standard above.
                          Labelled "AI" so the reader knows a model wrote it and the
                          analyst still owns it. */}
                      <div
                        style={{
                          padding: 'var(--s3) var(--s4)',
                          background: 'var(--surface-alt)',
                          borderLeft: '2px solid var(--info)',
                          borderRadius: 'var(--r-sm)',
                          fontSize: '0.82rem',
                          lineHeight: 1.5,
                        }}
                      >
                        <div className="rsk-rec-label">
                          {/* Iter5 #24 — the label previously implied management had
                              already agreed; renamed to Recommended Action. 2026-07-12 —
                              now "AI Recommended Action", to name its author. */}
                          <span className="rsk-rec-label-hl">AI</span> Recommended Actions
                        </div>
                        {/* Not `inline` — the plan is multi-step and its steps must
                            render as steps. */}
                        <FormattedText
                          text={String(
                            (r as Record<string, unknown>).recommended_action
                            ?? (r as Record<string, unknown>).management_action
                            ?? (r as Record<string, unknown>).action
                            ?? 'Deal team / control owner to confirm remediation plan and target close-out date.',
                          )}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            );
          })()}
        </div>
      ) : (
        <Pending note="Recommendations & Implications pending — re-run Risk Assessment to generate the 7th synthesis pass." />
      )}

      {/* Sprint 5 client feedback 2026-06-24 — the audit-doc metadata
          (Scope / Opinion Basis / AI Confidence / Issued By / Version)
          renders ABOVE inside the Auditor Assurance Opinion subsection.
          A second copy at the bottom of §15 was redundant. Run
          telemetry (Triggered / Completed / External Calls breakdown)
          dropped entirely per the same feedback — analyst-facing
          report shouldn't lead with engineering cost telemetry. */}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-30 (New Issues R3) — §17 used to
   render a flat 46-row table mixing all field types. The new layout
   per the design spec organises it into 5 structured subsections:
     1. Financials — 3-col data table
     2. Cap Table — horizontal stacked bar chart
     3. Deal Terms — 2-col key-value table
     4. Sales Projection — single-row time-series table
     5. Market Forecast — two-row time-series table
   Each subsection pulls from the existing captured_data.tables rows
   by matching field names; the flat layout was losing visual
   hierarchy and dropping time-series structure. */

const _CAP_PALETTE = ['#1E40AF', '#1D4ED8', '#A16207', '#CA8A04', '#6B7280', '#9CA3AF'];

function _findCapturedRow(rows: Record<string, unknown>[], patterns: string[]): Record<string, unknown> | null {
  for (const row of rows) {
    const field = String(row.field ?? '').toLowerCase();
    if (patterns.some((p) => field.includes(p.toLowerCase()))) return row;
  }
  return null;
}

function CapturedDataCaveatBanner(): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--watch-bg)',
        borderLeft: '4px solid var(--watch)',
        padding: 'var(--s3) var(--s4)',
        borderRadius: 'var(--r-md)',
        marginBottom: 'var(--s4)',
        fontSize: '0.875rem',
      }}
    >
      <strong style={{ color: 'var(--watch)' }}>⚠ Caveat:</strong>{' '}
      These are private or management-provided figures extracted for the auditor's
      reference. They are unaudited and not independently verifiable; confirm
      against primary documents before reliance.
    </div>
  );
}

function _SubsectionCard({
  title, footnote, children,
}: { title: string; footnote?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="card"
      style={{
        padding: 'var(--s4)',
        marginBottom: 'var(--s4)',
      }}
    >
      <p style={{
        margin: 0, marginBottom: 'var(--s3)',
        fontSize: '0.75rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--muted)',
      }}>{title}</p>
      {children}
      {footnote && (
        <p style={{
          marginTop: 'var(--s3)', marginBottom: 0,
          fontSize: '0.75rem', color: 'var(--muted)', fontStyle: 'italic',
        }}>{footnote}</p>
      )}
    </div>
  );
}

function CapturedData({ dd }: { dd: Record<string, unknown> }): JSX.Element {
  const captured =
    (getNested(dd, 'captured_data.tables') as Record<string, unknown>[]) ?? [];
  if (captured.length === 0) {
    return <Pending note="No captured document tables — DD JSON missing captured_data section." />;
  }

  // Pull the source rows for each subsection from the flat list.
  const revenueRow = _findCapturedRow(captured, ['revenue', 'sales / revenue']);
  const ebitdaRow = _findCapturedRow(captured, ['ebitda']);
  const capRow = _findCapturedRow(captured, ['cap table', 'shareholder']);
  const dealSizeRow = _findCapturedRow(captured, ['deal size', 'oia equity']);
  // Sprint 5 client feedback 2026-06-30 — drop the 'lead investor'
  // alias. Total Round Size and Lead Investor are conceptually
  // distinct rows; _findCapturedRow does a substring includes() match
  // and "Lead Investor" was winning ahead of the real "Total Round
  // Size" row, so the Deal Terms panel rendered investor names
  // (e.g. "Fortescue Future Industries ...") in the Total Round Size
  // slot instead of the $USD-M value the user expected.
  const totalRoundRow = _findCapturedRow(captured, ['total round']);
  const irrRow = _findCapturedRow(captured, ['projected irr', 'irr']);
  // Sprint 5 client feedback 2026-06-30 (R2) — drop the 'multiple'
  // alias. _findCapturedRow does a substring includes() match and
  // 'multiple' was greedily catching the "Terminal EV / EBITDA Multiple"
  // row (e.g. 6.27x) ahead of the actual "Money Multiple (MoM)" row
  // (3.6x), so §17 Deal Terms rendered the wrong figure. Same fix
  // pattern as commit bbb8796 for Total Round Size.
  const multipleRow = _findCapturedRow(captured, ['money multiple', 'mom']);
  const holdingRow = _findCapturedRow(captured, ['holding period']);
  const marketRow = _findCapturedRow(captured, ['market forecast', 'market size', 'market growth']);

  // SUBSECTION 1: Financials — present the two key projection rows
  // in a single compact table.
  const financialMetrics: Array<[string, Record<string, unknown> | null]> = [
    ['Revenue / Sales (projected)', revenueRow],
    ['EBITDA (projected)', ebitdaRow],
  ];
  const hasFinancials = financialMetrics.some(([, r]) => r && r.value_kind !== 'missing');

  // SUBSECTION 2: Cap Table — parse the shareholder structure into
  // segments for a horizontal stacked bar. The value_raw may be a
  // list of {holder, pct} or a string; render best-effort.
  //
  // Sprint 5 client feedback 2026-07-02 — when percentages aren't
  // extractable but we DO have investor names (typical when §17.2
  // is scavenged from lead_investors / strategic_partners lists
  // via the BE fallback in risk_assessment.py), also expose the
  // name-only rows so §17.2 renders a plain shareholder list
  // instead of the "not captured" caveat. capNamesOnly powers a
  // secondary list rendered when capSegments is empty.
  type _CapRecord = { holder: string; pct: number | null; class?: string; sourceDoc?: string; sourcePage?: string | number };
  const capRecords: _CapRecord[] = (() => {
    if (!capRow) return [];
    const raw = capRow.value_raw;
    if (!Array.isArray(raw)) return [];
    const recs: _CapRecord[] = [];
    for (const it of raw) {
      if (!it || typeof it !== 'object') continue;
      const rec = it as Record<string, unknown>;
      const holder = String(rec.holder ?? rec.shareholder ?? rec.name ?? '').trim();
      if (!holder || holder === '—') continue;
      const pctRaw = rec.pct ?? rec.percentage ?? rec.percent ?? rec.share;
      const pct = typeof pctRaw === 'number'
        ? pctRaw
        : (pctRaw != null && !Number.isNaN(parseFloat(String(pctRaw)))
            ? parseFloat(String(pctRaw))
            : null);
      recs.push({
        holder,
        pct,
        class: rec.class ? String(rec.class) : undefined,
        sourceDoc: rec.source_doc ? String(rec.source_doc) : undefined,
        sourcePage: (rec.source_page as string | number | undefined),
      });
    }
    return recs;
  })();
  const capSegments: Array<{ holder: string; pct: number }> =
    capRecords
      .filter((r) => r.pct != null && r.pct > 0)
      .map((r) => ({ holder: r.holder, pct: r.pct as number }));
  const capNamesOnly: _CapRecord[] = capRecords.filter((r) => r.pct == null || !(r.pct > 0));

  // SUBSECTION 3: Deal Terms — 2-col key-value
  // Sprint 5 client feedback 2026-06-30 (R8) — cosmetic cleanups:
  //   - OIA Equity Check was showing the LLM's verbose extracted
  //     prose ("Investor commitment: US$ 30M | Total round: US$ 350M
  //     ..."); compress to the first dollar figure when present.
  //   - "Projected IRR" was showing the value "projected 29%" (LLM
  //     prepended its own "projected" prefix even though the label
  //     already says so). Strip the leading "projected " token if it
  //     appears so the value reads cleanly as "29%".
  const _firstDollarToken = (s: string): string => {
    const m = s.match(/US\$\s*[\d.,]+\s*[MB]?|\$\s*[\d.,]+\s*[MB]?/i);
    return m ? m[0].replace(/\s+/g, ' ').trim() : s;
  };
  const _stripProjectedPrefix = (s: string): string =>
    s.replace(/^\s*(projected|projection)\s*[:\-]?\s*/i, '').trim();
  const _dealSizeRaw = String(dealSizeRow?.value ?? '—');
  const _irrRaw = String(irrRow?.value ?? '—');
  const dealTerms: Array<[string, string]> = [
    ['OIA Equity Check', _dealSizeRaw === '—' ? '—' : _firstDollarToken(_dealSizeRaw)],
    ['Total Round Size', String(totalRoundRow?.value ?? '—')],
    ['Projected IRR', _irrRaw === '—' ? '—' : _stripProjectedPrefix(_irrRaw)],
    ['Money Multiple', String(multipleRow?.value ?? '—')],
    ['Holding Period', String(holdingRow?.value ?? '—')],
  ];

  // SUBSECTION 4: Sales Projection — time-series. If revenue row's
  // value_raw is a year→value map, render as one row across columns.
  //
  // Sprint 5 client feedback 2026-07-02 — the value_raw year map is
  // now often scavenged from §6 assumption prose (see
  // backend/src/api/routes/risk_assessment.py:_scavenge_revenue_year_map).
  // Those values arrive as raw numbers in USD millions ({"2024": 24}).
  // Format them as "$24M" so the cell reads naturally and matches the
  // §17 currency convention used elsewhere in the section.
  const salesYears: Array<[string, string]> = (() => {
    if (!revenueRow) return [];
    const raw = revenueRow.value_raw;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const entries = Object.entries(raw as Record<string, unknown>)
        .filter(([k]) => /^\d{4}$/.test(k))
        .sort(([a], [b]) => Number(a) - Number(b));
      return entries.map(([y, v]) => {
        const n = Number(v);
        const cell =
          Number.isFinite(n)
            ? `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}M`
            : String(v);
        return [y, cell];
      });
    }
    return [];
  })();

  // SUBSECTION 5: Market Forecast — unified Metric × Value × Source
  // rows aggregated from:
  //   (a) market_forecasts items on marketRow.value_raw (each item is
  //       a {field, claimed_value, year?, page_number?} record from
  //       the DD extractor's forecast-slide pass)
  //   (b) the 4 canonical scalar attributes (market_size_usd_m,
  //       market_growth_rate_pct, estimated_market_share_pct,
  //       market_indicators) — the same fields §13-C verifies.
  //
  // Sprint 5 client feedback 2026-07-02 — §17.5 is the internal /
  // management-provided view (no verification, matches §17.4 Sales
  // Projection shape). §13-C is the verification view. Two sections,
  // one dataset, different purposes. Layout follows Layout B from
  // the spec (Metric | Value | Source) so time-series and single-
  // metric rows coexist naturally — the year context can travel in
  // the metric label ("Global TAM (2028)") when needed.
  type _MarketMetric = {
    metric: string;
    value: string;
    sourceDoc?: string;
    sourcePage?: number | string;
  };
  const _fmtSource = (doc?: string, page?: number | string): string | undefined => {
    if (!doc || doc === '—') return undefined;
    return page != null && page !== '' ? `${doc} · p.${page}` : doc;
  };
  // Sprint 5 client feedback 2026-07-02 — Bug 3 safety net. Any
  // Python dict-str shape ("{'2024': 24, '2025': 291}") that slips
  // past the BE sanitizer gets rewritten client-side as
  // "2024: 24 | 2025: 291" so the Value cell never leaks braces /
  // quotes / colons to the analyst.
  const _sanitizeDictStr = (v: string): string => {
    if (!v || v.indexOf('{') < 0 || v.indexOf('}') < 0) return v;
    const trimmed = v.trim();
    if (!/^\{.*\}$/.test(trimmed)) return v;
    const inner = trimmed.slice(1, -1).trim();
    if (!inner) return v;
    const parts: string[] = [];
    for (const pair of inner.split(',')) {
      const idx = pair.indexOf(':');
      if (idx < 0) continue;
      const k = pair.slice(0, idx).trim().replace(/^['"]|['"]$/g, '').trim();
      const val = pair.slice(idx + 1).trim().replace(/^['"]|['"]$/g, '').trim();
      if (!k && !val) continue;
      parts.push(k ? `${k}: ${val}` : val);
    }
    return parts.length ? parts.join(' | ') : v;
  };
  const marketMetrics: _MarketMetric[] = (() => {
    const out: _MarketMetric[] = [];
    const seen = new Set<string>();
    const push = (m: _MarketMetric) => {
      const key = `${m.metric.toLowerCase()}|${m.value.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(m);
    };
    // (a) forecast-item expansion — marketRow.value_raw carries the
    //     market_forecasts list when the DD extractor landed on the
    //     forecast slide.
    if (marketRow && Array.isArray(marketRow.value_raw)) {
      for (const it of marketRow.value_raw) {
        if (!it || typeof it !== 'object') continue;
        const rec = it as Record<string, unknown>;
        const metricRaw = String(rec.field ?? rec.metric ?? '').trim();
        const value = _sanitizeDictStr(String(rec.claimed_value ?? rec.value ?? '').trim());
        if (!value) continue;
        const yearStr = rec.year != null ? String(rec.year).trim() : '';
        // Prepend year to the metric label when the record has both
        // (matches Layout A → Layout B collapse in the spec).
        const metric = metricRaw
          ? (yearStr && !metricRaw.includes(yearStr)
              ? `${metricRaw} (${yearStr})`
              : metricRaw)
          : (yearStr ? `Market forecast (${yearStr})` : 'Market forecast');
        const page = (rec.page_number ?? rec.source_page) as
          | number | string | undefined;
        push({
          metric,
          value,
          sourceDoc: (marketRow.source_doc as string | undefined),
          sourcePage: page ?? (marketRow.source_page as number | undefined),
        });
      }
    }
    // (b) canonical scalars — pull each captured_data row whose
    //     field label matches one of the 4 canonical market attrs.
    //     Rows with value_kind === 'missing' are skipped so §17.5
    //     doesn't fill up with "Not extracted" placeholders (those
    //     live in §13-C where "not verified" is the point).
    const canonicalKeys: Array<[string[], string]> = [
      [['market size (usd m)', 'market size'], 'Market size (USD M)'],
      [['market growth rate (%)', 'market growth'], 'Market growth rate (CAGR)'],
      [['estimated market share (%)', 'market share'], 'Estimated market share'],
      [['industry indicators', 'market indicator'], 'Industry indicators'],
    ];
    for (const [aliases, displayLabel] of canonicalKeys) {
      const row = _findCapturedRow(captured, aliases);
      if (!row) continue;
      if (row.value_kind === 'missing') continue;
      const value = _sanitizeDictStr(String(row.value ?? '').trim());
      if (!value || value === '—') continue;
      push({
        metric: String(row.field ?? displayLabel),
        value,
        sourceDoc: row.source_doc as string | undefined,
        sourcePage: row.source_page as number | undefined,
      });
    }
    return out;
  })();

  return (
    <>
      <CapturedDataCaveatBanner />

      {/* SUBSECTION 1: Financials */}
      <_SubsectionCard
        title="1 · Financials — management-provided, unaudited"
        footnote={hasFinancials ? undefined : 'Financial projections not captured in source documents.'}
      >
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th style={{ width: '50%' }}>Metric</th><th>Value</th><th>Source</th></tr></thead>
            <tbody>
              {financialMetrics.map(([label, row]) => (
                <tr key={label}>
                  <td className="td-name">{label}</td>
                  <td className="t-mono">{row?.value_kind === 'missing' || !row ? '—' : String(row.value ?? '—')}</td>
                  <td className="t-xs t-muted">
                    {row?.source_doc && row.source_doc !== '—'
                      ? `${row.source_doc}${row.source_page != null ? ` · p.${row.source_page}` : ''}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </_SubsectionCard>

      {/* SUBSECTION 2: Cap Table */}
      <_SubsectionCard
        title="2 · Cap Table — shareholder structure"
        footnote={
          capSegments.length > 0
            ? `${capSegments.length} holder${capSegments.length !== 1 ? 's' : ''} with % ownership · identities labelled in source`
            : capNamesOnly.length > 0
              ? `${capNamesOnly.length} shareholder${capNamesOnly.length !== 1 ? 's' : ''} identified · ownership % not extractable from source`
              : (capRow ? 'Cap table structure not captured.' : undefined)
        }
      >
        {capSegments.length > 0 ? (
          <>
            <div style={{
              display: 'flex',
              width: '100%',
              height: 32,
              borderRadius: 'var(--r-sm)',
              overflow: 'hidden',
              border: '1px solid var(--lines)',
              marginBottom: 'var(--s2)',
            }}>
              {capSegments.map((s, i) => (
                <div
                  key={i}
                  data-tip={`${s.holder}: ${s.pct}%`}
                  style={{
                    width: `${s.pct}%`,
                    background: _CAP_PALETTE[i % _CAP_PALETTE.length],
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--s2)', fontSize: '0.75rem' }}>
              {capSegments.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: _CAP_PALETTE[i % _CAP_PALETTE.length], borderRadius: 2 }} />
                  <span className="t-muted">{s.holder}</span>
                  <span className="t-mono" style={{ marginLeft: 'auto' }}>{s.pct}%</span>
                </div>
              ))}
            </div>
          </>
        ) : capNamesOnly.length > 0 ? (
          /* Sprint 5 client feedback 2026-07-02 — name-only fallback.
             When BE scavenge landed shareholder names from lead_investors /
             strategic_partners / assumption prose but couldn't extract
             ownership percentages, render a compact Shareholder | Class
             table so §17.2 still surfaces "who's in the deal" instead
             of falling through to the "not captured" caveat. */
          <div className="table-wrap">
            <table className="data-table" aria-label="Cap table shareholder list">
              <thead>
                <tr>
                  <th>Shareholder</th>
                  <th>Class</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {capNamesOnly.map((r, i) => (
                  <tr key={i}>
                    <td className="td-name">{r.holder}</td>
                    <td className="t-xs t-muted">{r.class ?? '—'}</td>
                    <td className="t-xs t-muted">
                      {r.sourceDoc
                        ? `${r.sourceDoc}${r.sourcePage != null ? ` · p.${r.sourcePage}` : ''}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            {/* Sprint 5 client feedback 2026-07-02 — Fix 6. Replace
                the vague "Not captured in documents" with a pointer to
                the likely extraction blind spots so the analyst knows
                where to look for manual entry. */}
            {capRow?.value && !/scavenged|not captured/i.test(String(capRow.value))
              ? String(capRow.value)
              : 'Cap Table not extractable — check IC Memo Cap Table slide (typically titled "Cap Table" or "Pro Forma Cap Table") and SPA Exhibit A (Purchaser Schedule) for share count data. Slide-embedded tables may require manual entry.'}
          </p>
        )}
      </_SubsectionCard>

      {/* SUBSECTION 3: Deal Terms */}
      <_SubsectionCard title="3 · Deal Terms">
        <div className="table-wrap">
          <table className="data-table">
            <tbody>
              {dealTerms.map(([k, v]) => (
                <tr key={k}>
                  <td className="td-name" style={{ width: '34%' }}>{k}</td>
                  <td className="t-mono">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </_SubsectionCard>

      {/* SUBSECTION 4: Sales Projection */}
      <_SubsectionCard
        title="4 · Sales Projection — management forecast"
        footnote={salesYears.length > 0 ? `${salesYears.length}-year revenue trajectory.` : undefined}
      >
        {salesYears.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>{salesYears.map(([y]) => <th key={y}>{y}</th>)}</tr></thead>
              <tbody>
                <tr>{salesYears.map(([y, v]) => <td key={y} className="t-mono">{v}</td>)}</tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            Annual revenue projection not captured as a multi-year series.
            {revenueRow?.value ? ` Snapshot value: ${String(revenueRow.value)}.` : ''}
          </p>
        )}
      </_SubsectionCard>

      {/* SUBSECTION 5: Market Forecast — Metric | Value | Source
          layout matching §17.4 Sales Projection shape. Unaudited
          management-provided view; verification lives in §13-C. */}
      <_SubsectionCard
        title="5 · Market Forecast captured from deck"
        footnote={marketRow?.quoted_text ? `Source quote: ${String(marketRow.quoted_text).slice(0, 200)}` : undefined}
      >
        {marketMetrics.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '48%' }}>Metric</th>
                  <th style={{ width: '22%' }}>Value</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {marketMetrics.map((m, i) => (
                  <tr key={`${m.metric}-${i}`}>
                    <td className="td-name">{m.metric}</td>
                    <td className="t-mono">{m.value}</td>
                    <td className="t-xs t-muted">
                      {_fmtSource(m.sourceDoc, m.sourcePage) ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            {/* Sprint 5 client feedback 2026-07-02 — clearer fallback
                when no market signal was scavenged (Fix 5 of §17.5
                spec). Points the analyst at the likely extraction
                gap so a Market Overview slide with chart-only data
                can be verified / entered manually rather than
                treated as absent. */}
            {marketRow?.value
              ? String(marketRow.value)
              : 'Market forecast content not extractable from deck. Verify Market Overview / Sizing slides — chart-only data may require manual entry.'}
          </p>
        )}
      </_SubsectionCard>
    </>
  );
}

/* ── Sprint 5 — LLM-synthesized sections (Executive Summary,
   Consolidated Findings, Control Strengths). Reads from
   `risk.enrichment.synthesis` populated by the Stage 3 orchestrator.
   Falls back to a clear placeholder when the synthesis pass didn't run
   (older Risk runs from before the synthesizer commit). */

function _synthesis(risk: Record<string, unknown>): Record<string, unknown> | null {
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  if (!enrichment) return null;
  const s = enrichment.synthesis as Record<string, unknown> | undefined;
  return s ?? null;
}

function ExecutiveSummaryCards({
  dd,
  spa,
  risk,
}: {
  dd: Record<string, unknown>;
  spa: Record<string, unknown>;
  risk: Record<string, unknown>;
}): JSX.Element {
  /* Sprint 5 client feedback 2026-06-25 (Issues/FE doc #5) — Executive
     Summary rebuilt as a 3-card layout (Financial Position / Risk
     Summary / Legal Summary) + Conclusion panel. Cards source content
     from EXISTING report data — not LLM-synthesized prose. This means
     the cards always populate as long as DD / SPA / Risk have any data,
     and never read as a hallucinated narrative.

     Sources (per the analyst spec):
       • Financial Position  ← dd.financial_parameters.rows (Value /
         Benchmark / Variance / Classification per metric)
       • Risk Summary        ← dd.risk_profile.rows + synth Requires
         Attention items + critical-flag counts
       • Legal Summary       ← spa mandatory_section_detection +
         synth legal_assessment_summary cards
       • Conclusion panel    ← risk.ai_opinion_paragraph (existing AI
         opinion text) OR synth.executive_summary.conclusion fallback */

  // ── Financial Position card ────────────────────────────────────────
  const financialRows =
    (getNested(dd, 'financial_parameters.rows') as Array<Record<string, unknown>>) ?? [];

  const finExtracted = financialRows.filter((r) => {
    const v = String(r.value ?? '').toLowerCase();
    return v && v !== 'not found in documents' && v !== '—';
  }).length;
  const finReview = financialRows.filter((r) => {
    const c = String(r.classification ?? '').toLowerCase();
    return c === 'review required' || c === 'fail' || c === 'flagged';
  }).length;
  const finPass = financialRows.filter((r) => {
    const c = String(r.classification ?? '').toLowerCase();
    return c === 'pass' || c.includes('within');
  }).length;
  const finBullets: string[] = [];
  if (financialRows.length > 0) {
    finBullets.push(`${finExtracted} of ${financialRows.length} financial metrics extracted`);
    if (finPass > 0) finBullets.push(`${finPass} parameter(s) within benchmark`);
    if (finReview > 0) finBullets.push(`${finReview} parameter(s) require review`);
    const topMetrics = financialRows
      .filter((r) => {
        const v = String(r.value ?? '').toLowerCase();
        return v && v !== 'not found in documents' && v !== '—';
      })
      .slice(0, 3)
      .map((r) => `${r.metric}: ${r.value}`);
    if (topMetrics.length > 0) {
      finBullets.push(`Key values — ${topMetrics.join('; ')}`);
    }
  } else {
    finBullets.push('No financial parameters extracted yet — DD assessment may be pending.');
  }
  const finVariant: 'pass' | 'watch' | 'fail' | 'info' =
    finReview >= 3 ? 'fail'
    : finReview > 0 ? 'watch'
    : finPass > 0 ? 'pass'
    : 'info';
  const finLabel =
    finVariant === 'pass' ? 'Pass'
    : finVariant === 'watch' ? 'Watch'
    : finVariant === 'fail' ? 'Review'
    : 'Pending';

  // ── Risk Summary card ──────────────────────────────────────────────
  const riskRows =
    (getNested(dd, 'risk_profile.rows') as Array<Record<string, unknown>>) ?? [];
  // Composite severity (mirrors §5's _classifyRow logic — Critical at top end)
  const _likeRank: Record<string, number> = { likely: 0, possible: 1, unlikely: 2, rare: 3 };
  const _impRank: Record<string, number> = { severe: 0, major: 1, moderate: 2, minor: 3 };
  let rCrit = 0, rHigh = 0, rMed = 0;
  for (const r of riskRows) {
    const li = _likeRank[String(r.likelihood ?? '').toLowerCase()] ?? -1;
    const ii = _impRank[String(r.impact ?? '').toLowerCase()] ?? -1;
    if (li < 0 || ii < 0) continue;
    const composite = (3 - li) + (3 - ii);
    if (composite >= 5) rCrit++;
    else if (composite >= 4) rHigh++;
    else if (composite >= 2) rMed++;
  }
  const synth = _synthesis(risk);
  const overallSynth = synth?.risk_assessment_overall as Record<string, unknown> | undefined;
  const attention = (overallSynth?.requires_attention as Array<Record<string, unknown>> | undefined) ?? [];
  const riskBullets: string[] = [];
  if (riskRows.length > 0) {
    riskBullets.push(`${riskRows.length} risk(s) identified in DD profile`);
    const parts: string[] = [];
    if (rCrit > 0) parts.push(`${rCrit} Critical`);
    if (rHigh > 0) parts.push(`${rHigh} High`);
    if (rMed > 0) parts.push(`${rMed} Medium`);
    if (parts.length > 0) riskBullets.push(`Severity breakdown — ${parts.join(', ')}`);
  } else {
    riskBullets.push('No risk profile rows yet.');
  }
  if (attention.length > 0) {
    riskBullets.push(`${attention.length} item(s) flagged for management attention`);
  }
  const overallRiskRating = String(overallSynth?.rating ?? '');
  if (overallRiskRating) {
    riskBullets.push(`Overall risk rating: ${overallRiskRating}`);
  }
  const riskVariant: 'pass' | 'watch' | 'fail' | 'info' =
    rCrit > 0 ? 'fail'
    : rHigh >= 3 ? 'fail'
    : rHigh > 0 || rMed >= 5 ? 'watch'
    : riskRows.length > 0 ? 'pass'
    : 'info';
  const riskLabel =
    riskVariant === 'pass' ? 'Pass'
    : riskVariant === 'watch' ? 'Watch'
    : riskVariant === 'fail' ? 'Review'
    : 'Pending';

  // ── Legal Summary card ─────────────────────────────────────────────
  const universal =
    (getNested(spa, 'mandatory_section_detection.universal') as Array<Record<string, unknown>>) ?? [];
  const jurisdiction =
    (getNested(spa, 'mandatory_section_detection.jurisdiction') as Array<Record<string, unknown>>) ?? [];
  const univDetected = universal.filter((s) => s.detected).length;
  const jurisDetected = jurisdiction.filter((s) => s.detected).length;
  const legalSynth = synth?.legal_assessment_summary as Record<string, unknown> | undefined;
  const legalCards = (legalSynth?.cards as Array<Record<string, unknown>> | undefined) ?? [];
  const legalDeficient = legalCards.filter((c) => {
    const s = String(c.status ?? '').toLowerCase();
    return s === 'deficient' || s === 'missing' || s === 'absent';
  }).length;
  const legalBullets: string[] = [];
  if (universal.length > 0) {
    legalBullets.push(`${univDetected} of ${universal.length} universal mandatory sections detected`);
  }
  if (jurisdiction.length > 0) {
    legalBullets.push(`${jurisDetected} of ${jurisdiction.length} jurisdiction-specific clauses detected`);
  }
  if (legalCards.length > 0) {
    legalBullets.push(`${legalCards.length} legal area(s) assessed`);
    if (legalDeficient > 0) legalBullets.push(`${legalDeficient} marked Deficient or Missing`);
  }
  const legalRating = String(legalSynth?.rating ?? '');
  if (legalRating) {
    legalBullets.push(`Legal risk rating: ${legalRating}`);
  }
  if (legalBullets.length === 0) {
    legalBullets.push('No SPA / legal data extracted yet.');
  }
  const legalVariant: 'pass' | 'watch' | 'fail' | 'info' =
    legalDeficient >= 3 ? 'fail'
    : legalDeficient > 0 ? 'watch'
    : universal.length > 0 && univDetected === universal.length ? 'pass'
    : 'info';
  const legalLabel =
    legalVariant === 'pass' ? 'Pass'
    : legalVariant === 'watch' ? 'Watch'
    : legalVariant === 'fail' ? 'Review'
    : 'Pending';

  // ── Conclusion panel ───────────────────────────────────────────────
  // Sprint 5 client feedback 2026-07-09 follow-up 6 — the BE-emitted
  // auditor_opinion.executive_conclusion cites the OLD synth histogram
  // (e.g. "10 total issues: 6 Critical / 1 High / 3 Medium / 0 Low") and
  // now disagrees with §11's FE-recomputed count strip after the
  // cross-report ticket rewired the pipeline. Regenerate the conclusion
  // deterministically from the SAME merged pool §11 renders so §2 and
  // §11 always agree on totals.
  const conclusion = (() => {
    // Sprint 5 client feedback 2026-07-09 follow-up 7 — reuse the
    // canonical builder so §2 count matches §11 / §15 / §16 exactly.
    const canon = buildCanonicalFindings(risk, spa, dd);
    if (canon.length > 0) {
      /* Sprint 5 client feedback 2026-07-12 (issues/10 audit +
       * Iter9 #4) — Critical is retired at the severity level.
       * Fold any residual Critical (from legacy runs pre-Iter9) into
       * High; count Recommendation as its own tier. The narrative
       * now reports "N High, M Medium, K Low, R Recommendation" so
       * §2 conclusion mirrors the same 4-tier vocab as §15 / §11 /
       * KPI tile / header meta. */
      let h = 0, m = 0, lo = 0, rec = 0;
      for (const f of canon) {
        if (f.severity === 'Critical' || f.severity === 'High') h++;
        else if (f.severity === 'Medium') m++;
        else if (f.severity === 'Recommendation') rec++;
        else lo++;
      }
      const total = canon.length;
      const tail = rec > 0
        ? `, ${lo} Low, and ${rec} Recommendation${rec === 1 ? '' : 's'}`
        : `, and ${lo} Low`;
      const parts: string[] = [
        `${total} total issues, comprising ${h} High, ${m} Medium${tail} findings.`,
      ];
      if (h > 0) parts.push('The presence of High-severity findings materially elevates the overall risk posture and requires remediation before IC submission.');
      else if (m > 0) parts.push('The severity mix is dominated by Medium findings — control gaps present but not blocking.');
      else parts.push('The severity mix reflects a comparatively well-controlled posture with only Low-tier items.');
      return `The analysis identified ${parts.join(' ')}`;
    }
    // Fallback — no findings at all, use existing narrative sources.
    const es = synth?.executive_summary as Record<string, string> | undefined;
    const aoConclusion = (synth?.auditor_opinion as Record<string, unknown> | undefined)
      ?.executive_conclusion as string | undefined;
    return (
      aoConclusion ??
      (es as Record<string, string> | undefined)?.conclusion ??
      (risk.ai_opinion_paragraph as string | null) ??
      'No material control gaps surfaced across DD / SPA / Risk.'
    );
  })();

  const renderCard = (
    title: string,
    bullets: string[],
    variant: 'pass' | 'watch' | 'fail' | 'info',
    label: string,
  ) => (
    <div className="ra-exec-card" key={title}>
      <div className="h">
        <span>{title}</span>
        <Badge variant={variant}>{label}</Badge>
      </div>
      {/* Sprint 5 client feedback 2026-07-03 — bullets can come from
          LLM narrative (e.g. Risk Summary / Legal Summary bullets that
          summarise flag composition). Route through FormattedText so
          any embedded markdown renders instead of leaking asterisks. */}
      <ul>
        {bullets.map((b, i) => (
          <li key={i}><FormattedText text={b} inline /></li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <div className="ra-exec-grid">
        {renderCard('Financial Position', finBullets, finVariant, finLabel)}
        {renderCard('Risk Summary', riskBullets, riskVariant, riskLabel)}
        {renderCard('Legal Summary', legalBullets, legalVariant, legalLabel)}
      </div>
      {conclusion && (
        <div className="ra-conclusion">
          <div className="t">Conclusion</div>
          {/* Sprint 5 client feedback 2026-07-03 — LLM prose passes
              through FormattedText so **bold** and bullet markers
              render as HTML instead of visible asterisks. */}
          <FormattedText text={conclusion} />
        </div>
      )}
    </>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 11 redesigned to match
   `New UI Changes/risk-report.html` §11 — 4-column table with an Area
   badge (Legal/Financial/Technical/Operational). The Area field is now
   produced by the synthesizer's consolidated_findings LLM pass (it was
   added to the prompt schema in this commit; older runs without it
   fall back to deriving area from the id prefix). */
function _areaBadgeClass(_area: string): string {
  /* Sprint 5 client feedback 2026-07-11 (Iter5 #12) — category
     badges in §11 must all be the same neutral colour so only the
     Severity badge (Critical/High/Medium/Low) carries a
     differentiating hue. Previously Legal was red, Operational
     amber, Risk blue — the double-colour treatment made the row
     read as if Category also carried severity information. */
  return 'badge badge-neutral';
}

function _inferArea(id: string, description: string): string {
  const upper = (id + ' ' + description).toUpperCase();
  if (upper.includes('SPA-') || upper.includes('LEGAL') || upper.includes('CLAUSE') || upper.includes('INDEMN') || upper.includes('ESCROW')) return 'Legal';
  if (upper.includes('DD-CALC') || upper.includes('DD-STD') || upper.includes('FINANCIAL') || upper.includes('EBITDA') || upper.includes('IRR') || upper.includes('REVENUE')) return 'Financial';
  if (upper.includes('TECHNICAL') || upper.includes('TECHDD') || upper.includes('ENGINEER') || upper.includes('CAPEX')) return 'Technical';
  return 'Operational';
}

/* Sprint 5 client feedback 2026-07-12 (Iter9 #4) — colour spec
 * updated to match the client's new 4-tier severity model:
 *   High           → Red    (badge-fail)
 *   Medium         → Amber  (badge-watch)
 *   Low            → Green  (badge-pass)
 *   Recommendation → Blue   (badge-info)
 * Critical is retired at the severity level and folds into High so
 * legacy Critical rows still render (as red) without a data migration. */
function _severityBadgeClass(s: string): string {
  const k = (s ?? '').trim().toLowerCase();
  if (k === 'high' || k === 'critical' || k === 'reject' || k === 'severe' || k === 'fail' || k === 'flag') return 'badge badge-fail';
  if (k === 'medium' || k === 'moderate' || k === 'watch') return 'badge badge-watch';
  if (k === 'recommendation' || k === 'opportunity' || k === 'info') return 'badge badge-info';
  if (k === 'low' || k === 'minor' || k === 'pass') return 'badge badge-pass';
  return 'badge badge-neutral';
}

function ConsolidatedFindingsTable({
  risk,
  spaAttributeGroups: _unusedSpaAttrGroups,
  spaReport,
  ddReport,
}: {
  risk: Record<string, unknown>;
  spaAttributeGroups?: Record<string, unknown> | undefined;
  spaReport?: Record<string, unknown> | undefined;
  ddReport?: Record<string, unknown> | undefined;
}): JSX.Element {
  void _unusedSpaAttrGroups; // superseded by buildCanonicalFindings
  /* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6):
     §11 Consolidated Findings now renders as severity-grouped CARDS
     instead of a table. Layout matches the design doc's "findings
     as individual cards with severity indicators" requirement. Each
     card shows a left-border severity stripe, finding ID + area
     chips on the top row, the title in larger type, and the
     description below. Cards are ordered Critical → High → Medium
     → Low; severity counts shown in a summary strip above.

     Sprint 5 client feedback 2026-07-09 (cross-report consistency
     ticket) — the Legal-category slice of this table is now sourced
     from SPA §4 directly via getSpaIssueTableRows. LLM-produced
     Legal findings from the synthesizer's consolidated_findings
     pass are DROPPED and replaced 1:1 with SPA §5 rows so the
     ticket's row-for-row invariant holds. Non-Legal findings
     (Financial / Technical / Operational / Risk) continue to flow
     through from the LLM synth unchanged. */
  /* Sprint 5 client feedback 2026-07-09 follow-up 7 — canonical
     findings pipeline. Single reader (buildCanonicalFindings) that
     returns everything with F-01, F-02, ... display IDs already
     assigned, deduped, and severity-normalised. This replaces the
     bespoke merge that used to live inline here so §11 / §9 / §16
     all render the same rows in the same order with the same IDs. */
  const _canonicalFindings = buildCanonicalFindings(risk, spaReport, ddReport);
  /* Sprint 5 client feedback 2026-07-12 (issues/11 follow-up) —
   * strip the boilerplate "'Name' — Status: review clause status"
   * pattern from title + description so §11 cards read as business
   * statements, not debug output. Same regex as §9 above; kept
   * inline for a small change surface. */
  const rawFindings = _canonicalFindings.map((c) => ({
    id: c.display_id,
    original_id: c.original_id,
    area: c.area,
    severity: c.severity,
    title: stripClauseBoilerplate(c.title),
    description: stripClauseBoilerplate(c.description),
    source_page: c.source_page,
    action_required: c.action_required,
    responsible: c.responsible,
    attribute_key: c.attribute_key,
  }));

  // Canonical builder produced rawFindings above — all merge / dedupe /
  // renumber / severity-normalise logic lives there. `findings` is just
  // an alias for TypeScript below.
  const findings: Array<Record<string, unknown>> = rawFindings;
  if (findings.length === 0) {
    return <Pending note="No consolidated findings. Re-run Risk Assessment after DD/SPA complete." />;
  }
  // Sprint 5 client feedback 2026-07-02 — findings must render in
  // STRICT NUMERICAL ORDER by finding_id (F-01, F-02, F-03, ...,
  // F-N). Previously sorted by severity, which caused the synthetic
  // F-XX finding (DD §5 Overall Investment Risk verdict, injected
  // in synthesizer._append_dd_overall_risk_finding) to bubble into
  // the middle of the list based on its severity — e.g. for
  // AUDIT-2026-0094 the display order was F-01, F-02, F-06, F-03,
  // F-04, F-05. Sort by the numeric suffix of the id so F-06 sits
  // at the end regardless of severity; keep a stable severity
  // secondary sort for legacy rows without a "F-NN" pattern id.
  const _findingIdNumber = (raw: unknown): number => {
    const s = String(raw ?? '').trim();
    const m = s.match(/(\d+)/);
    if (!m) return Number.POSITIVE_INFINITY;
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  };
  /* Sprint 5 client feedback 2026-07-12 (Iter9 #4) — sort order and
   * KPI strip now use the new 4-tier vocab (High → Medium → Low →
   * Recommendation). Critical is folded into High so legacy data
   * still ranks correctly. */
  const sevOrder: Record<string, number> = {
    critical: 0, high: 0, fail: 0, severe: 0, reject: 0, flag: 0,
    medium: 1, moderate: 1, watch: 1,
    low: 2, minor: 2,
    recommendation: 3, opportunity: 3, info: 3,
  };
  const sorted = [...findings].sort((a, b) => {
    const an = _findingIdNumber(a.id);
    const bn = _findingIdNumber(b.id);
    if (an !== bn) return an - bn;
    const av = sevOrder[String(a.severity ?? '').toLowerCase()] ?? 99;
    const bv = sevOrder[String(b.severity ?? '').toLowerCase()] ?? 99;
    return av - bv;
  });
  const counts = { high: 0, medium: 0, low: 0, recommendation: 0 };
  for (const f of sorted) {
    const bucket = _normSeverity(f.severity).toLowerCase() as keyof typeof counts;
    if (bucket in counts) counts[bucket] += 1;
  }
  /* Sprint 5 client feedback 2026-07-12 (Iter9 #4) — colour spec:
   *   High           → Red   (fail)
   *   Medium         → Amber (watch)
   *   Low            → Green (pass)
   *   Recommendation → Blue  (info)
   * Critical folds into High so legacy rows still render red. */
  const sevColor = (sev: string): string => {
    const s = sev.toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'reject' || s === 'severe' || s === 'fail' || s === 'flag') return 'var(--fail)';
    if (s === 'medium' || s === 'watch' || s === 'moderate') return 'var(--watch)';
    if (s === 'recommendation' || s === 'opportunity' || s === 'info') return 'var(--info)';
    if (s === 'low' || s === 'pass' || s === 'minor') return 'var(--pass)';
    return 'var(--muted)';
  };
  return (
    <div style={{ display: 'grid', gap: 'var(--s4)' }}>
      {/* Severity overview strip — Sprint 5 client feedback
          2026-07-12 (Iter9 #4) — Critical chip retired; Recommendation
          chip added. Order matches the client's spec: High → Medium
          → Low → Recommendation. */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--s2)',
          flexWrap: 'wrap',
          paddingBottom: 'var(--s2)',
          borderBottom: '1px solid var(--lines-soft)',
        }}
      >
        {([
          ['High',           counts.high,           'var(--fail)',  'var(--fail-bg)'],
          ['Medium',         counts.medium,         'var(--watch)', 'var(--watch-bg)'],
          ['Low',            counts.low,            'var(--pass)',  'var(--pass-bg)'],
          ['Recommendation', counts.recommendation, 'var(--info)',  'var(--info-bg)'],
        ] as Array<[string, number, string, string]>).map(([lbl, n, fg, bg]) => (
          <span
            key={lbl}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 12px',
              borderRadius: 100,
              background: bg,
              color: fg,
              fontSize: '0.75rem',
              fontWeight: 600,
              opacity: n > 0 ? 1 : 0.45,
            }}
          >
            <b>{n}</b> {lbl}
          </span>
        ))}
      </div>
      {/* Finding cards */}
      <div style={{ display: 'grid', gap: 'var(--s3)' }}>
        {sorted.map((f, i) => {
          const id = String(f.id ?? `F-${String(i + 1).padStart(2, '0')}`);
          const area = String(f.area ?? _inferArea(id, String(f.description ?? f.title ?? '')));
          const severity = _normSeverity(f.severity);
          const title = String(f.title ?? f.description ?? '—');
          const desc = f.description && f.description !== f.title ? String(f.description) : '';
          const accent = sevColor(severity);
          /* Sprint 5 client feedback 2026-07-11 (Task #718) — the
             prior tooltip dumped title + description again, which
             just repeated what the analyst already sees on the card
             body. The tooltip now surfaces information NOT visible
             on the card:
               1. Section-of-origin + cross-references (which of §9
                  / §10 / §12 / §15 / §16 this finding also appears
                  in) — helps the analyst navigate the report.
               2. Severity meaning + remediation window from
                  Appendix A vocabulary — makes the badge concrete.
               3. Source doc + page in the canonical "InvMemo p.31"
                  format if defined.
               4. Action + responsible + attribute key + internal ID
                  only when they contain real values (skip when the
                  underlying data is empty or placeholder).
               5. Related-findings hint: sibling F-IDs that share the
                  same attribute-family prefix (e.g. all
                  anti_dilution_* findings shown together). */
          const _sevMeta = ((): { window: string; meaning: string } => {
            const k = severity.trim().toLowerCase();
            if (k === 'critical' || k === 'reject') {
              return {
                window: 'Immediate escalation',
                meaning: 'Systemic control failure with material adverse effect on the deal',
              };
            }
            if (k === 'high' || k === 'watch' || k === 'flag') {
              return {
                window: 'Generally within 60 days',
                meaning: 'Serious control weakness that could undermine internal controls',
              };
            }
            if (k === 'medium' || k === 'moderate') {
              return {
                window: 'Generally within 120 days',
                meaning: 'Control weakness needing improvement to strengthen the environment',
              };
            }
            if (k === 'low' || k === 'minor' || k === 'pass') {
              return {
                window: 'No indicative timing',
                meaning: 'Occasional lapse with minimal process impact',
              };
            }
            return { window: 'Under review', meaning: 'Severity pending analyst confirmation' };
          })();
          /* Cross-references are section IDs, not hand-typed labels. The label
             and the number now come from TOC_ITEMS — one source of truth, so a
             renumbered section can't leave a stale "§7" behind — and each one
             renders as a link that jumps to the section. The old "§" glyph is
             gone: it reads as a stray symbol, renders as a box in some fonts,
             and contradicted the TOC's own "3. Financial Assessment" style. */
          const _sections = ((): string[] => {
            const s: string[] = [];
            const areaLower = area.toLowerCase();
            const sevLower = severity.toLowerCase();
            if (areaLower === 'legal') {
              s.push('s10');
              if (sevLower === 'critical' || sevLower === 'high') s.push('s9');
            } else if (areaLower === 'financial') {
              s.push('s3', 's7');
            } else if (areaLower === 'technical' || areaLower === 'operational' || areaLower === 'risk') {
              s.push('s5');
            }
            s.push('s15', 's16');
            return s;
          })();
          const _related = ((): string[] => {
            const my = String(f.attribute_key ?? '').trim().toLowerCase();
            if (!my) return [];
            // Common prefix family: first 2 tokens of a snake_case key
            const family = my.split('_').slice(0, 2).join('_');
            if (!family) return [];
            return sorted
              .filter((o) => {
                const k = String(o.attribute_key ?? '').trim().toLowerCase();
                return k && k !== my && k.startsWith(family);
              })
              .map((o) => String(o.id))
              .slice(0, 4);
          })();
          const _sourceStr = ((): string | null => {
            const doc = f.source_document != null ? String(f.source_document).trim() : '';
            const page = f.source_page != null ? String(f.source_page).trim() : '';
            if (!doc && !page) return null;
            if (doc && page) return `${doc} p.${page}`;
            return doc || `p.${page}`;
          })();
          /* This detail block used to be a native `title` tooltip on the
             whole card. That was wrong twice over: the browser delayed it
             by ~1s, and eight lines of structured detail is not tooltip
             content — it covered the card behind it, couldn't be selected
             or copied, and vanished on pointer-out. It is now a
             click-to-expand panel (see FindingCard). */
          type DetailRow = [string, React.ReactNode];
          const _detailRows: DetailRow[] = [
            ['Severity', `${severity} — ${_sevMeta.meaning} · ${_sevMeta.window}`],
            ...(_sections.length
              ? [['Also appears in', <SectionRefs key="refs" ids={_sections} />] as DetailRow]
              : []),
            ...(_sourceStr ? [['Source', _sourceStr] as DetailRow] : []),
            ...(f.action_required
              ? [['Action required', String(f.action_required)] as DetailRow]
              : []),
            ...(f.responsible && String(f.responsible).trim().toLowerCase() !== 'to be assigned'
              ? [['Responsible', String(f.responsible)] as DetailRow]
              : []),
            /* Attribute ("calculation_verifications") and Internal ID
               ("DD-CALC-952502DA") removed per client 2026-07-12 — they are pipeline
               plumbing, not audit content. A raw snake_case key next to a hash reads as
               debug output leaking into the report, and the reader can act on neither.
               Both are still carried on the finding (cross-referencing, cache keys) —
               just not shown. */
            ...(_related.length
              ? [['Related findings', `${_related.join(', ')} (same family)`] as DetailRow]
              : []),
          ];
          return (
            <FindingCard
              key={id}
              id={id}
              area={area}
              severity={severity}
              title={title}
              desc={desc}
              accent={accent}
              detailRows={_detailRows}
            />
          );
        })}
      </div>
    </div>
  );
}

/* Cross-references to other sections of this report. Labels + numbers come
   from TOC_ITEMS so they can never drift from the actual section headings,
   and each renders as a link that scrolls to it. */
function SectionRefs({ ids }: { ids: string[] }): JSX.Element {
  return (
    <>
      {ids.map((sid, i) => {
        const label = TOC_ITEMS.find((t) => t.id === sid)?.label ?? sid;
        return (
          <Fragment key={sid}>
            {i > 0 && <span style={{ color: 'var(--lines)' }}> · </span>}
            <a href={`#${sid}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {label}
            </a>
          </Fragment>
        );
      })}
    </>
  );
}

/* DOM id of a §11 finding card. Namespaced so an F-XX display id can never
   collide with a section anchor (s11, s12, …) or an SPA/DD element id.
   Single source of truth for both the card and the §15 chips that link to it. */
function findingAnchorId(displayId: string): string {
  return `finding-${displayId.toLowerCase()}`;
}

/* §11 finding card. The cross-reference / severity-meaning / provenance
   block is revealed by an explicit "Details" toggle rather than by
   hovering the card: it is a structured block, so it must stay on screen,
   be selectable, and be reachable from the keyboard. Hover tooltips are
   for one-liners. */
function FindingCard({
  id,
  area,
  severity,
  title,
  desc,
  accent,
  detailRows,
}: {
  id: string;
  area: string;
  severity: string;
  title: string;
  desc: string;
  accent: string;
  detailRows: Array<[string, React.ReactNode]>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = `finding-details-${id}`;

  return (
    <article
      /* Anchor target for the "Closes F-XX" chips in §15 — see
         findingAnchorId(). :target highlights the card so the reader can
         see which one the jump landed on. */
      id={findingAnchorId(id)}
      className="ra-finding-card"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--lines)',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 'var(--r-md)',
        padding: 'var(--s4) var(--s5)',
        display: 'grid',
        gap: 'var(--s2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)', flexWrap: 'wrap' }}>
        <span className="t-mono t-xs" style={{ fontWeight: 600, color: 'var(--text-sec)' }}>{id}</span>
        <span className={_areaBadgeClass(area)}>{area}</span>
        <span className={_severityBadgeClass(severity)}>{severity}</span>
        {detailRows.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginLeft: 'auto' }}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide details' : 'Details'}
          </button>
        )}
      </div>
      {/* Sprint 5 client feedback 2026-07-03 — LLM finding prose passes
          through FormattedText so **bold** / bullets / soft-breaks render
          as HTML instead of literal markdown characters. */}
      <div style={{ fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.4 }}>
        <FormattedText text={title} inline />
      </div>
      {desc && (
        <div style={{ fontSize: '0.82rem', color: 'var(--text-sec)', lineHeight: 1.5 }}>
          <FormattedText text={desc} />
        </div>
      )}
      {open && (
        <dl
          id={panelId}
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            gap: '6px var(--s4)',
            margin: 'var(--s2) 0 0',
            paddingTop: 'var(--s3)',
            borderTop: '1px dashed var(--lines)',
            fontSize: '0.78rem',
          }}
        >
          {detailRows.map(([label, value]) => (
            <Fragment key={label}>
              <dt style={{ color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</dt>
              <dd style={{ margin: 0, color: 'var(--text-sec)', minWidth: 0, overflowWrap: 'anywhere' }}>
                {value}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
    </article>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 12 redesigned to match
   `New UI Changes/risk-report.html` §12 — clean checkmark grid with
   SHORT title-style strengths (the LLM prompt was rewritten to
   produce 80-char labels instead of rambling observations). */
function ControlStrengthsList({
  risk,
  spaAttributeGroups,
}: {
  risk: Record<string, unknown>;
  spaAttributeGroups?: Record<string, unknown> | undefined;
}): JSX.Element {
  /* Source priority — MUST stay identical to the PDF's
     _prepare_good_practice (backend renderer.py). Both read
     synth.control_strengths first and fall back to SPA §4 Pass rows.

     History, because this flipped twice and the reason matters:
     ticket RG-7 (2026-07-09) made SPA §4 Pass rows primary and demoted the
     synth list. But §12 never actually took that path — /consolidated-report
     didn't send `attribute_groups`, so §12 silently fell through to
     control_strengths, and that 6-item list is what the client signed off on
     (confirmed 2026-07-12). The PDF was aligned to it in fff311f.
     Now that attribute_groups IS hydrated, leaving Pass-rows primary would
     have swapped §12 to a different (3-item) list behind the client's back.
     control_strengths is primary on both surfaces; Pass rows remain the
     fallback for runs with no synthesis. */
  const synth = _synthesis(risk);
  const strengths = synth?.control_strengths as Array<Record<string, unknown>> | undefined;
  const passClauses = getSpaPassClauses(spaAttributeGroups);

  const primary: Array<{ key: string; label: string }> =
    strengths && strengths.length > 0
      ? strengths.map((s, i) => ({
          key: String(s.label ?? s.observation ?? s.area ?? i),
          label: String(s.label ?? s.observation ?? s.area ?? '—'),
        }))
      : passClauses.map((c) => ({
          key: c.attribute_key ?? c.display_name,
          label: c.display_name || c.attribute_key,
        }));

  if (primary.length === 0) {
    return <Pending note="No control strengths or Pass-rated SPA clauses for this deal — no good practice to report." />;
  }

  return (
    <div className="ra-strength-grid">
      {primary.map((s, i) => (
        /* Iter5 #13/#14 — light green tint, no tick, no bold. */
        <div
          key={s.key ?? i}
          className="ra-strength"
          style={{
            background: 'rgba(46, 160, 67, 0.06)',
            border: '1px solid rgba(46, 160, 67, 0.18)',
          }}
        >
          <span className="tx" style={{ fontWeight: 400 }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 5 redesign matching
   `New UI Changes/risk-report.html`. Reads risk.enrichment.synthesis
   .risk_assessment_overall (populated by the synthesizer's 4th pass).
   Renders:
     - Overall Investment Risk badge (Low / Medium / High, coloured)
     - "Why the rating is X" rationale paragraph
     - Requires Attention bullet list with coloured dots
       (review = red, watch = amber, pass = green) */
function Section5RiskAssessmentSynthesis({ risk }: { risk: Record<string, unknown> }): JSX.Element | null {
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const overall = synth?.risk_assessment_overall as Record<string, unknown> | undefined;
  if (!overall) {
    return (
      <div style={{ marginTop: 'var(--s4)' }}>
        <Pending note="Overall risk synthesis not available yet — re-run Risk Assessment to populate this block." />
      </div>
    );
  }

  const rating = (overall.rating as string | null) ?? null;
  const rationale = (overall.rationale as string | null) ?? '';
  const attention = (overall.requires_attention as Array<Record<string, unknown>> | undefined) ?? [];

  const ratingColour =
    rating === 'High' ? 'var(--fail)' :
    rating === 'Medium' ? 'var(--watch)' :
    rating === 'Low' ? 'var(--pass)' :
    'var(--muted)';

  /* Sprint 5 client feedback 2026-06-23 — Phase 2 of Risk Report UI port.
     Switched from inline-styled cards to the design's .ra-rationale
     (2-col grid with top-border separator, .ra-rr-h accent headers,
     .ra-watch-list with .ra-wd severity dots). The overall-risk pill
     is no longer rendered here — DdRiskTable owns it via .ra-overall
     inside the heatwrap's left column so the layout matches the
     mockup. _ratingColour is still used to swatch the dots in
     ra-watch-list. */
  void ratingColour; // intentionally unused: previously used by the pill we now skip

  return (
    <div className="ra-rationale" id="risk-overall-rationale">
      <div className="ra-rr-col">
        <div className="ra-rr-h">Why the rating is {rating ?? '—'}</div>
        {/* Sprint 5 client feedback 2026-07-07 (UI Issues #3) — the
            synthesizer LLM emits **bold** in the rationale prose and
            React previously rendered them as literal asterisks. Route
            through FormattedText so the enclosed spans render bold and
            the surrounding ** markers are removed. */}
        <p>{rationale ? <FormattedText text={rationale} inline /> : '—'}</p>
      </div>
      <div className="ra-rr-col">
        <div className="ra-rr-h">Requires Attention</div>
        {attention.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>No items.</p>
        ) : (
          <ul className="ra-watch-list">
            {attention.map((item, i) => {
              const sev = String(item.severity || 'watch') as 'review' | 'watch' | 'pass';
              const dotClass =
                sev === 'review' ? 'ra-wd ra-wd-review'
                : sev === 'pass' ? 'ra-wd ra-wd-pass'
                : 'ra-wd ra-wd-watch';
              // Sprint 5 client feedback 2026-06-24 (DD/Risk.docx issue 7) —
              // synthesizer LLM emits **bold** markdown in label/text
              // fields; React renders them as literal asterisks. Strip
              // the surrounding ** markers so the prose reads cleanly
              // (the <b> tag already styles the label as bold; inline
              // emphasis isn't supported in this list).
              const rawLabel = String(item.label || '—');
              const rawText = String(item.text || '');
              const cleanLabel = rawLabel.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*/g, '');
              const cleanText = rawText.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*/g, '');
              return (
                <li key={i}>
                  <span className={dotClass} aria-hidden="true" />
                  <span>
                    <b>{cleanLabel}</b> — {cleanText}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 8 redesign matching
   `New UI Changes/risk-report.html`. Reads
   risk.enrichment.synthesis.legal_assessment_summary populated by the
   synthesizer's 5th LLM pass. Renders:
     - 6-8 legal-area cards in a grid, each with a status badge
       (Adequate / Deficient / Present / Secured / Partial / Missing)
     - Overall Legal Risk Rating (Low / Medium / High) + 1-line rationale
   Falls back to the legacy SpaLegalSummary mandatory-section grid when
   synthesis hasn't run yet. */
function _legalCardBadge(status: string): { className: string; label: string } {
  const k = (status ?? '').trim().toLowerCase();
  if (k === 'deficient' || k === 'missing' || k === 'absent' || k === 'not present') {
    return { className: 'badge badge-fail', label: status };
  }
  if (k === 'partial' || k === 'watch' || k === 'conditional' || k === 'partially present') {
    return { className: 'badge badge-watch', label: status };
  }
  if (k === 'adequate' || k === 'present' || k === 'secured' || k === 'satisfactory' || k === 'pass') {
    return { className: 'badge badge-pass', label: status };
  }
  return { className: 'badge badge-neutral', label: status };
}

function Section8LegalAssessment({
  risk,
  fallbackSpa,
}: {
  risk: Record<string, unknown>;
  fallbackSpa: Record<string, unknown>;
}): JSX.Element {
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const legal = synth?.legal_assessment_summary as Record<string, unknown> | undefined;

  /* Fallback path — legacy view when synthesis hasn't run yet */
  if (!legal) {
    return (
      <>
        <SpaLegalSummary spa={fallbackSpa} />
        <div style={{ marginTop: 'var(--s4)' }}>
          <Pending note="Legal Risk Rating not yet synthesized — re-run Risk Assessment to populate this block." />
        </div>
      </>
    );
  }

  const cards = (legal.cards as Array<Record<string, unknown>> | undefined) ?? [];
  const rating = (legal.rating as string | null) ?? null;
  const rationale = (legal.rationale as string | null) ?? '';

  /* Sprint 5 client feedback 2026-06-23 — Phase 4 of Risk Report UI port.
     Switched from inline-styled card grid + bordered rating box to the
     mockup's .ra-legal-cards (4-col auto-fit grid of .ra-lcard) +
     .ra-legal-rating (fail-tinted full-width rating panel). When the
     synthesizer returns a rationale paragraph it now sits below the
     pill with the same .fail/.watch tint as the rating itself. */
  return (
    <div>
      <div className="ra-legal-cards">
        {cards.length === 0 ? (
          <Pending note="Synthesizer returned no legal-area cards." />
        ) : (
          cards.map((c, i) => {
            const status = String(c.status || '—');
            const area = String(c.area || '—');
            const cardRationale = String(c.rationale || '');
            const badge = _legalCardBadge(status);
            return (
              // Sprint 5 client feedback 2026-07-03 — strip markdown in
              // the tooltip so LLM-emitted ** markers don't render as
              // literal asterisks in the native title attribute.
              <div key={i} className="ra-lcard" data-tip={cardRationale ? cleanTooltip(cardRationale) : undefined}>
                <span className="nm">{area}</span>
                <span className={badge.className}>{badge.label}</span>
              </div>
            );
          })
        )}
      </div>
      {/* Sprint 5 client feedback 2026-07-11 (Iter6c #3) — hover
          tooltip on the Legal Risk Rating badge showing the LLM
          rationale that produced the rating. pre-line so the
          multi-line reasoning renders scannably in the native
          tooltip. The <p className="ra-note"> below repeats the
          same rationale as visible text for readers who don't hover;
          the tooltip is redundant-but-explicit for badge-first
          scanners. */}
      <div
        className="ra-legal-rating"
        data-tip={(() => {
          const _rat = String(rationale ?? '').trim();
          if (_rat) return cleanTooltip(`Legal Risk Rating — ${rating ?? '—'}\n\nWhy: ${_rat}`);
          return `Legal Risk Rating — ${rating ?? '—'}\n\nThe AI aggregated the mandatory-clause + investor-protection card statuses to arrive at the overall Legal Risk Rating. No explicit rationale was attached for this run.`;
        })()}
        style={{ cursor: 'help', whiteSpace: 'pre-line' }}
      >
        <span className="l">Legal Risk Rating</span>
        <span className="v">{rating ?? '—'}</span>
      </div>
      {rationale && (
        <p className="ra-note">
          {/* Format Phase 6 — LLM rationale carries **bold** markers. */}
          <FormattedText text={rationale} inline />
        </p>
      )}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 6 redesign matching
   `New UI Changes/risk-report.html` Section 06. The 4-6 cards come from
   the synthesizer's 6th LLM pass (synthesis.key_assumptions) which
   groups the 60+ raw DD assumption rows into curated themes. Falls
   back to the legacy DdAssumptions list when synthesis hasn't run. */
function Section6KeyAssumptionsCards({
  risk,
  fallbackDd,
}: {
  risk: Record<string, unknown>;
  fallbackDd: Record<string, unknown>;
}): JSX.Element {
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const cards = synth?.key_assumptions as Array<Record<string, unknown>> | undefined;

  if (!cards || cards.length === 0) {
    /* Fallback to the legacy list when the synthesis pass hasn't run.
       The legacy renderer reads dd.key_assumptions.items which the
       backend enrichment in risk_assessment.py builds from the raw
       DD rows — useful for older Risk Assessments. */
    return (
      <>
        <DdAssumptions dd={fallbackDd} />
        <div style={{ marginTop: 'var(--s3)' }}>
          <Pending note="Curated assumption cards pending — re-run Risk Assessment to generate the 4-6 thematic cards from the raw DD data." />
        </div>
      </>
    );
  }

  /* Sprint 5 client feedback 2026-06-23 — Phase 3 of Risk Report UI port.
     Switched from generic surface cards to the mockup's .ra-assum-list
     of .ra-assum cards. Each card has .ra-assum-h (name + status
     badge), .ra-assum-x (italic display-font extracted quote with a
     left border), and .ra-assum-f (source-tag + assessment badge). */
  return (
    <div>
      <div className="ra-assum-list">
        {cards.map((c, i) => {
          // Sprint 5 client feedback 2026-06-24 (AUDIT-2026-0054
          // S-12/S-13/S-14/S-31) — assumption cards leaked raw
          // Python repr through the renderer:
          //   * narrative wrapped in TRIPLE quotes ("""..."""") because
          //     the synth emitted a JSON string that already had outer
          //     quotes (S-12)
          //   * source_doc rendered as ['Investment Memo', 'Investment
          //     Memo'] including the Python list brackets + single
          //     quotes + duplicates (S-13)
          //   * dict-shaped values appeared as {'2024': 291, '2025':
          //     569, ...} instead of an inline list (S-14)
          //   * single-quote leak from list elements (S-31)
          //
          // Three helpers below normalise each surface before display.
          const _stripQuoteLayers = (raw: string): string => {
            let s = raw.trim();
            // Drop matched outer quotes — both straight and curly.
            while (s.length >= 2) {
              const first = s.charAt(0);
              const last = s.charAt(s.length - 1);
              const pairs = ['""', "''", '“”', '‘’'];
              const matched = pairs.some(
                ([open, close]) => first === open && last === close,
              );
              if (!matched) break;
              s = s.slice(1, -1).trim();
            }
            return s;
          };
          const _formatSourceField = (raw: unknown): string => {
            if (raw == null) return '—';
            if (Array.isArray(raw)) {
              const seen = new Set<string>();
              const cleaned = raw
                .map((x) => String(x).trim().replace(/^['"]|['"]$/g, ''))
                .filter((x) => {
                  if (!x || seen.has(x)) return false;
                  seen.add(x);
                  return true;
                });
              return cleaned.length ? cleaned.join(', ') : '—';
            }
            const s = String(raw).trim();
            // Detect a Python-list literal that arrived as a string:
            // "['Investment Memo', 'Investment Memo']"
            if (s.startsWith('[') && s.endsWith(']')) {
              try {
                // Convert single-quoted JSON-ish to valid JSON.
                const repaired = s.replace(/'/g, '"');
                const parsed = JSON.parse(repaired);
                if (Array.isArray(parsed)) return _formatSourceField(parsed);
              } catch {
                /* fall through */
              }
            }
            return s || '—';
          };
          const _formatDictValueDisplay = (raw: unknown): string => {
            // Already a non-dict primitive → just stringify.
            if (raw == null || raw === '') return '—';
            if (typeof raw === 'number' || typeof raw === 'boolean') {
              return String(raw);
            }
            // Plain object: render as inline "key: value · key: value"
            if (typeof raw === 'object' && !Array.isArray(raw)) {
              const entries = Object.entries(raw as Record<string, unknown>);
              if (!entries.length) return '—';
              return entries
                .map(([k, v]) => {
                  const isCurrency = /^[+-]?\d+(\.\d+)?$/.test(String(v));
                  const valDisplay = isCurrency ? `$${v}M` : String(v);
                  return `${k}: ${valDisplay}`;
                })
                .join(' · ');
            }
            // String that LOOKS like a Python dict repr: "{'2024': 291, '2025': 569}"
            const s = String(raw).trim();
            if (s.startsWith('{') && s.endsWith('}')) {
              try {
                const repaired = s.replace(/'/g, '"');
                const parsed = JSON.parse(repaired);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  return _formatDictValueDisplay(parsed);
                }
              } catch {
                /* fall through */
              }
            }
            return s || '—';
          };
          const status = String(c.status || '—');
          const assessment = String(c.assessment || '—');
          const statusKey = status.trim().toLowerCase();
          const assessmentKey = assessment.trim().toLowerCase();
          // Sprint 5 client feedback 2026-06-24 (FIRST SET #6) +
          // 2026-06-25 (First set.docx issue 11): the header badge
          // started showing both Status + Assessment chips on cards
          // where status != "watch" — analysts read this as "one
          // assumption has 2 badges" (e.g. "Pass" badge in header AND
          // "Conservative" badge in footer for the same theme).
          // The footer Assessment badge (Aggressive/Conservative/
          // Regular) is the genuinely informative polarity signal;
          // the header Status (Pass/Watch) is now redundant when
          // both are present. Drop the header chip whenever the
          // footer assessment chip carries a valid polarity label.
          const _assessmentLabels = new Set([
            'aggressive', 'conservative', 'regular', 'moderate', 'balanced',
          ]);
          const isRedundantWatch = statusKey === 'watch'
            || _assessmentLabels.has(assessmentKey);
          const statusVariantClass =
            statusKey === 'pass' || statusKey === 'conservative' ? 'badge badge-pass'
            : statusKey === 'watch' || statusKey === 'warning' ? 'badge badge-watch'
            : statusKey === 'fail' || statusKey === 'aggressive' ? 'badge badge-fail'
            : 'badge badge-neutral';
          const assessmentVariantClass =
            assessmentKey === 'aggressive' ? 'badge badge-fail'
            : assessmentKey === 'conservative' ? 'badge badge-pass'
            : assessmentKey === 'moderate' || assessmentKey === 'balanced' ? 'badge badge-watch'
            : 'badge badge-info';
          const page = c.source_page;
          // S-13/S-31: format the source field cleanly — handles
          // arrays AND stringified Python lists.
          const sourceDocClean = _formatSourceField(c.source_doc);
          // Iter6 #1 — sourceTag string no longer needed; SourceChip
          // component renders the pill.
          const _pageForChip: string | number | null = typeof page === 'string' || typeof page === 'number'
            ? page
            : null;
          // S-12: strip outer/double-wrapped quote layers from the
          // narrative so we don't render """..."""
          const narrativeClean = _stripQuoteLayers(
            _formatDictValueDisplay(c.narrative),
          );
          return (
            <article key={i} className="ra-assum">
              <div className="ra-assum-h">
                <span className="nm">{String(c.name || '—')}</span>
                {!isRedundantWatch && (
                  <span className={statusVariantClass}>{status}</span>
                )}
              </div>
              <p className="ra-assum-x">{narrativeClean ? `"${narrativeClean}"` : '—'}</p>
              <div className="ra-assum-f">
                {/* Iter6 #1 — canonical SourceChip. */}
                <SourceChip doc={sourceDocClean || null} page={_pageForChip} />
                {/* Sprint 5 client feedback 2026-07-11 (Iter6c #2) —
                    hover tooltip on the Assessment badge shows the AI
                    rationale for the Aggressive / Conservative /
                    Regular / Moderate classification. Reads
                    c.rationale (falls back to a generic explanation
                    when the LLM didn't attach one). pre-line so the
                    reasoning renders as a scannable multi-line
                    tooltip. */}
                <span
                  className={assessmentVariantClass}
                  data-tip={(() => {
                    const _rat = String((c as Record<string, unknown>).rationale ?? '').trim();
                    if (_rat) {
                      return cleanTooltip(`Assessment — ${assessment}\n\nWhy: ${_rat}`);
                    }
                    return `Assessment — ${assessment}\n\nWhy: the AI compared the stated assumption against benchmark values and classified the polarity. No explicit rationale was attached for this row.`;
                  })()}
                  style={{ cursor: 'help', whiteSpace: 'pre-line' }}
                >
                  {assessment}
                </span>
              </div>
            </article>
          );
        })}
      </div>
      <p className="ra-note">
        Assumptions are captured as extracted text where the underlying figures
        are not separately reported; the assessment (Conservative / Regular /
        Aggressive) and flag are derived from that narrative.
      </p>
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 7 compact 5-column
   table matching `New UI Changes/risk-report.html` Section 07. No LLM
   needed — data is already structured by the DD calc engine (after
   commit ca62db6 fixed the key lookups). Variance cell goes red when
   non-zero or when status flags it for review. */
function Section7CalcVerificationCompact({ dd }: { dd: Record<string, unknown> }): JSX.Element {
  const rows = (getNested(dd, 'calculations.rows') as Array<Record<string, unknown>>) ?? [];
  if (rows.length === 0) {
    return <Pending note="No calculation verifications recorded." />;
  }
  /* Sprint 5 client feedback 2026-07-11 (Iter5 #7) — Risk §7 layout
     realigned to DD §8 "Financial Parameter Validations":
       Calculation / Stated / Computed / Variance % / Status /
       How Validated / Source
     Dropped columns: Inputs, Formula (matched DD after its Step2
     drop). "How Validated" is a first-class column now (was a
     Formula-cell hover tooltip). Source cell uses the combined
     "Investment Memo p.32" format per Iter5 #2. Status badge
     mapping mirrors DD (Verified → pass, Review → watch, Failed →
     fail, Computed → risk-low, Self-Consistency → risk-medium). */
  return (
    <div className="table-wrap">
      <table className="data-table" aria-label="Calculation verification">
        <thead>
          <tr>
            <th>Calculation</th>
            <th>Stated</th>
            <th>Computed</th>
            <th>Variance %</th>
            <th>Status</th>
            <th>How Validated</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const variance = row.variance_pct;
            const status = String(row.status || '—');
            const statusKey = status.trim().toLowerCase();
            /* Same table, same data, same styling as DD §8 "Financial Parameter
               Validations" (ReportPage.tsx) — it uses the risk-* chips, this used
               badge-* ones, so the identical status rendered differently in the two
               reports.
               And the mapping was wrong: it matched 'review required', but the data says
               "Review" — so Review fell through to grey NEUTRAL, indistinguishable from
               Computed. A variance that BREACHES tolerance was styled as if nothing had
               happened. One mapping now, matching DD exactly. */
            const statusBadge =
              statusKey === 'verified' || statusKey === 'pass' || statusKey === 'confirmed' ? 'risk-minimal'
              : statusKey === 'review' || statusKey === 'review required' || statusKey === 'watch' || statusKey === 'flag' ? 'risk-medium'
              : statusKey === 'fail' || statusKey === 'failed' || statusKey === 'critical' ? 'risk-high'
              : statusKey === 'computed' ? 'risk-low'
              : statusKey === 'self-consistency' ? 'risk-medium'
              : 'risk-neutral';
            /* What the status actually MEANS. "Computed" vs "Verified" is not obvious:
               one was checked against a stated figure, the other had no stated figure to
               check against — a difference that matters to a reviewer. */
            const statusTip =
              statusKey === 'verified' ? 'Verified — the computed value matches the stated value within the ±5% tolerance.'
              : statusKey === 'review' || statusKey === 'review required' ? 'Review — the computed value differs from the stated value by more than the ±5% tolerance. A reviewer must establish which figure is right.'
              : statusKey === 'computed' ? 'Computed — no stated value was extracted from the documents, so this figure was derived from the model inputs. There is nothing to check it against.'
              : statusKey === 'fail' || statusKey === 'failed' ? 'Failed — the calculation could not be completed from the extracted inputs.'
              : statusKey === 'skipped' ? 'Skipped — a required input was missing, so the calculation was not attempted. The missing inputs are listed below.'
              : statusKey === 'self-consistency' ? 'Self-Consistency — checked against related figures in the model rather than against a stated value.'
              : `Status: ${status}`;
            const varianceIsFail =
              variance != null &&
              variance !== '—' &&
              typeof variance === 'string' &&
              variance.includes('%') &&
              parseFloat(variance.replace('%', '').replace('+', '')) >= 5;
            const missing = Array.isArray(row.missing_inputs)
              ? (row.missing_inputs as string[])
              : [];
            const howValidated = String(row.how_validated || '');
            const srcDoc = row.source_doc ?? row.source_document ?? row.source ?? null;
            const srcPage = row.source_page ?? null;
            return (
              <tr key={i}>
                <td className="td-name">{String(row.calculation ?? '—')}</td>
                <td className="t-mono t-xs">{String(row.stated ?? '—')}</td>
                <td className="t-mono t-xs">{String(row.computed ?? '—')}</td>
                <td className={`t-mono t-xs ${varianceIsFail ? 't-fail' : 't-muted'}`}>
                  {variance != null && variance !== '' ? String(variance) : '—'}
                </td>
                <td>
                  <span className={statusBadge} data-tip={statusTip}>{status}</span>
                  {status === 'Skipped' && missing.length > 0 && (
                    <div className="t-xs t-muted" style={{ marginTop: 4 }}>
                      missing: {missing.join(', ')}
                    </div>
                  )}
                </td>
                <td className="t-xs" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                  {howValidated || <span className="t-muted">—</span>}
                </td>
                <td className="t-xs">
                  {/* Iter6 #1 — canonical SourceChip. */}
                  <SourceChip
                    doc={srcDoc != null ? String(srcDoc) : null}
                    page={srcPage != null ? String(srcPage) : null}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="ra-note">
        Computed values are re-derived from extracted cash flows and model
        inputs; any value with a non-zero variance against the stated figure
        is flagged for review. Skipped rows list the input(s) that were
        missing. Layout matches DD "Financial Parameter Validations".
      </p>
    </div>
  );
}


/* Reference: kept in module to satisfy strict-unused after Section 7
   redesign — DdCalcTable was the previous renderer; Section7CalcVerificationCompact
   replaced it. Touching this line keeps the export tree stable. */
void DdCalcTable;

/* Sprint 5 client feedback 2026-06-20: Section 9 redesign matching
   `New UI Changes/risk-report.html` Section 09. The legacy SpaFlagsTable
   was 3 columns (ID, Description, Severity). Design wants 4 columns
   adding "Action Required" — which the backend already populates for
   every SPA flag (see SPA.yaml severity rules). No LLM needed. */
function Section9CriticalLegalFindings({
  risk, spa, ddReport,
}: {
  risk: Record<string, unknown>;
  spa: Record<string, unknown>;
  ddReport?: Record<string, unknown> | undefined;
}): JSX.Element {
  /* Sprint 5 client feedback 2026-07-09 follow-up 8 + 2026-07-11
     Iter4e — §9 consumes the canonical F-XX findings and filters to
     Critical rows only (the section title is "Critical Legal
     Findings"). The prior filter included High too; the Iter4e
     client invariant is count(§9) == count(§11 rows where
     severity=Critical), so High rows now stay in §11 without also
     surfacing here. Legal-area filter retained per the section
     title's "Legal" scope. */
  const _canon = buildCanonicalFindings(risk, spa, ddReport ?? null);
  const _legalCritHigh = _canon
    .filter((f) => f.area === 'Legal' && f.severity === 'Critical')
    .map((f) => ({
      id: f.display_id,
      original_id: f.original_id,
      area: 'Legal' as const,
      severity: f.severity,
      title: f.title,
      description: f.description,
      source_page: f.source_page,
      action_required: f.action_required,
      responsible: f.responsible,
      attribute_key: f.attribute_key,
    }));
  // Preserve the two variable names the legacy render code below reads.
  void ddReport;
  /* Sprint 5 client feedback 2026-07-09 (cross-report consistency
     ticket, Phase 2) — §9 now consumes SPA §5 directly via
     getSpaIssueTableRows + a severity === 'Critical' filter. The
     LLM-narration-based Legal-keyword scanner below is retired —
     it's kept in the module for reference only. The §9 card list
     is now byte-identical to the Critical rows of SPA §5.

     RG-4 (BLOCKING) — count(Risk §9 cards) == count(SPA §5 rows
     where Severity = Critical). */
  // Canonical replaces all prior SPA §5 walk + fallbacks. Rows below
  // are already F-XX numbered from _legalCritHigh (built at top of
  // function via buildCanonicalFindings).
  const criticalSpa: Array<Record<string, unknown>> = _legalCritHigh;

  // Legacy vars preserved only to satisfy the message wording below
  // and downstream typed references (variables still read by the
  // gap-note + rendering code further down).
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const synthFindings = (synth?.consolidated_findings as Array<Record<string, unknown>> | undefined) ?? [];
  const _LEGAL_KEYWORD_RE = /\b(spa|legal|governing law|representations and warranties|reps?\s*&?\s*warranties|indemnification|anti.?dilution|tag.?along|drag.?along|rofr|escrow|confidentiality clause|investor protection|share purchase agreement)\b/i;
  void _LEGAL_KEYWORD_RE;
  // Reference retained for symmetry with §11 fallback semantics.
  const legalFindings = criticalSpa;
  void ((): Array<Record<string, unknown>> => {
    // Retained (dead) scanner — kept for reference should the ticket
    // ever be reverted. See git blame for the original filter shape.
    return synthFindings.filter((f) => {
      const area = String(f.area ?? '').toLowerCase();
      if (area === 'legal') return true;
    const rawSources = f.sources;
    const sourcesStr = Array.isArray(rawSources)
      ? (rawSources as unknown[]).map(String).join(',')
      : String(rawSources ?? '');
    if (/\bspa\b/i.test(sourcesStr)) return true;
    const id = String(f.id ?? '');
    if (id.toUpperCase().startsWith('SPA-')) return true;
    // Title / description keyword scan — catches findings the LLM
    // typed as legal-content but tagged with a different area.
    const title = String(f.title ?? '');
    const description = String(f.description ?? '');
    if (_LEGAL_KEYWORD_RE.test(title) || _LEGAL_KEYWORD_RE.test(description)) {
      return true;
    }
    return false;
    });
  })();

  /* Sprint 5 client feedback 2026-07-09 — legalFindings is now
     the SPA §5 Critical slice (see top of function). The fallback
     to `spa.flags` / `spa.sections.flags_section.rows` is retained
     only for the legacy "SPA workflow ran but produced no
     attribute_groups" edge case; on modern runs criticalSpa is
     the authoritative source. */
  const sourceFlags = (
    legalFindings.length > 0
      ? legalFindings
      : ((getNested(spa, 'flags') as Array<Record<string, unknown>> | undefined) ?? [])
  );
  /* Same fallback as the synth pass — pre-Sprint5 reports stored
     flags at sections.flags_section.rows instead of flags. */
  const flagsFromSections = sourceFlags.length === 0
    ? ((getNested(spa, 'sections.flags_section.rows') as Array<Record<string, unknown>> | undefined) ?? [])
    : [];
  const flags = sourceFlags.length > 0 ? sourceFlags : flagsFromSections;
  if (flags.length === 0) {
    // Sprint 5 client feedback 2026-06-24 — when synthFindings is
    // populated but NONE qualify as legal under the broader filter,
    // the SPA workflow genuinely produced no critical legal items.
    // Message wording differentiates that "clean review" case from
    // the "synth not run" case the user actually wants to know
    // about.
    if (synthFindings.length > 0) {
      return (
        <Pending note="No critical legal findings — the SPA workflow detected all mandatory sections + investor-protection clauses, and no SPA-area issues qualified for Section 9 escalation. (See Section 11 Consolidated Findings for the full list across DD + SPA + Risk.)" />
      );
    }
    return <Pending note="Risk Assessment synthesis pass hasn't run yet — re-run Risk Assessment to populate findings via the LLM consolidation pass." />;
  }
  /* Sprint 5 client feedback 2026-07-07 (BE Issue #8) — sort by
     numeric finding ID ascending, NOT severity. The reviewer expects
     to scan the list in F-01 / F-02 / F-03 order so numeric
     cross-reference to §11 stays sane. Severity is still visible
     via the badge on each card.

     IDs are canonical DB values (no renumbering per view), so the
     sorted list can have gaps — e.g. F-04, F-07 might belong to
     non-Legal categories and live only in §11. The gap-note
     computation below surfaces those missing IDs so the reviewer
     knows where to find them. */
  const _extractNumericId = (id: unknown): number => {
    const m = /(\d+)/.exec(String(id ?? ''));
    return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
  };
  const sorted = [...flags].sort((a, b) => {
    const na = _extractNumericId(a.id ?? a.flag_id);
    const nb = _extractNumericId(b.id ?? b.flag_id);
    return na - nb;
  });
  /* Sprint 5 client feedback 2026-06-23 (Second-Set FE Issue 6):
     §9 Critical Legal Findings now renders as cards instead of a
     table. Each card has severity-coloured left-border stripe,
     Issue ID + severity chip top, description in larger text,
     "Action Required" callout in a tinted box below. Cards ordered
     Critical → High → Medium → Low. Caps at 30 cards (same as
     prior table to keep the section a reasonable length). */
  /* Sprint 5 client feedback 2026-07-12 (Iter9 #4) — new colour
   * scheme: High=Red, Medium=Amber, Low=Green, Recommendation=Blue.
   * Critical folds into High for legacy rows. */
  const sevColor = (sev: string): string => {
    const s = sev.toLowerCase();
    if (s === 'high' || s === 'critical' || s === 'reject' || s === 'severe' || s === 'fail' || s === 'flag') return 'var(--fail)';
    if (s === 'medium' || s === 'watch' || s === 'moderate') return 'var(--watch)';
    if (s === 'recommendation' || s === 'opportunity' || s === 'info') return 'var(--info)';
    if (s === 'low' || s === 'pass' || s === 'minor') return 'var(--pass)';
    return 'var(--muted)';
  };
  /* Sprint 5 client feedback 2026-07-12 (issues/11 follow-up) —
   * §9 card ID + description sanitization:
   *   1. Prefer the canonical F-XX display_id over the raw flag_id.
   *      The canonical builder puts F-XX in `id` / `display_id`;
   *      only fall back to `flag_id` when neither is set.
   *   2. Strip the boilerplate "'Name' — Status: review clause
   *      status" pattern from the description so the reader sees
   *      a business-language sentence, not the debug composition. */
  return (
    <div style={{ display: 'grid', gap: 'var(--s3)' }}>
      {sorted.slice(0, 30).map((f, i) => {
        /* Prefer canonical display_id (F-XX). Fall back to raw flag_id
         * only when nothing else is available so we never leak
         * SPA-CLAUSE-* internal identifiers to the reader. */
        const _fExt = f as unknown as { display_id?: string };
        const _rawId = String(f.id ?? f.flag_id ?? `ISS-${String(i + 1).padStart(3, '0')}`);
        const id = String(_fExt.display_id ?? '').trim() || (
          /^F-\d+$/i.test(_rawId) ? _rawId : `F-${String(i + 1).padStart(2, '0')}`
        );
        const severity = _normSeverity(f.severity);
        const description = stripClauseBoilerplate(String(f.description ?? f.title ?? f.text ?? '—'), '—');
        const action = String(f.recommendation ?? f.action_required ?? f.action ?? '');
        const accent = sevColor(severity);
        /* Sprint 5 client feedback 2026-07-11 (Iter5 #8) — analyst
           asked for a hover tooltip on each finding surfacing the
           extra detail beyond the short label. Aggregate ID + full
           description + action into one multi-line tooltip so a
           single hover reveals everything. */
        const _tooltip = [
          `${id} — Severity: ${severity}`,
          description ? `Finding: ${description}` : null,
          action ? `Action: ${action}` : null,
        ].filter(Boolean).join('\n\n');
        return (
          <article
            key={i}
            data-tip={cleanTooltip(_tooltip)}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--lines)',
              borderLeft: `4px solid ${accent}`,
              borderRadius: 'var(--r-md)',
              padding: 'var(--s4) var(--s5)',
              display: 'grid',
              gap: 'var(--s3)',
              cursor: 'help',
              whiteSpace: 'pre-line',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)', flexWrap: 'wrap' }}>
              <span className="t-mono t-xs" style={{ fontWeight: 600, color: 'var(--text-sec)' }}>{id}</span>
              <span className={_severityBadgeClass(severity)}>{severity}</span>
            </div>
            <div style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>{description}</div>
            {/* Sprint 5 client feedback 2026-06-25 (Issues/§13 Material
                Items #2) — analyst confirmed "Action Required" callout
                is not needed. The recommendation / action_required
                text is preserved in the underlying data (and surfaces
                in §15 Recommendations as "Management Action") so no
                information is lost; the callout is just dropped from
                the §9 card. */}
            {void action}
          </article>
        );
      })}
      {/* Sprint 5 client feedback 2026-07-12: the "omitted findings" footnote is gone.
          §9 numbers its own cards F-01.. independently of §11, so the two sections use
          colliding id spaces — any cross-reference between them misleads the reader
          regardless of how the omitted set is computed. */}
    </div>
  );
}

/* Sprint 5 client feedback 2026-06-20: Section 10 redesign matching
   `New UI Changes/risk-report.html` Section 10. The design shows a
   simple 3-column table (Clause | Present | Assessment) using the same
   clause set the Section 8 cards are based on. We reuse the
   legal_assessment_summary.cards LLM output to keep the two sections
   consistent. Falls back to the legacy SpaClauseTable when synthesis
   hasn't run. */
function Section10MandatoryClauseCompliance({
  risk,
  fallbackSpa,
  jurisdictionOverride,
}: {
  risk: Record<string, unknown>;
  fallbackSpa: Record<string, unknown>;
  jurisdictionOverride?: string;
}): JSX.Element {
  const enrichment = risk.enrichment as Record<string, unknown> | null;
  const synth = enrichment?.synthesis as Record<string, unknown> | undefined;
  const legal = synth?.legal_assessment_summary as Record<string, unknown> | undefined;
  const cards = (legal?.cards as Array<Record<string, unknown>> | undefined) ?? [];

  if (cards.length === 0) {
    return (
      <>
        <SpaClauseTable spa={fallbackSpa} jurisdictionOverride={jurisdictionOverride} />
        <div style={{ marginTop: 'var(--s3)' }}>
          <Pending note="Mandatory clause compliance view requires the Section 8 legal synthesis — re-run Risk Assessment to populate." />
        </div>
      </>
    );
  }

  /* Sprint 5 client feedback 2026-06-22 (Second-Set SPA Issue N6):
     the mandatory-clause compliance table used to show only the clause
     name + status + assessment text — the analyst could see "Governing
     Law: Present, p.20" in the rationale but had no idea which uploaded
     document (SPA / IRA / SHA / Escrow) backed it. Now we look up the
     synthesizer's area label against the underlying SPA attribute_results
     (section_* + clause_*) and surface source_document + source_page in
     a new Source column. */
  const spaAttrs = (fallbackSpa?.attribute_results as Record<string, unknown> | undefined) ?? {};
  // Sprint 5 client feedback 2026-07-03 — 'exit rights' was mapped to
  // the legacy `clause_exit_rights` key which no longer exists (the
  // §4.5 refactor split it into three separate clauses:
  // clause_exit_drag_along, clause_exit_tag_along_cosale,
  // clause_exit_rofr_transfer). We now list all three so the
  // source-lookup finds the first one with populated provenance
  // instead of returning null.
  const _AREA_TO_ATTR_KEYS: Record<string, string[]> = {
    'investor protection': ['clause_investor_protection_present'],
    'indemnification': ['clause_indemnification_cap_pct'],
    'anti-dilution': ['clause_antidilution_type', 'clause_antidilution_esop_carveout_pct'],
    'board rights': ['clause_board_representation'],
    'exit rights': [
      'clause_exit_drag_along',
      'clause_exit_tag_along_cosale',
      'clause_exit_rofr_transfer',
    ],
    'confidentiality': ['clause_confidentiality_years'],
    'dispute resolution': ['clause_dispute_resolution_method'],
    'governing law': ['section_governing_law'],
    'parties and recitals': ['section_parties', 'section_recitals'],
    'purchase price': ['section_purchase_price'],
    'representations and warranties': ['section_representations'],
    'execution and signatures': ['section_signatures'],
    'closing conditions': ['section_closing_conditions'],
  };
  function _sourceFor(area: string): { doc: string | null; page: number | null } {
    const keys = _AREA_TO_ATTR_KEYS[area.trim().toLowerCase()] ?? [];
    for (const key of keys) {
      const attr = spaAttrs[key] as Record<string, unknown> | undefined;
      if (!attr) continue;
      const doc = (attr.source_document_label as string | null | undefined)
        ?? (attr.source_document as string | null | undefined)
        ?? null;
      const page = (attr.source_page as number | null | undefined) ?? null;
      if (doc || page != null) {
        return { doc: doc ? String(doc) : null, page };
      }
    }
    return { doc: null, page: null };
  }

  // Sprint 5 client feedback 2026-07-01 — §10 restructured into three
  // sub-tables split by BE-tagged card_type. Previously all cards
  // (mandatory sections + investor protection) rendered in one flat
  // table; now Investor Protection is its own labelled sub-heading
  // (heading + Severity column added), and the Universal / Jurisdiction
  // tables below gain an Assessment column so all three surfaces show
  // the same shape.
  const _presentLabel = (status: string): { label: string; klass: string } => {
    const sk = String(status ?? '').trim().toLowerCase();
    /* Sprint 5 client feedback 2026-07-11 (Iter5 #9) — "Standard"
       is the SPA §4 Investor Protection vocabulary for a benchmark-
       satisfying clause; when it flows through to §10 Universal /
       Jurisdictional rows the analyst expects the same green Pass
       treatment they see in §4. Map Standard alongside Present /
       Adequate / Secured. */
    if (sk === 'standard' || sk === 'pass') {
      return { label: 'Pass', klass: 'badge badge-pass' };
    }
    if (sk === 'adequate' || sk === 'present' || sk === 'secured' || sk === 'satisfactory') {
      return { label: 'Present', klass: 'badge badge-pass' };
    }
    if (sk === 'partial' || sk === 'conditional' || sk === 'watch') {
      return { label: 'Conditional', klass: 'badge badge-watch' };
    }
    if (sk === 'deficient' || sk === 'missing' || sk === 'absent') {
      return { label: 'Missing', klass: 'badge badge-fail' };
    }
    if (sk === 'cannot assess' || sk === 'cannot_assess') {
      return { label: 'Cannot Assess', klass: 'badge badge-watch' };
    }
    if (sk === 'not applicable' || sk === 'n/a' || sk === 'na') {
      return { label: 'N/A', klass: 'badge badge-neutral' };
    }
    return { label: String(status ?? '—'), klass: 'badge badge-neutral' };
  };
  /* Sprint 5 client feedback 2026-07-07 (BE Issue #9) — derive
     Severity from Status deterministically. Prior renderer read
     c.severity from the BE card and defaulted to "—" when missing,
     which produced two visible bugs:
       (a) same status ("Conditional") got different severities on
           different rows depending on whether the LLM happened to
           emit a c.severity field.
       (b) Jurisdiction-Specific Clauses showed "—" for every row
           because the BE never populated c.severity for them.
     Now severity is a pure function of status — always consistent,
     always populated. */
  /* Sprint 5 client feedback 2026-07-11 (Iter6c #4) — for §10
     Universal SPA Structure + Jurisdiction-Specific Clauses
     sub-tables, positive cases (present / adequate / secured /
     standard / satisfactory) render "Pass" (green), and negative
     cases (missing / deficient / absent) render "Critical" (red).
     Prior mapping used "Standard" (neutral grey) for positive cases,
     which read as "no verdict" — client wants an unambiguous
     Pass/Critical signal. Watch / Cannot Assess stay as Watch. */
  const _severityFromStatus = (status: string): { label: string; klass: string } => {
    const sk = String(status ?? '').trim().toLowerCase();
    if (sk === 'deficient' || sk === 'missing' || sk === 'absent') {
      return { label: 'Critical', klass: 'badge badge-fail' };
    }
    if (sk === 'partial' || sk === 'conditional' || sk === 'watch') {
      return { label: 'Watch', klass: 'badge badge-watch' };
    }
    if (sk === 'cannot assess' || sk === 'cannot_assess') {
      return { label: 'Medium', klass: 'badge badge-watch' };
    }
    if (sk === 'adequate' || sk === 'present' || sk === 'secured' || sk === 'satisfactory' || sk === 'standard' || sk === 'pass') {
      return { label: 'Pass', klass: 'badge badge-pass' };
    }
    if (sk === 'not applicable' || sk === 'n/a' || sk === 'na') {
      return { label: 'N/A', klass: 'badge badge-neutral' };
    }
    return { label: '—', klass: 'badge badge-neutral' };
  };
  const _cardSource = (c: Record<string, unknown>): { doc: string | null; page: number | null } => {
    // Sprint 5 client feedback 2026-07-07 (BE Issue #9) — MERGE the
    // BE-tagged card source with the attribute-level source lookup.
    // Prior renderer preferred BE tagging outright and only fell back
    // to _sourceFor when the BE had NEITHER doc nor page. That produced
    // "SPA" without a page on cards where BE tagged source_doc="SPA"
    // but source_page=null even though the underlying SPA attribute
    // HAD a page (e.g. Drag-Along Rights). Now: BE fields win where
    // present, but a missing page falls through to the attribute-level
    // page lookup — same for doc.
    const cDoc = c.source_doc;
    const cPage = c.source_page;
    const beDoc = cDoc != null ? String(cDoc) : null;
    const bePage = typeof cPage === 'number' ? cPage : null;
    if (beDoc && bePage != null) return { doc: beDoc, page: bePage };
    const fromArea = _sourceFor(String(c.area ?? ''));
    return {
      doc: beDoc ?? fromArea.doc,
      page: bePage ?? fromArea.page,
    };
  };
  /* Sprint 5 client feedback 2026-07-07 (BE Issue #9) — sanitise the
     Assessment column so we never emit "Detected in —." (source
     placeholder leak) or an unhelpful bare em-dash. When the LLM's
     rationale is broken or empty, derive a truthful sentence from
     (status, source). Keep the LLM's rationale otherwise. */
  const _assessmentText = (
    status: string,
    rationale: string,
    src: { doc: string | null; page: number | null },
  ): string => {
    const cleanRationale = rationale.trim();
    const rationaleBroken =
      !cleanRationale ||
      cleanRationale === '—' ||
      /detected in\s*—/i.test(cleanRationale) ||
      /in\s*—\s*p\.?\s*—/i.test(cleanRationale);
    if (!rationaleBroken) return cleanRationale;
    const label = _presentLabel(status).label;
    const srcStr = src.doc
      ? `${src.doc}${src.page != null ? ` p.${src.page}` : ''}`
      : null;
    if (label === 'Present') {
      return srcStr ? `Detected in ${srcStr}.` : 'Detected in the uploaded documents.';
    }
    if (label === 'Conditional') {
      return srcStr
        ? `Partial / non-standard terms detected in ${srcStr}. Needs review.`
        : 'Partial or non-standard terms detected. Needs review.';
    }
    if (label === 'Missing') {
      return 'Section not detected in any uploaded document.';
    }
    if (label === 'Cannot Assess') {
      return 'Canonical source document is not in the upload — presence cannot be determined.';
    }
    if (label === 'N/A') {
      return 'Not applicable to this deal type.';
    }
    return srcStr ? `See ${srcStr}.` : '—';
  };

  const ipCards = cards.filter((c) => String(c.card_type ?? '').toLowerCase() === 'investor_protection');
  const univCards = cards.filter((c) => String(c.card_type ?? '').toLowerCase() === 'universal');
  const jurCards = cards.filter((c) => String(c.card_type ?? '').toLowerCase() === 'jurisdiction');

  // Legacy fallback: when the BE hasn't tagged card_type (pre-cca0a2c
  // runs), treat every card as investor_protection so the flat list
  // still renders under the Investor Protection heading and doesn't
  // silently disappear.
  const untaggedFallback = cards.filter((c) => !c.card_type);
  const _ipToRender = ipCards.length > 0 ? ipCards : untaggedFallback;

  /* Sprint 5 client feedback 2026-07-08 (FE Ticket — Risk §10 badge
     reconciliation).

     Two bugs on EH2 today:
       (a) Investor Protection row for "Dispute Resolution Method"
           renders Missing / Critical while Source =
           "InvestorsRights · p.22" and Assessment = "Detected in
           InvestorsRights p.22." — the row is contradictory. The
           BE emits status="Missing" but source_doc + source_page
           are populated.
       (b) The Jurisdiction-Specific table's row for "Dispute
           Resolution" (same underlying clause) correctly reads
           Present at InvestorsRights p.22 — so the two tables
           disagree on the same canonical clause.

     Two-layer reconciliation (FE render-time only for now — the
     full BE canonical registry is a follow-up ticket):

     Layer 1 — canonical-name index across all three tables.
       Build a map of {canonical_area_key: PresentEvidence} from
       every card that reports Present with a source. Any row in
       any table whose canonical area matches an entry in the map
       inherits Present + evidence from the winning row, regardless
       of its own status/source fields.

     Layer 2 — within-row impossible-state override.
       A row whose status is Missing but whose Source is populated
       is impossible under the FIX 1 atomic-derivation rule. Detect
       and override to Present. Log a console.warn so operators can
       grep the noise.

     Both layers are pure display-time reconciliation. The
     underlying BE payload is not mutated — the FE just refuses to
     render impossible states. */

  const _canonicalAreaKey = (raw: string): string => {
    /* Collapse taxonomy variants for the same underlying clause:
       "Dispute Resolution Method" → "dispute resolution"
       "Dispute Resolution Jurisdiction" → "dispute resolution"
       "Anti-Dilution Type" → "anti-dilution"
       etc. Any label that ends with a modifier word ("Method",
       "Type", "Jurisdiction", "Period", "Cap") is stripped so the
       parent clause label is what the map keys on. */
    let key = String(raw ?? '').trim().toLowerCase();
    // Strip common sub-attribute suffix words.
    key = key.replace(
      /\s+(method|methods|type|types|jurisdiction|period|cap|%|percentage|years?)\s*$/i,
      '',
    );
    // Also strip leading article + punctuation noise.
    key = key.replace(/^(the |a |an )/, '').replace(/[.:;,()]+/g, ' ');
    return key.replace(/\s+/g, ' ').trim();
  };

  interface PresentEvidence {
    doc: string | null;
    page: number | null;
    rationale: string;
    origin_area: string;
  }

  const _canonicalPresenceIndex = (() => {
    const idx = new Map<string, PresentEvidence>();
    for (const c of [...ipCards, ..._ipToRender, ...univCards, ...jurCards]) {
      const area = String(c.area ?? '');
      if (!area) continue;
      const status = String(c.status ?? '').trim().toLowerCase();
      const src = _cardSource(c);
      const hasSrc = Boolean(src.doc || src.page != null);
      const isPresent =
        status === 'adequate' || status === 'present' ||
        status === 'secured' || status === 'satisfactory';
      /* Winning-row rule: a card contributes to the presence index
         when EITHER (a) status explicitly Present, OR (b) source is
         populated (which under FIX 1's atomic-derivation rule means
         detection succeeded — a stale Missing badge alongside a
         real source is exactly the bug we're fixing). Present cards
         win over Missing-with-source cards on tie-break. */
      if (!isPresent && !hasSrc) continue;
      const key = _canonicalAreaKey(area);
      if (!key) continue;
      const existing = idx.get(key);
      const rationale = String(c.rationale ?? '');
      const candidate: PresentEvidence = {
        doc: src.doc,
        page: src.page,
        rationale,
        origin_area: area,
      };
      if (!existing) {
        idx.set(key, candidate);
      } else if (isPresent && !existing.doc && candidate.doc) {
        // Present card with source beats a source-only card.
        idx.set(key, candidate);
      }
    }
    return idx;
  })();

  const _reconcileCard = (
    c: Record<string, unknown>,
  ): {
    status: string;
    src: { doc: string | null; page: number | null };
    rationale: string;
    reconciled: boolean;
    reconcile_reason: string | null;
  } => {
    const status = String(c.status ?? '');
    const src = _cardSource(c);
    const rationale = String(c.rationale ?? '');
    const label = _presentLabel(status).label;

    // Layer 1 — canonical-name lookup across all three tables.
    const canonKey = _canonicalAreaKey(String(c.area ?? ''));
    const canonHit = canonKey ? _canonicalPresenceIndex.get(canonKey) : undefined;
    if (
      canonHit &&
      (label === 'Missing' || label === 'Cannot Assess') &&
      canonHit.doc
    ) {
      return {
        status: 'Present',
        src: { doc: canonHit.doc, page: canonHit.page },
        rationale: canonHit.rationale || rationale,
        reconciled: true,
        reconcile_reason:
          `cross-table canonical match on "${canonKey}" from row "${canonHit.origin_area}"`,
      };
    }

    // Layer 2 — within-row impossible-state override.
    // BE emitted status=Missing/Cannot-Assess but source is populated.
    if (
      (label === 'Missing' || label === 'Cannot Assess') &&
      (src.doc || src.page != null)
    ) {
      return {
        status: 'Present',
        src,
        rationale,
        reconciled: true,
        reconcile_reason:
          `within-row: status=${label} but source doc/page populated`,
      };
    }

    // Reverse assertion — Present badge without source is also
    // impossible under FIX 1. Do NOT override (badge is more visible
    // than source), but warn.
    if (label === 'Present' && !src.doc && src.page == null) {
      console.warn(
        `[Risk §10] Row "${String(c.area ?? '')}" claims Present but has ` +
        `no source doc or page. Verify BE detection output.`,
      );
    }

    return { status, src, rationale, reconciled: false, reconcile_reason: null };
  };

  const _subHeading = (label: string, count: string): JSX.Element => (
    <h3 style={{
      margin: '0 0 var(--s2) 0',
      fontFamily: 'var(--font-body)',
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--muted)',
    }}>
      {label} {count && <span style={{ marginLeft: 6, fontWeight: 500 }}>({count})</span>}
    </h3>
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--s5)' }}>
      {/* Sub-heading 1 — Investor Protection Clauses */}
      {_ipToRender.length > 0 && (
        <div className="table-wrap">
          {_subHeading('Investor Protection Clauses',
            /* Sprint 5 client feedback 2026-07-08 (FE Ticket — IP
               counter "N of M present" semantics). "Present" means
               anything the LLM / util could pin down to a concrete
               directional label — Standard, Pass, Investor-friendly,
               Conservative, Aggressive, Inadequate, Watch, Not
               Applicable all count. The only row that does NOT count
               is "Not Present" (terminal absence). Equivalently:
               N = M − (count of "Not Present" rows).

               Was previously counting status === 'Pass' which
               excluded Watch / Conservative / Aggressive / Inadequate
               even though those clauses ARE present in the doc — the
               LLM just judged them non-standard. That undercounted
               the header (EH2 read "4 of 11" when 7 of 11 rows had a
               populated directional judgement). */
            (() => {
              const presentCount = _ipToRender.filter((c) => {
                // Sprint 5 client feedback 2026-07-09 — pipe source
                // through so the util evaluates the same precedence
                // ladder used by the row renderer below. See the
                // comment there for the full rationale.
                const cardRec = _reconcileCard(c);
                const spa4RowShape = {
                  category_assigned: c.category_assigned,
                  value: c.value,
                  raw_value: (c as Record<string, unknown>).raw_value,
                  source_document: cardRec.src.doc,
                  source_page: cardRec.src.page,
                  attribute_key: c.attr_key,
                  display_name: c.area,
                } as unknown as Parameters<typeof computeSpa4Status>[0];
                return computeSpa4Status(spa4RowShape).aiAnalysisLabel !== 'Not Present';
              }).length;
              return `${presentCount} of ${_ipToRender.length} present`;
            })()
          )}
          <table className="data-table" aria-label="Investor Protection Clauses">
            <thead>
              <tr>
                <th>Clause</th>
                <th>Present</th>
                <th>Source · Page</th>
                <th>Severity</th>
                <th>Assessment</th>
              </tr>
            </thead>
            <tbody>
              {_ipToRender.map((c, i) => {
                const area = String(c.area ?? '—');
                /* Sprint 5 client feedback 2026-07-08 (FE Ticket —
                   Risk §10 IP sub-table consumes computeSpa4Status).
                   The Present + Severity columns for IP rows are
                   now derived from the shared computeSpa4Status
                   util so this sub-table and SPA §4 are byte-
                   identical in AI Analysis label + Status pair.

                   The card carries the raw SPA attribute state via
                   the BE tagging in synthesizer.py cards.append
                   (category_assigned + value). We adapt to the
                   SpaClauseRow shape the util expects with a small
                   local cast. Source · Page + Assessment columns
                   flow through the existing reconciler
                   unchanged. */
                const rec = _reconcileCard(c);
                const src = rec.src;
                /* Sprint 5 client feedback 2026-07-09 — pipe
                   source_document + source_page into the row shape.
                   Prior version omitted them, which made
                   computeSpa4Status think Source ∈ EMPTY_SET on every
                   row (Path 3 wins → aiAnalysisLabel falls through to
                   the stored category or "Not Present"). That
                   silently bypassed Path B (extraction failure with
                   populated source → Watch / Medium), so EH2
                   Drag-Along rendered "Standard / Pass" instead of
                   "Watch / Medium" and diverged from SPA §4. Source
                   is now taken from the reconciler's resolved `src`
                   (same fields SPA §4 sees), so the util evaluates
                   the same precedence ladder and produces byte-
                   identical Present + Severity to SPA §4. */
                const spa4RowShape = {
                  category_assigned: c.category_assigned,
                  value: c.value,
                  raw_value: (c as Record<string, unknown>).raw_value,
                  source_document: src.doc,
                  source_page: src.page,
                  attribute_key: c.attr_key,
                  display_name: area,
                } as unknown as Parameters<typeof computeSpa4Status>[0];
                const spa4 = computeSpa4Status(spa4RowShape);
                /* Defensive guard (WORK 7): when the BE didn't tag
                   this card with category_assigned + value (legacy
                   payload, or unmapped clause), computeSpa4Status
                   still returns a well-formed shape by falling
                   through to its "no category" branch. The util
                   maps missing-value → Not Present + Critical,
                   which is the correct terminal semantic. Log so
                   QA can catch missing BE tagging. */
                const bothMissing = (
                  c.category_assigned == null && c.value == null
                );
                if (bothMissing) {
                  console.warn(
                    `[Risk §10 IP] row "${area}" has no category_assigned or value ` +
                    `on the card. Falling through to computeSpa4Status defaults.`,
                  );
                }
                const assessment = _assessmentText(
                  rec.status, rec.rationale, src,
                );
                if (rec.reconciled) {
                  console.info(
                    `[Risk §10 IP] reconciled row "${area}": ${rec.reconcile_reason}`,
                  );
                }
                return (
                  <tr key={`ip-${i}`}>
                    <td className="td-name">
                      {area}
                      {rec.reconciled && (
                        <span
                          className="t-xs"
                          style={{ marginLeft: 6, color: 'var(--muted)', fontStyle: 'italic' }}
                          data-tip={rec.reconcile_reason ? cleanTooltip(rec.reconcile_reason) : undefined}
                        >
                          (reconciled)
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={spa4.aiAnalysisClass}>{spa4.aiAnalysisLabel}</span>
                    </td>
                    <td className="t-xs">
                      {/* Iter6 #1 — canonical SourceChip. */}
                      <SourceChip doc={src.doc ?? null} page={src.page ?? null} />
                    </td>
                    <td>
                      <span className={spa4.statusClass}>{spa4.status}</span>
                    </td>
                    <td className="t-xs">{assessment}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sub-heading 2 — Universal SPA Structure */}
      {univCards.length > 0 && (
        <div className="table-wrap">
          {_subHeading('Universal SPA Structure',
            `${univCards.filter((c) => _presentLabel(_reconcileCard(c).status).label === 'Present').length} of ${univCards.length} detected`
          )}
          <table className="data-table" aria-label="Universal SPA Structure">
            <thead>
              <tr>
                <th>Section</th>
                <th>Present</th>
                <th>Source · Page</th>
                {/* Sprint 5 client feedback 2026-07-07 (BE Issue #9) —
                    Severity column added so all three sub-tables share
                    the same shape and taxonomy. */}
                <th>Severity</th>
                <th>Assessment</th>
              </tr>
            </thead>
            <tbody>
              {univCards.map((c, i) => {
                const area = String(c.area ?? '—');
                const rec = _reconcileCard(c);
                const p = _presentLabel(rec.status);
                const sev = _severityFromStatus(rec.status);
                const src = rec.src;
                const assessment = _assessmentText(rec.status, rec.rationale, src);
                if (rec.reconciled) {
                  console.info(`[Risk §10 Universal] reconciled row "${area}": ${rec.reconcile_reason}`);
                }
                return (
                  <tr key={`univ-${i}`}>
                    <td className="td-name">
                      {area}
                      {rec.reconciled && (
                        <span
                          className="t-xs"
                          style={{ marginLeft: 6, color: 'var(--muted)', fontStyle: 'italic' }}
                          data-tip={rec.reconcile_reason ? cleanTooltip(rec.reconcile_reason) : undefined}
                        >
                          (reconciled)
                        </span>
                      )}
                    </td>
                    <td><span className={p.klass}>{p.label}</span></td>
                    <td className="t-xs">
                      {/* Iter6 #1 — canonical SourceChip. */}
                      <SourceChip doc={src.doc ?? null} page={src.page ?? null} />
                    </td>
                    <td>
                      <span className={sev.klass}>{sev.label}</span>
                    </td>
                    <td className="t-xs">{assessment}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sub-heading 3 — Jurisdiction-Specific Clauses */}
      {jurCards.length > 0 && (
        <div className="table-wrap">
          {_subHeading('Jurisdiction-Specific Clauses',
            `${jurCards.filter((c) => _presentLabel(_reconcileCard(c).status).label === 'Present').length} of ${jurCards.length} detected`
          )}
          <table className="data-table" aria-label="Jurisdiction-Specific Clauses">
            <thead>
              <tr>
                <th>Clause</th>
                <th>Present</th>
                <th>Source · Page</th>
                <th>Severity</th>
                <th>Assessment</th>
              </tr>
            </thead>
            <tbody>
              {jurCards.map((c, i) => {
                const area = String(c.area ?? '—');
                const rec = _reconcileCard(c);
                const p = _presentLabel(rec.status);
                const sev = _severityFromStatus(rec.status);
                const src = rec.src;
                const assessment = _assessmentText(rec.status, rec.rationale, src);
                if (rec.reconciled) {
                  console.info(`[Risk §10 Jur] reconciled row "${area}": ${rec.reconcile_reason}`);
                }
                return (
                  <tr key={`jur-${i}`}>
                    <td className="td-name">
                      {area}
                      {rec.reconciled && (
                        <span
                          className="t-xs"
                          style={{ marginLeft: 6, color: 'var(--muted)', fontStyle: 'italic' }}
                          data-tip={rec.reconcile_reason ? cleanTooltip(rec.reconcile_reason) : undefined}
                        >
                          (reconciled)
                        </span>
                      )}
                    </td>
                    <td><span className={p.klass}>{p.label}</span></td>
                    <td className="t-xs">
                      {/* Iter6 #1 — canonical SourceChip. */}
                      <SourceChip doc={src.doc ?? null} page={src.page ?? null} />
                    </td>
                    <td>
                      <span className={sev.klass}>{sev.label}</span>
                    </td>
                    <td className="t-xs">{assessment}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Sprint 5 client feedback 2026-07-01 — the old "Raw Section
          Detection (engine-level)" duplicate block was removed. The
          three sub-tables above now derive from the deterministic
          legal_assessment_summary builder which iterates the same
          section_/dyn_clause_/clause_ attribute keys the raw detection
          panel used to walk directly. Rendering both was redundant
          and confused analysts who saw the same section listed twice.
          If a legacy debug view is needed later, SpaClauseTable is
          still exported and can be dropped in behind a flag. */}
    </div>
  );
}

/* SpaFlagsTable replaced by Section9CriticalLegalFindings (commit
   adds the Action Required column); kept in module to preserve the
   reference. */
void SpaFlagsTable;

/* Sprint 5 client feedback 2026-06-20: Section 16 — Evidence &
   Traceability cards matching `New UI Changes/risk-report.html` §16.
   For each consolidated finding (or raw flag as fallback), render an
   XAI panel with Source Doc / Page / Extracted quote / Benchmark /
   Rule Triggered / Confidence — pulled from attribute_results lookup
   when available. */
function _findingToAttrKey(findingId: string, description: string): string | null {
  const id = findingId.toUpperCase();
  if (id.includes('-NOT-FOUND')) {
    return id.replace(/^DD-/, '').replace('-NOT-FOUND', '').toLowerCase();
  }
  if (id.startsWith('SPA-SEC-') && id.endsWith('-MISSING')) {
    return id.replace('SPA-SEC-', '').replace('-MISSING', '').toLowerCase();
  }
  const d = description.toLowerCase();
  // Sprint 5 client feedback 2026-06-25 (DD.docx §16 issue) — broaden
  // the keyword list so consolidated-finding titles for SPA clause
  // families (jurisdiction, closing conditions, dispute resolution,
  // representations, etc.) actually resolve to an attribute key. The
  // prior set only covered DD financial attributes and a handful of
  // clause names; jurisdiction-clause findings ("4/8 Jurisdiction-
  // Specific Clauses Absent: Confidentiality, Indemnification…")
  // fell through to null and §16 cards rendered every field empty.
  const keywords = [
    // DD financial
    'ebitda', 'revenue', 'irr', 'xirr', 'mom', 'entry_ev',
    'deal_size', 'valuation', 'multiple_of_money',
    // DD descriptive
    'geography', 'asset_class', 'sub_sector', 'risk_profile',
    'assumptions', 'investment_type', 'sector',
    // SPA clause families
    'indemnification', 'anti_dilution', 'escrow', 'governing_law',
    'dispute_resolution', 'closing_conditions', 'closing_condition',
    'representations_and_warranties', 'representations', 'warranties',
    'confidentiality', 'entire_agreement', 'exit_rights', 'notices',
    'definitions', 'regulatory_approvals', 'parties', 'recitals',
    'execution_and_signatures', 'execution', 'signatures',
    'purchase_price_and_consideration', 'purchase_price',
    'governing_law_and_jurisdiction', 'jurisdiction',
    'information_rights', 'investor_protection',
  ];
  for (const k of keywords) {
    if (d.includes(k.replace(/_/g, ' '))) return k;
  }
  return null;
}

function _confidenceBar(pct: number | null | undefined): JSX.Element {
  if (pct == null) return <span className="t-muted">—</span>;
  const raw = pct <= 1 ? pct * 100 : pct;
  const p = Math.max(0, Math.min(100, Math.round(raw)));
  const colour = p >= 80 ? 'var(--pass)' : p >= 50 ? 'var(--watch)' : 'var(--fail)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-block',
          width: 60,
          height: 6,
          background: 'var(--lines-soft, var(--lines))',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${p}%`,
            background: colour,
          }}
        />
      </span>
      <span className="t-mono t-xs">{p}%</span>
    </span>
  );
}

function Section16EvidenceTraceability({
  risk,
  ddReport,
  spaReport,
}: {
  risk: Record<string, unknown>;
  ddReport: Record<string, unknown>;
  spaReport: Record<string, unknown>;
}): JSX.Element {
  /* Sprint 5 client feedback 2026-07-09 follow-up 8 — §16 consumes
     the canonical F-XX findings. One card per canonical entry,
     using display_id (F-01, F-02, ...) as the card header. Dedupe
     + Overall Risk Rating inclusion + severity normalisation all
     handled upstream in buildCanonicalFindings. */
  const _canonS16 = buildCanonicalFindings(risk, spaReport, ddReport);
  /* Sprint 5 client feedback 2026-07-11 (§16 audit trail empty) — the
     mapper was dropping quoted_text / clause_number / benchmark /
     confidence / criteria which the CanonicalFinding now carries.
     Thread every provenance field so §16 doesn't fall back to the
     attr-index heuristic (which returns null for most findings). */
  const findings: Array<Record<string, unknown>> = _canonS16.map((f) => ({
    id: f.display_id,
    original_id: f.original_id,
    area: f.area,
    severity: f.severity,
    title: f.title,
    description: f.description,
    source_page: f.source_page,
    action_required: f.action_required,
    responsible: f.responsible,
    attribute_key: f.attribute_key,
    source_document: f.source_document,
    quoted_text: f.quoted_text ?? null,
    clause_number: f.clause_number ?? null,
    benchmark: f.benchmark ?? null,
    confidence: f.confidence ?? null,
    criteria: f.criteria ?? null,
  }));

  const sourceItems: Array<Record<string, unknown>> = findings;

  if (sourceItems.length === 0) {
    return <Pending note="No flags to surface evidence for. Re-run DD/SPA/Risk to populate." />;
  }

  /* attribute_results is a dict ({key: obj}), not an array — spreading
     it with [...attrs] throws "attrs is not iterable" and blanks the
     whole page. Normalize defensively to an array of entries that carry
     the attribute_key. */
  function _attrsToArray(src: unknown): Array<Record<string, unknown>> {
    if (Array.isArray(src)) return src as Array<Record<string, unknown>>;
    if (src && typeof src === 'object') {
      return Object.entries(src as Record<string, unknown>)
        .filter(([k]) => !k.startsWith('_'))
        .map(([k, v]) =>
          v && typeof v === 'object'
            ? { attribute_key: k, ...(v as Record<string, unknown>) }
            : { attribute_key: k, value: v },
        );
    }
    return [];
  }
  const ddAttrs = _attrsToArray(ddReport?.attribute_results);
  const spaAttrs = _attrsToArray(spaReport?.attribute_results);
  const attrIndex: Record<string, Record<string, unknown>> = {};
  for (const a of [...ddAttrs, ...spaAttrs]) {
    const key = String(a.attribute_key || '');
    if (key) attrIndex[key] = a;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 'var(--s3)',
      }}
    >
      {/* Sprint 5 client feedback 2026-07-09 follow-up 9 — cap
          removed. Prior cap of 12 hid canonical findings beyond
          F-12 from §16 even though §11 rendered all of them,
          breaking the "every §11 finding has a matching §16
          entry" invariant. All canonical findings render now;
          the layout already grid-wraps and scrolls, so long
          lists remain usable. */}
      {sourceItems.map((item, idx) => {
        const id = String(item.id ?? item.flag_id ?? `F-${String(idx + 1).padStart(2, '0')}`);
        const severity = String(item.severity ?? 'Medium');
        const title = String(item.title ?? item.description ?? '—');
        /* Sprint 5 client feedback 2026-07-12 (Iter9 #5) — the canonical
         * finding now carries its own attribute_key thread-through from
         * BE (buildCanonicalFindings surfaces it at line 7706). Prefer
         * that direct key over the description-keyword heuristic so
         * findings with non-obvious titles ("Deficient Documentation
         * Traceability") also resolve to their backing attribute row
         * instead of falling through to null and rendering blanks. */
        const directAttrKey = (item as { attribute_key?: unknown }).attribute_key;
        const attrKey =
          (typeof directAttrKey === 'string' && directAttrKey.trim())
            ? directAttrKey.trim()
            : _findingToAttrKey(id, title);
        const attr = attrKey ? attrIndex[attrKey] : null;

        const sourceDoc = String(
          attr?.source_document ?? (item as { source_document?: unknown }).source_document ?? '—',
        );
        const sourcePage = (attr?.source_page ?? (item as { source_page?: unknown }).source_page) as
          number | string | null | undefined;
        const extractedRaw = String(
          attr?.quoted_text ?? (item as { quoted_text?: unknown }).quoted_text ?? '',
        );
        // Sprint 5 client feedback 2026-06-25 (DD.docx §16 issue) —
        // when no real extraction is available, do NOT echo the
        // finding title back as Extracted (the user reported this
        // as "tautological"). Show an em-dash instead so the row
        // honestly signals "no quoted_text captured for this
        // finding" rather than presenting a synthetic quote.
        const extracted = extractedRaw || '';
        // Sprint 5 client feedback 2026-06-30 (New Issues R2) — when
        // _findingToAttrKey returns null (the heuristic doesn't cover
        // every finding shape) the attr-side lookups all returned "—".
        // The synthesizer now back-fills source_doc / source_page /
        // quoted_text / confidence / clause_number / benchmark
        // directly onto the consolidated_findings entries
        // (_backfill_finding_provenance), so prefer the finding's own
        // fields when attr is missing.
        const bench = attr?.benchmark_result as Record<string, unknown> | null | undefined;
        const benchValue =
          bench?.row_key ?? bench?.matched_column ?? bench?.extracted_value
          ?? (item as { benchmark?: unknown }).benchmark
          ?? null;
        const confidence =
          (attr?.confidence as number | null | undefined)
          ?? ((item as { confidence?: number | string | null }).confidence as number | null | undefined)
          ?? null;
        /* Sprint 5 client feedback 2026-06-23 (Third feedback round, Issue 7)
           — Rule Triggered was sliced(0, 120) which chopped mid-word
           leaving "in [target" and "mi". Removed the slice; full rule
           text is rendered now. If a future LLM produces a very long
           rule the cell can wrap freely — the surrounding card layout
           already handles multi-line content. */
        const rule = String(item.criteria ?? item.rule ?? '—');

        const sevBadge = _severityBadgeClass(severity);

        /* Iter9 #5 — the earlier local `_wordTrim` helper is retired;
         * extracted evidence now uses TruncatedCell (line-clamp +
         * View popup) so no in-place slicing is needed. */

        // Sprint 5 client feedback 2026-06-25 (Issues/§16 Evidence) —
        // Clause Number row added between Page and Extracted. Pulled
        // from attr.clause_number / attr.clause_id when the finding
        // ties to a SPA clause; falls back to attr.attribute_key
        // (e.g. "clause_indemnification_cap_pct") with the leading
        // "clause_" prefix stripped + title-cased for readability.
        const clauseNumber = (() => {
          // Sprint 5 client feedback 2026-06-30 (New Issues R2) — also
          // honour clause_number back-filled onto the finding itself by
          // the synth pass (_backfill_finding_provenance). Previous
          // version short-circuited to "—" when attr was null even
          // though the finding now carries the field.
          const itemClause = (item as { clause_number?: unknown }).clause_number;
          if (itemClause && String(itemClause).trim()) return String(itemClause);
          if (!attr) {
            if (attrKey && attrKey.startsWith('clause_')) {
              return attrKey.replace(/^clause_/, '').replace(/_/g, ' ');
            }
            if (attrKey && attrKey.startsWith('section_')) {
              return attrKey.replace(/^section_/, '').replace(/_/g, ' ');
            }
            return '—';
          }
          const raw = (attr as Record<string, unknown>).clause_number
            ?? (attr as Record<string, unknown>).clause_id
            ?? (attr as Record<string, unknown>).section_number
            ?? '';
          if (raw && String(raw).trim()) return String(raw);
          if (attrKey && attrKey.startsWith('clause_')) {
            return attrKey.replace(/^clause_/, '').replace(/_/g, ' ');
          }
          if (attrKey && attrKey.startsWith('section_')) {
            return attrKey.replace(/^section_/, '').replace(/_/g, ' ');
          }
          return '—';
        })();

        /* Sprint 5 client feedback 2026-07-12 (Iter9 #5) —
         *   1. Source Doc + Page unified into a single SourceChip so
         *      §16 matches the "standard Source chip format used
         *      across the DD report" (Iter6 #1 lineage).
         *   2. Extracted quote rendered via TruncatedCell so long
         *      evidence follows the standard View-popup pattern.
         *   3. Genuinely-empty fields render "Not Available" instead
         *      of "—" per the client spec ("Only display Not
         *      Available when the information genuinely does not
         *      exist in the source documents"). */
        const _NA = <span className="t-muted">Not Available</span>;
        const _hasSource = (sourceDoc && sourceDoc !== '—') || sourcePage != null;
        const sourceCell = _hasSource
          ? <SourceChip doc={sourceDoc === '—' ? null : sourceDoc} page={sourcePage ?? null} />
          : _NA;
        const clauseCell = clauseNumber && clauseNumber !== '—' ? clauseNumber : _NA;
        const extractedCell = extracted
          ? <TruncatedCell title={`${id} — Extracted Evidence`} text={stripMarkdown(extracted)} lines={2} />
          : _NA;
        const benchCell = benchValue ? String(benchValue) : _NA;
        const ruleCell = rule && rule !== '—' ? rule : _NA;

        const rows: Array<[string, React.ReactNode]> = [
          ['Source', sourceCell],
          ['Clause Number', clauseCell],
          ['Extracted', extractedCell],
          ['Benchmark', benchCell],
          ['Rule Triggered', ruleCell],
        ];

        return (
          <article
            key={id}
            style={{
              padding: 'var(--s4)',
              background: 'var(--surface)',
              border: '1px solid var(--lines)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 'var(--s2)',
                marginBottom: 'var(--s2)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-mono t-xs" style={{ color: 'var(--muted)' }}>{id}</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, marginTop: 2, lineHeight: 1.4 }}>
                  {/* Format Phase 6 — stripMarkdown before slicing so an
                      **bold** pair can never end up with a stray ** on the
                      truncated tail. */}
                  {(() => {
                    const clean = stripMarkdown(title);
                    return clean.length > 90 ? clean.slice(0, 87) + '…' : clean;
                  })()}
                </div>
              </div>
              <span className={sevBadge}>{severity}</span>
            </div>

            <div style={{ display: 'grid', gap: 4 }}>
              {rows.map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr',
                    gap: 'var(--s2)',
                    alignItems: 'baseline',
                    fontSize: '0.78rem',
                  }}
                >
                  <span style={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem', fontWeight: 600 }}>
                    {k}
                  </span>
                  {/* Iter9 #5 — Source cell contains a SourceChip
                      component; Extracted cell contains a TruncatedCell;
                      neither should take the mono styling. Rule
                      Triggered is prose so also skip mono. */}
                  <span
                    className={(k === 'Extracted' || k === 'Source' || k === 'Rule Triggered') ? '' : 't-mono t-xs'}
                    style={{ wordBreak: 'break-word', color: 'var(--text)' }}
                  >
                    {v}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: 'var(--s2)',
                  alignItems: 'center',
                  fontSize: '0.78rem',
                  marginTop: 2,
                }}
              >
                <span style={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem', fontWeight: 600 }}>
                  Confidence
                </span>
                {_confidenceBar(confidence)}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
